import { portalNL } from '../portal/nl';
import { privacyPolicyBodyNl } from '@/shared/i18n/locales/privacy-policy-bodies/nl';
import { restaurantOrdersNl } from '@/shared/i18n/locales/restaurant-orders/nl';
import type { TranslationSchema } from '@/shared/i18n/types';

const paymentPolicyTemplate = `BETAAL-, KAART- EN ANNULERINGSBELEID

1. Identiteit van de zaak
De reservering en de betaling ervan worden beheerd door [BEDRIJFSNAAM], gevestigd op [ADRES], bereikbaar via [E-MAIL/TELEFOON]. Stripe treedt uitsluitend op als betaaldienstverlener.

2. Kaartverificatie
Wanneer alleen verificatie vereist is, wordt er geen bedrag afgeschreven of geblokkeerd. De kaart wordt via Stripe geverifieerd om de geldigheid te bevestigen.

3. Vooruitbetaling
Wanneer directe betaling vereist is, wordt het totaal dat vóór de bevestiging wordt getoond bij het reserveren afgeschreven en geboekt als [AANBETALING/VOLLEDIGE BETALING].

4. Pre-autorisatie
Wanneer een pre-autorisatie is voorzien, wordt het getoonde bedrag tijdelijk geblokkeerd. De zaak mag dit innen in de gevallen die in dit beleid worden beschreven. Als er niet wordt geïnd, hangt de vrijgavetermijn af van de bank van de klant.

5. Annulering en wijziging
Gratis annuleren tot [TERMIJN]. Daarna mag de zaak [BEDRAG/PERCENTAGE] inhouden of in rekening brengen. Wijzigingen zijn afhankelijk van beschikbaarheid.

6. No-show en te laat komen
Bij het niet verschijnen of een vertraging van meer dan [MINUTEN] mag de zaak [BEDRAG/PERCENTAGE] inhouden of in rekening brengen.

7. Terugbetalingen
Terugbetalingen worden binnen [DAGEN] dagen op dezelfde betaalmethode gedaan; de uiteindelijke bijschrijftermijn hangt af van het kaartnetwerk en de bank.

8. Bewaren en toekomstig gebruik van de kaart
De kaart kan door Stripe in getokeniseerde vorm worden bewaard en alleen opnieuw worden gebruikt als de klant specifieke toestemming geeft voor de beschreven afschrijvingen. PRO20 en de zaak bewaren het volledige kaartnummer niet.

9. Geschillen en ondersteuning
Neem voor verzoeken, annuleringen of geschillen contact op via [E-MAIL/TELEFOON] onder vermelding van de reserveringscode.

Versie en geldigheid
De geaccepteerde versie, het bedrag, de betaalwijze en de acceptatiedatum worden samen met de reservering vastgelegd.`;

export const nl: TranslationSchema = {
  collaboratorRoles: {
    inherited:
      'This role is not configured yet and retains its current access. Save to define explicit permissions.',
    title: 'App permissions',
    description:
      'Choose which app operations each collaborator role can perform. Portal access is unchanged.',
    role: 'Collaborator role',
    enabled: 'enabled',
    wholeCategory: 'Entire category',
    search: 'Search operations',
    saved: 'Permissions saved',
    saveError:
      'Could not save. Reload the role to check for changes by another manager.',
    versionError: 'The permission catalog has changed. Reload before saving.',
    unsaved: 'Unsaved changes',
    appOnly: 'These permissions apply to the app only.',
    reset: 'Discard changes',
    save: 'Save permissions',
    discard: 'Discard unsaved changes and load the selected role?',
    selectBranch: 'Select a branch to configure permissions.',
    loading: 'Loading permissions…',
    unavailable:
      'Role permissions could not be loaded. Check your access and that the service supports this configuration. No permission changes have been applied.',
    retry: 'Retry',
  },

  portal: portalNL,
  restaurantOrders: restaurantOrdersNl,
  giftCards: {
    openRecap: 'Overzicht openen',
    recapError:
      'Kan de persoonlijke kaartlink niet ophalen. De Wallet-service is mogelijk nog niet beschikbaar.',

    select: 'Selecteren',
    card: 'Kaart',
    invalidDates: 'Selecteer een geldige periode.',

    title: 'Cadeaukaarten',
    description: 'Uitgegeven kaarten, QR-codes en saldo’s',
    create: 'Cadeaukaart maken',
    search: 'Zoek klant of kaartcode…',
    empty: 'Geen cadeaukaarten gevonden',
    loadError: 'Kan cadeaukaarten niet laden.',
    noBranch: 'Selecteer een vestiging.',
    customer: 'Klant',
    amount: 'Bedrag (€)',
    chooseCustomer: 'Kies een klant uit de lijst',
    selected: 'Geselecteerde klant',
    createSuccess: 'Cadeaukaart aangemaakt.',
    createError: 'Kan cadeaukaart niet maken.',
    amountError: 'Voer een bedrag boven nul in met maximaal twee decimalen.',
    details: 'Kaartgegevens',
    initial: 'Beginbedrag',
    balance: 'Saldo',
    status: 'Status',
    created: 'Aangemaakt',
    expires: 'Vervalt',
    operations: 'Transacties',
    operation: 'Transactie',
    note: 'Notitie',
    noOperations: 'Geen transacties beschikbaar.',
    email: 'E-mail versturen',
    emailSuccess: 'E-mailverzending aangevraagd.',
    emailError: 'Kan e-mailverzending niet aanvragen.',
    active: 'Actief',
    used: 'Gebruikt',
    expired: 'Verlopen',
    cancelled: 'Geannuleerd',
    allStates: 'Alle statussen',
    from: 'Van',
    to: 'Tot',
    dateRange: 'Datumbereik',
    clearDateRange: 'Datumbereik wissen',
    requestPending:
      'Behoud klant en bedrag om opnieuw te proberen zonder een tweede kaart uit te geven.',
  },
  common: {
    retry: 'Opnieuw proberen',
    loading: 'Laden',
    save: 'Opslaan',
    delete: 'Ja, verwijderen',
    deleting: 'Verwijderen...',
    createLoading: 'Aanmaken...',
    cancel: 'Annuleren',
    yes: 'Ja',
    no: 'Nee',
    back: 'Terug',
    search: 'Zoeken',
    noData: 'Geen gegevens beschikbaar',
    noDataDesc:
      'Er zijn geen gegevens die voldoen aan de geselecteerde filters.',
    previous: 'Vorige pagina',
    next: 'Volgende pagina',
    actions: 'Acties',
    serialNumber: 'Sr No.',
    itemsPerPage: 'Items per pagina',
    range: '{{start}} - {{end}} van {{total}}',
    footer: 'Pro20 © {{year}}',
    toast: {
      copyUrlSuccess: 'URL gekopieerd naar klembord.',
    },
  },
  nav: {
    collaboratorRoles: 'Collaborator roles',
    deliverySection: 'Delivery',
    drivers: 'Bezorgers',
    orders: 'Bestellingen',
    delivery: 'Delivery',
    deliveryTakeaway: 'Bezorgen / Afhalen',
    orderingConfiguration: 'Configuratie',
    takeaway: 'Afhalen',
    operations: 'Bestellingen',
    suppliers: 'Suppliers and catalogs',
    supplierOrderHistory: 'Order history',
    supplierOrders: 'Supplier orders',
    ingredients: 'Ingrediënten',
    menuList: 'Je menu’s',
    home: 'HOME',
    menu: 'Menu',
    settingsGroup: 'INSTELLINGEN',
    dashboard: 'Dashboard',
    calendar: 'Kalender',
    calendarDesc: 'Boekingen en formulieren',
    marketing: 'Marketing',
    marketingDesc: 'Berichten en promoties',
    menuItem: 'Menu',
    staff: 'Personeel',
    settings: 'Configuraties',
    payments: 'Betalingen',
    settingsDesc: 'Algemene instellingen',
    users: 'Gebruikers',
    reports: 'Rapporten',
    notifications: 'Meldingen',
    bookingHistory: 'Reserveringen',
    forms: 'Boekingsformulieren',
    formHistory: 'Booking',
    formMenu: 'Menu',
    others: 'Overige',
    extraServiceAndProduct: 'Extra diensten',
    events: 'Event',
    bulkMessage: 'Bulkbericht',
    surveys: 'Questionnaires',
    automationConfiguration: 'Configuratie',
    automationDashboard: 'Prestatiedashboard',
    automation: 'Automation',
    dataCollectionForm: 'Gegevensverzameling',
    promotion: 'Promoties',
    fidelity: 'Loyaliteitsprogramma',
    discountCards: 'Discount cards',
    cards: 'Cards',
    experienceCards: 'Experience',
    prizes: 'Prijs',
    loyalty: 'Loyaliteit',
    landingPage: 'Landingspagina’s',
    tag: 'Tag',
    publicRelation: 'Public relations',
    collaborator: 'Medewerkers',
    collaboratorReport: 'Rapport medewerkers',
    myCustomers: 'Mijn klanten',
    localConfiguration: 'Lokaal',
    stripeConfiguration: 'Stripe-configuratie',
    stripeTransactions: 'Stripe-transacties',
    configureWhatsApp: 'WhatsApp onofficieel',
    messages: 'Berichten',
    officialWhatsAppChannel: 'Officieel WhatsApp-kanaal',
    templates: 'Sjablonen',
    whatsAppOfficial: 'WhatsApp Official',
    whatsAppOfficialConfigure: 'Configureren',
    whatsAppOfficialTemplates: 'Sjablonen',
    whatsAppOfficialInbox: 'Postvak IN',
  },
  header: {
    fullscreen: 'Volledig scherm',
    language: 'Taal',
    theme: 'Thema',
    lightMode: 'Licht',
    darkMode: 'Donker',
    profile: 'Profiel',
    signOut: 'Uitloggen',
    userCode: 'Gebruikerscode',
    branchList: 'Vestigingen',
    searchBranches: 'Zoek vestigingen...',
    noBranchesFound: 'Geen vestigingen gevonden',
    switchingBranch: 'Vestiging wisselen…',
  },
  dashboard: {
    aiCredits: 'AI-credits',
    smsAvailable: 'SMS beschikbaar',
    smsLoading: 'SMS-saldo laden…',
    smsError: 'Fout bij SMS-saldo',
    smsErrorHint: 'SMS-saldo niet beschikbaar. Probeer opnieuw.',
    smsRefresh: 'SMS-saldo vernieuwen',
    smsEmpty: 'Geen SMS beschikbaar',
    title: 'Dashboard',
    subtitle: 'Dit speelt zich af in jouw bedrijf.',
    headerMeta: '{{dateRange}} · prestatievenster van {{days}} dagen',
    customRange: 'Aangepast',
    customRangePlaceholder: 'Selecteer datumbereik',
    refresh: 'Vernieuwen',
    bookingStatistics: 'Booking statistics',
    lastWeek: 'Last week',
    thisWeek: 'This week',
    processedBookings: 'Processed bookings vs. Arrived customers',
    overview: 'Overview',
    bookings: 'Bookings',
    customersManaged: 'Customers managed',
    arrived: 'Aangekomen',
    refused: 'REFUSED',
    noShow: 'NO SHOW',
    cancelled: 'CANCELLED',
    summary: 'Summary of workspace',
    customerContacts: 'Customer Contacts',
    acceptedBookings: 'Accepted Bookings',
    managedGuests: 'Managed Guests',
    callCenterInteractions: 'Call Center Interactions',
    remainingSms: 'Remaining SMS',
    remainingEmails: 'Remaining Emails',
    totalUsers: 'Total Users',
    activeUsers: 'Active Users',
    revenue: 'Revenue',
    pendingTasks: 'Pending Tasks',
    recentActivity: 'Recent Activity',
    quickActions: 'Quick Actions',
    fromLastMonth: 'from last month',
    noData: '—',
    noBranchTitle: 'No branch selected',
    noBranchMessage: 'Select a branch to view dashboard analytics.',
    loadError: 'Could not load dashboard data. Please try again.',
    sectionLoadError: 'Kan dit onderdeel niet laden.',
    bookingsFlow: 'Bookings flow',
    forms: 'Formulieren',
    statusDistribution: 'Status distribution',
    totalBookingsShort: 'Bookings',
    waitingAction: 'Wacht op actie',
    waitingActionSubtitle: 'Reserveringen in IN_ATTESA klaar om te verwerken.',
    waitingBadge: 'Wachtend',
    people: 'personen',
    openBooking: 'Boeking openen',
    copyContact: 'Contact kopiëren',
    copySuccess: 'Gekopieerd naar klembord.',
    emptyWaiting: 'Geen boekingen die op actie wachten.',
    emptyChart: 'Geen gegevens voor deze periode.',
    emptyCustomers: 'Geen klanten om te tonen.',
    emptyForms: 'Geen formulieren om te tonen.',
    guests: 'gasten',
    children: 'kinderen',
    accept: 'Accepteren',
    reject: 'Weigeren',
    acceptSuccess: 'Boeking geaccepteerd.',
    rejectSuccess: 'Boeking geweigerd.',
    actionError: 'Boekingsstatus kon niet worden bijgewerkt.',
    busiestSlots: 'Drukste tijdvakken',
    busiestSlotsSubtitle: 'Waar de service het drukst is.',
    customerMix: 'Klantenbestand',
    customerRisk: 'Klantenrisico',
    customerRiskSubtitle: 'Concentratie van no-shows en weigeringen.',
    bestCustomers: 'Beste klanten',
    bestCustomersSubtitle: 'Terugkerende aanwezigheid van hoge waarde.',
    lastBooking: 'Laatste reservering',
    messageDelivery: 'Berichtbezorging',
    messageDeliverySubtitle: 'Gezondheid van geautomatiseerde communicatie.',
    dailyOperations: 'Dagelijkse operaties',
    dailyOperationsSubtitle: 'Boekingen en gastbelasting per dag.',
    bookingsFlowSubtitle:
      'Dagelijkse reserveringen in de geselecteerde periode.',
    formsSubtitle: 'Waar reserveringen vandaan komen.',
    statusDistributionSubtitle: 'Hoe reserveringen zijn afgelopen.',
    customerMixSubtitle: 'Je klantenbestand in deze periode.',
    pulse: {
      today: 'Vandaag',
      upcoming: 'Aankomend',
      pending: 'In afwachting',
      waitList: 'Wachtlijst',
      refusalRate: 'Weigeringspercentage',
      google: 'Google Reservations',
      googleOn: 'Actief',
      googleOff: 'Niet actief',
      googleMissing: '{{count}} ontbrekende vereisten',
    },
    waitingTabs: {
      all: 'Alles',
      today: 'Vandaag',
      future: 'Toekomst',
      large: 'Groot',
    },
    flow: {
      volume: 'Volume',
      people: 'Personen',
      outcome: 'Resultaat',
    },
    kpis: {
      reservations: 'Reserveringen',
      reservationsHint: '{{count}} personen',
      arrivalRate: 'Aankomstpercentage',
      customers: 'Klanten',
      arrivedOfTotal: '{{arrived}} van {{total}} aangekomen',
      customersHint: '{{active}} actief · {{new}} nieuw',
      messageErrors: '{{count}} verzendfouten',
      noShowRate: 'No-show percentage',
      arrivedVsNoShow: '{{arrived}} aangekomen · {{noShow}} no-show',
      peakDay: 'Piekdag',
      peakSlot: 'Piekvak',
      dailyAverage: 'Dagelijks gemiddelde',
      dailyAverageHint: 'Reserveringen per dag',
      messagesWaiting: 'Berichten in wachtrij',
      scheduledTotal: '{{count}} gepland totaal',
      bookingsGuests: '{{bookings}} boekingen, {{guests}} gasten',
      slotStats: '{{bookings}} boekingen · {{arrived}} aangekomen ({{rate}}%)',
      totalBookings: 'Totaal boekingen',
      activeUsers: 'Actieve gebruikers',
      newUsers: 'Nieuwe gebruikers',
      peakDate: 'Piekdatum',
      managedBookings: 'Beheerde boekingen',
    },
    series: {
      bookings: 'Boekingen',
      confirmed: 'Bevestigd',
      pending: 'In afwachting',
      refused: 'Geweigerd',
      noShow: 'No show',
      risk: 'Risico',
    },
    mix: {
      active: 'Actief',
      new: 'Nieuw',
      marketingOptIn: 'Marketing opt-in',
      blacklisted: 'Op zwarte lijst',
      ofBase: '{{percent}}% van bestand',
      total: 'totaal',
    },
    comm: {
      bookingNotifications: 'Boekingsmeldingen',
      scheduledMessages: 'Geplande berichten',
      successRate: 'Succespercentage',
      total: 'Totaal',
      processed: 'verwerkt',
      waiting: 'wachtend',
      errors: 'fouten',
      centralino: 'Centralino-status',
      centralinoActive: 'Centralino actief',
      wao: 'WAO',
      whatsapp: 'WhatsApp',
      sms: 'SMS',
      email: 'E-mail',
      enabled: 'Aan',
      disabled: 'Uit',
    },
    ops: {
      byDay: 'Per dag',
      byWeekday: 'Per weekdag',
      avgPeople: 'Gem. personen',
      avgBookings: 'Gem. reserveringen',
      avgPartySize: 'Gem. groepsgrootte',
      busiestWeekday: 'Drukste weekdag',
      totalPeople: 'Totaal personen',
      daysCount: '{{count}} dagen',
    },
    days: {
      mon: 'Ma',
      tue: 'Di',
      wed: 'Wo',
      thu: 'Do',
      fri: 'Vr',
      sat: 'Za',
      sun: 'Zo',
    },
  },
  auth: {
    welcomeBack: 'Welkom terug!',
    signIn: 'Inloggen',
    email: 'E-mail',
    password: 'Wachtwoord',
    enterPassword: 'Voer je wachtwoord in',
    userCode: 'Gebruikerscode',
    enterUserCode: 'Voer je gebruikerscode in',
    branchCode: 'Vestigingscode',
    enterBranchCode: 'Voer vestigingscode in',
    employeeId: 'Medewerker-ID',
    enterEmployeeId: 'Voer medewerker-ID in',
    rememberMe: 'Onthoud mij',
    rememberCredentials: 'Inloggegevens onthouden',
    forgotPassword: 'Wachtwoord vergeten?',
    signInToContinue: 'Log in om je restaurant verder te beheren met PRO20.',
    signInToAccount: 'Log in op je account',
    chooseSignInMethod: 'Kies je voorkeursmethode om in te loggen',
    or: 'of',
    loginWithEmployeeId: 'Inloggen met medewerker-ID',
    loginWithUserCode: 'Inloggen met gebruikerscode',
    loginWithAdminCode: 'Inloggen met beheerderscode',
    invalidCredentials: 'Ongeldige inloggegevens. Probeer opnieuw.',
    enterpriseTagline: 'Enterprise admin, vereenvoudigd.',
    enterpriseDescription:
      'Beheer gebruikers, monitor analyses en configureer je workspace vanuit één krachtig dashboard.',
    trustedBy: 'Vertrouwd door restaurants wereldwijd',
    trustSecurityTitle: 'Enterprise-beveiliging',
    trustSecurityDesc: 'Gegevensbescherming en compliance op bankniveau.',
    trustUptimeTitle: '99,9% uptime',
    trustUptimeDesc: 'Betrouwbare prestaties wanneer je ze nodig hebt.',
    trustSupportTitle: '24/7 support',
    trustSupportDesc: 'We staan altijd voor je klaar.',
    secureSignIn: 'Veilig inloggen | Je verbinding is versleuteld.',
    copyright: '© {{year}} PRO20. Alle rechten voorbehouden.',
    dashboardPreviewAlt: 'PRO20-dashboardvoorbeeld',
    demoCredentials: 'Demo-inloggegevens: admin@proventi.com / password123',
    version: 'v. {{version}}(#1)',
  },
  subscription: {
    expired: {
      badge: 'Abonnement verlopen',
      title: 'Je abonnement is verlopen',
      description: 'Als je al hebt verlengd, neem dan contact op met support.',
      infoTitle: 'Wil je verlengen of toegang herstellen?',
      infoDescription:
        'Neem contact op met ons supportteam en wij helpen je weer online.',
      footerHelp: 'Nog hulp nodig?',
      footerHelpDesc: 'Ons supportteam is 24/7 beschikbaar om je te helpen.',
    },
  },
  automation: {
    disabled: {
      badge: 'Sectie niet ingeschakeld',
      title: 'Deze sectie is niet ingeschakeld',
      description:
        'Deze functie is momenteel uitgeschakeld voor je account. Neem contact op met support voor meer informatie.',
      infoTitle: 'Toegang tot deze sectie nodig?',
      infoDescription:
        'Neem contact op met ons supportteam en we helpen je op weg.',
      footerHelp: 'Nog hulp nodig?',
      footerHelpDesc: 'Ons supportteam is 24/7 beschikbaar om je te helpen.',
    },
  },
  orders: {
    disabled: {
      badge: 'Sectie niet ingeschakeld',
      title: 'Deze sectie is niet ingeschakeld',
      description:
        'Deze functie is momenteel uitgeschakeld voor je account. Neem contact op met support voor meer informatie.',
      infoTitle: 'Toegang tot deze sectie nodig?',
      infoDescription:
        'Neem contact op met ons supportteam en we helpen je op weg.',
      footerHelp: 'Nog hulp nodig?',
      footerHelpDesc: 'Ons supportteam is 24/7 beschikbaar om je te helpen.',
    },
  },
  delivery: {
    disabled: {
      badge: 'Sectie niet ingeschakeld',
      title: 'Deze sectie is niet ingeschakeld',
      description:
        'Deze functie is momenteel uitgeschakeld voor je account. Neem contact op met support voor meer informatie.',
      infoTitle: 'Toegang tot deze sectie nodig?',
      infoDescription:
        'Neem contact op met ons supportteam en we helpen je op weg.',
      footerHelp: 'Nog hulp nodig?',
      footerHelpDesc: 'Ons supportteam is 24/7 beschikbaar om je te helpen.',
    },
  },
  users: {
    title: 'Gebruikers',
    subtitle: 'Beheer teamleden en rechten',
    searchPlaceholder: 'Zoek gebruikers...',
    role: 'Rol',
    status: 'Status',
    joined: 'Lid sinds',
    accountDetails: 'Accountgegevens',
    userId: 'Gebruikers-ID',
    created: 'Aangemaakt',
    lastUpdated: 'Laatst bijgewerkt',
    notFound: 'Gebruiker niet gevonden',
  },
  settings: {
    title: 'Instellingen',
    subtitle: 'Beheer workspace-voorkeuren',
    comingSoon: 'Deze sectie komt binnenkort beschikbaar.',
    general: 'Algemeen',
    theme: 'Thema',
    language: 'Taal',
    timezone: 'Tijdzone',
    notifications: 'Meldingen',
    emailNotifications: 'E-mailmeldingen',
    pushNotifications: 'Pushmeldingen',
    enabled: 'Ingeschakeld',
    disabled: 'Uitgeschakeld',
  },
  userSettings: {
    title: 'Gebruikersinstellingen',
    subtitle: 'Beheer je accountwachtwoord en voorkeuren',
    password: {
      sectionTitle: 'Wachtwoordbeheer',
      sectionSubtitle: 'Wachtwoord wijzigen',
      passwordLabel: 'Wachtwoord',
      confirmLabel: 'Bevestig wachtwoord',
      submit: 'Wachtwoord bijwerken',
      errors: {
        required: 'Wachtwoord is verplicht',
        minLength: 'Wachtwoord moet minstens 4 tekens bevatten',
        confirmRequired: 'Bevestig je wachtwoord',
        mismatch: 'Wachtwoorden komen niet overeen',
      },
      toast: {
        success: 'Wachtwoord succesvol bijgewerkt',
        failed: 'Wachtwoord kon niet worden bijgewerkt. Probeer opnieuw.',
        missingUser: 'Je account is niet gevonden. Log opnieuw in.',
      },
    },
  },
  localConfiguration: {
    subtitle: 'Configureer het restaurantlogo, rooster, locatie en tracking.',
    saveConfiguration: 'Configuratie opslaan',
    saveTooltip: 'Restaurantconfiguratie opslaan',
    noBranch: 'Selecteer een vestiging om lokale instellingen te configureren.',
    loadError:
      'Lokale configuratie kon niet worden geladen. Probeer het opnieuw.',
    logoTitle: 'Restaurantlogo',
    logoHint: 'Upload een afbeelding',
    logoDescription:
      'Upload een PNG-logo met transparante achtergrond. Dubbelklik op de afbeelding om een voorbeeld te openen.',
    uploadImage: 'Upload een afbeelding',
    removeLogo: 'Logo verwijderen',
    logoUploading: 'Uploaden…',
    logoRemoving: 'Verwijderen…',
    timezoneTitle: 'Tijdzone van de ruimte',
    timezoneHint:
      'Bepaal de huidige dag die in het formulier wordt getoond en voorkom datumverschuivingen voor klanten in het buitenland.',
    timezoneLabel: 'Geografisch gebied',
    timezoneHelper:
      'De wisseling tussen zonnetijd en zomertijd wordt automatisch beheerd.',
    generalTitle: 'Algemene details',
    generalHint:
      'Configureer de capaciteit van de vestiging, tijdsloten en sluitingsdagen.',
    scheduleTitle: 'Openingstijden',
    scheduleHint: 'Kies wanneer gasten kunnen reserveren.',
    dayOverviewTitle: 'Wanneer gasten kunnen reserveren',
    dayOverviewHint: 'Open dit om de hele dag te zien.',
    dayOverviewShow: 'Dagoverschicht tonen',
    dayOverviewHide: 'Dagoverschicht verbergen',
    guestsCanBook: 'Gasten kunnen in deze tijd reserveren.',
    guestsCannotBook: 'Gasten kunnen in deze tijd niet reserveren.',
    emptyServiceTimes:
      'Nog geen openingstijden. Voeg ontbijt, lunch of diner toe zodat gasten weten wanneer ze kunnen reserveren.',
    timeStepHint:
      'Kies tijden in stappen van 15 minuten. Reserveringen stoppen op de eindtijd.',
    switchOn: 'Aan',
    switchOff: 'Uit',
    editAction: 'Bewerken',
    deleteAction: 'Verwijderen',
    howItWorks: 'Hoe het werkt',
    previewOnly: 'Alleen voorbeeld — bewerk blokken hieronder',
    stepBadge: 'Stap: 15 min — 04:00 / 03:59',
    localDay: 'Lokale dag: {{time}}',
    tips: {
      turnOnTitle: 'Inschakelen',
      turnOn: 'Laat alleen de blokken die je gebruikt actief.',
      moveTitle: 'Verplaatsen',
      move: 'Verplaats begin en einde met de schuifregelaars.',
      saveTitle: 'Opslaan',
      save: 'Het systeem voorkomt overlappingen.',
    },
    shiftsTitle: 'Servicetijden',
    shiftsHint:
      'Voeg de tijden toe waarop je reserveringen aanneemt, zoals lunch of diner. Je kunt er een uitzetten zonder hem te verwijderen.',
    createMealBand: 'Servicetijd toevoegen',
    createMealBandAction: 'Toevoegen',
    createMealBandHint: 'Geef een naam en kies wanneer het begint en eindigt.',
    editMealBand: 'Servicetijd bewerken',
    editMealBandNamed: '{{name}} bewerken',
    updateMealBandAction: 'Opslaan',
    mealBandName: 'Naam',
    mealBandNamePlaceholder: 'Lunch',
    selectStartTime: 'Selecteer begintijd',
    selectEndTime: 'Selecteer eindtijd',
    mealBandLimitReached: 'Je kunt maximaal 20 servicetijden toevoegen.',
    customMealBand: 'Servicetijd',
    deleteMealBand: '{{name}} verwijderen',
    deleteMealBandTitle: 'Deze servicetijd verwijderen?',
    deleteMealBandDescription:
      'Weet je zeker dat je {{name}} wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
    active: 'Actief',
    inactive: 'Inactief',
    shiftLegendOff: '{{name}}: neemt geen reserveringen aan',
    shiftLegendRange: '{{name}}: {{from}} - {{to}}',
    booking: 'Reservering',
    activeBand: 'Reserveringen accepteren',
    activeBandHint:
      'Zet dit uit als gasten in deze tijd niet kunnen reserveren.',
    deactivate: 'Uitschakelen',
    deactivated: 'Uitgeschakeld',
    activate: 'Inschakelen',
    valueSaved: 'Waarde opgeslagen',
    valueSavedHint: 'Klanten kunnen binnen dit tijdslot reserveren.',
    selectBandTitle: 'Selecteer het blok met één balk',
    selectBandHint:
      'Het geselecteerde deel houdt rekening met de andere actieve blokken.',
    fine: 'Einde',
    spaceAvailable: 'Beschikbare ruimte voor {{name}}: {{from}} - {{to}}',
    minStartValue: 'Min start',
    maxEndValue: 'Max eind',
    bandTurnedOff:
      'Dit blok is uitgeschakeld. Schakel het in met de schakelaar hierboven om de tijden te bewerken.',
    activityHours: 'Activiteitsuren',
    start: 'Begin',
    end: 'Einde',
    closuresTitle: 'Dagen dat het restaurant gesloten is',
    closuresHint: 'Gasten kunnen op deze data niet reserveren.',
    addClosure: 'Sluiting toevoegen',
    closuresModalHint:
      'Selecteer een begin- en einddatum voor de sluitingsperiode.',
    closingPeriod: 'Sluitingsperiode',
    chooseDateRange: 'Kies een datumbereik',
    fromDate: 'Van datum',
    toDate: 'Tot datum',
    actions: 'Acties',
    noClosures: 'Nog geen sluitingsdagen.',
    deleteClosure: 'Sluiting verwijderen',
    locationTitle: 'Locatie',
    trackingTitle: 'Tracking en analyse',
    trackingHint: 'Stel Google Tagbeheer, Facebook-pixel en trackingtools in',
    googleTagManager: 'Google Tagbeheer',
    facebookPixel: 'Facebook-pixel',
    tabs: {
      tabListLabel: 'Onderdelen lokale configuratie',
      general: 'Algemeen',
      bookingTimeSlots: 'Openingstijden',
      locationTracking: 'Locatie en tracking',
    },
    shifts: {
      breakfast: 'Ontbijt',
      breakfastSubtitle: 'Ontbijttijdslot',
      lunch: 'Lunch',
      lunchSubtitle: 'Lunchtijdslot',
      aperitivo: 'Happy hour',
      aperitivoSubtitle: 'Aperitieftijdslot',
      dinner: 'Diner',
      dinnerSubtitle: 'Dinertijdslot',
    },
    fields: {
      localName: 'Naam vestiging',
      localCapacity: 'Capaciteit vestiging',
      address: 'Adres',
      city: 'Plaats',
      zip: 'Postcode',
      province: 'Provincie',
      nation: 'Land',
      state: 'Staat',
      latitude: 'Breedtegraad',
      longitude: 'Lengtegraad',
      gtmId: 'Container-ID',
      pixelId: 'Pixel-ID',
    },
    validation: {
      nameRequired: 'Naam van de vestiging is verplicht',
      mealBandNameRequired: 'Vul een naam in',
      mealBandNameDuplicate: 'Er bestaat al een servicetijd met deze naam',
      openingTimeRequired: 'Begintijd is verplicht',
      closingTimeRequired: 'Eindtijd is verplicht',
    },
    toast: {
      saved: 'Configuratie opgeslagen',
      saveError: 'Configuratie kon niet worden opgeslagen',
      logoUploadSuccess: 'Logo succesvol geüpload',
      logoUploadError: 'Logo kon niet worden geüpload',
      logoRemoveSuccess: 'Logo succesvol verwijderd',
      logoRemoveError: 'Logo kon niet worden verwijderd',
      selectDateRange: 'Selecteer een begin- en einddatum',
      closureAdded: 'Sluitingsperiode toegevoegd',
      closureAddError: 'Sluitingsperiode kon niet worden toegevoegd',
      closureOverlap:
        'Deze periode overlapt met een bestaande sluitingsperiode',
      closureRemoved: 'Sluitingsperiode verwijderd',
      closureDeleteError: 'Sluitingsperiode kon niet worden verwijderd',
      mealBandCreated: 'Servicetijd toegevoegd',
      mealBandCreateError: 'Kon deze servicetijd niet toevoegen',
      mealBandUpdated: 'Servicetijd bijgewerkt',
      mealBandUpdateError: 'Kon deze servicetijd niet bijwerken',
      mealBandStatusUpdated: 'Reserveringsstatus bijgewerkt',
      mealBandStatusError: 'Kon de reserveringsstatus niet bijwerken',
      mealBandDeleted: 'Servicetijd verwijderd',
      mealBandDeleteError: 'Kon deze servicetijd niet verwijderen',
      mealBandInvalidCode:
        'Deze servicetijd kon niet worden opgeslagen. Probeer het opnieuw.',
    },
  },
  stripe: {
    common: {
      refresh: 'Vernieuwen via Stripe',
      noBranch: 'Selecteer een vestiging om verder te gaan.',
      processing: 'Bezig…',
    },
    configuration: {
      title: 'Stripe-configuratie',
      subtitle: 'Live gegevens van het gekoppelde Stripe-account.',
      subtitleWithBranch:
        'Live gegevens van het account dat aan {{branch}} is gekoppeld.',
      loadError: 'Kon de status niet rechtstreeks bij Stripe ophalen.',
      notConnectedTitle: 'Stripe-account niet geconfigureerd',
      notConnectedDescription:
        'Deze vestiging heeft nog geen gekoppeld Stripe-account. Rond de onboarding af vanuit de backoffice.',
      accountSection: 'Accountgegevens',
      accountHeading: 'Stripe-account',
      email: 'E-mail',
      country: 'Land',
      currency: 'Valuta',
      accountType: 'Accounttype',
      disabledReason: 'Reden blokkade',
      disabledReasonNone: 'Geen',
      detailsSubmitted: 'Gegevens verzonden',
      chargesEnabled: 'Betalingen',
      payoutsEnabled: 'Uitbetalingen',
      requirements: {
        title: 'Stripe-vereisten',
        currentlyDue: 'Nog aan te leveren',
        pastDue: 'Verlopen',
        pendingVerification: 'In verificatie',
        none: 'Geen',
      },
      policy: {
        title: 'Betaalbeleid',
        description:
          'Tekst die aan de klant wordt getoond wanneer de reservering kaartverificatie, een pre-autorisatie of een betaling vereist.',
        versionMeta: 'Versie {{version}} · bijgewerkt {{date}}',
        useTemplate: 'Stel een beleidsstructuur voor',
        templateWarning:
          'Het sjabloon is een startpunt: vervang alle velden tussen vierkante haken en laat de tekst controleren door een juridisch adviseur.',
        placeholder: 'Voer het betaalbeleid van de vestiging in…',
        charCount: '{{count}}/{{max}} tekens',
        save: 'Opslaan en nieuwe versie maken',
        template: paymentPolicyTemplate,
      },
      toast: {
        policySaved: 'Betaalbeleid opgeslagen.',
        policyError: 'Kon het betaalbeleid niet opslaan.',
      },
      validation: {
        policyRequired: 'Voer de tekst van het betaalbeleid in.',
        policyMax: 'Het beleid mag niet langer zijn dan 20000 tekens.',
      },
    },
    transactions: {
      title: 'Stripe-transacties',
      empty: 'Geen transacties',
      emptyDescription:
        'Er zijn nog geen betalingen of aanbetalingen geregistreerd voor deze vestiging.',
      notConnectedTitle: 'Stripe-account niet gekoppeld.',
      notConnectedDescription:
        'Alle historische transacties die voor deze vestiging zijn vastgelegd, blijven hier beschikbaar.',
      filters: {
        all: 'Alle',
        succeeded: 'Geslaagd',
        uncaptured: 'Niet geïnd',
        failed: 'Mislukt',
      },
      columns: {
        date: 'Datum',
        booking: 'Reservering',
        operation: 'Bewerking',
        requested: 'Gevraagd',
        captured: 'Geïnd',
        status: 'Status',
        details: 'Details',
      },
      mode: {
        payNow: 'Betaling',
        authorizeOnly: 'Aanbetaling / pre-autorisatie',
      },
      status: {
        linkCreated: 'Wacht op betaling',
        processing: 'Geregistreerd, wacht op bevestiging van Stripe',
        pending: 'In afwachting',
        authorized: 'Niet geïnd',
        partiallyPaid: 'Gedeeltelijk geïnd',
        paid: 'Geslaagd',
        failed: 'Mislukt',
        canceled: 'Geannuleerd',
        refundPending: 'Terugbetaling bezig',
        partiallyRefunded: 'Gedeeltelijk terugbetaald',
        refunded: 'Terugbetaald',
        refundFailed: 'Terugbetaling mislukt',
      },
      actions: {
        capture: 'Aanbetaling innen',
        release: 'Aanbetaling weigeren',
      },
      confirm: {
        captureTitle: 'Aanbetaling innen?',
        captureDescription:
          'Er wordt {{amount}} geïnd voor reservering {{booking}}.',
        releaseTitle: 'Aanbetaling weigeren?',
        releaseDescription:
          'De autorisatie van {{amount}} wordt geannuleerd en het bedrag wordt vrijgegeven.',
      },
      toast: {
        captureSuccess: 'Stripe heeft de inning bevestigd.',
        captureError:
          'Inning mislukt. Controleer de status in Stripe en probeer het opnieuw.',
        releaseSuccess: 'Stripe heeft de autorisatie geannuleerd.',
        releaseError:
          'Vrijgeven mislukt. Controleer de status in Stripe en probeer het opnieuw.',
      },
      technical: {
        title: 'Technische details',
        description:
          'Identificatiegegevens die PRO20 en Stripe voor deze bewerking gebruiken.',
        open: 'Technische details tonen',
        close: 'Sluiten',
        bookingCode: 'Reserveringscode',
        depositCode: 'PRO20-transactiecode',
        paymentIntentId: 'Stripe PaymentIntent',
        paymentLinkId: 'Stripe Payment Link',
        connectedAccountId: 'Stripe-account',
      },
    },
  },
  messages: {
    orderingEvents: {
      CREATED: 'Bestelling ontvangen',
      ACCEPTED: 'Bestelling geaccepteerd',
      PREPARING: 'Bestelling in bereiding',
      READY: 'Bestelling klaar',
      DELIVERED: 'Bestelling bezorgd',
      COLLECTED: 'Bestelling afgehaald',
      CANCELLED: 'Bestelling geannuleerd',
      FAILED: 'Bezorging mislukt',
      PAYMENT_REQUIRED: 'Betaallink',
      PAYMENT_CONFIRMED: 'Betaling ontvangen',
      PAYMENT_FAILED: 'Betaling mislukt',
      REFUNDED: 'Terugbetaling bevestigd',
      SCHEDULE_REMINDER: 'Herinnering bestelling',
    },
    orderingFlows: {
      CREATED:
        'Na het aanmaken van de bestelling. De klant weet dat het restaurant deze nog moet accepteren.',
      ACCEPTED:
        'Wanneer je de bestelling accepteert en ervoor kiest de klant te informeren.',
      PREPARING:
        'Wanneer de bereiding start en je ervoor kiest de klant te informeren.',
      READY:
        'Wanneer de bestelling klaar is en je ervoor kiest de klant te informeren. Bij bezorging betekent dit niet dat de koerier is vertrokken.',
      DELIVERED:
        'Wanneer je de bestelling individueel als bezorgd verwerkt en ervoor kiest de klant te informeren. Bulkacties en koerier-afronding blijven stil.',
      COLLECTED:
        'Wanneer je de bestelling individueel als afgehaald verwerkt en ervoor kiest de klant te informeren. Bulkacties blijven stil.',
      CANCELLED:
        'Wanneer je de bestelling annuleert en ervoor kiest de klant te informeren. Dit bevestigt geen terugbetaling.',
      FAILED:
        'Wanneer je de bezorging als mislukt markeert en ervoor kiest de klant te informeren.',
      PAYMENT_REQUIRED:
        'Alleen nadat er daadwerkelijk een geldige betaallink is aangemaakt.',
      PAYMENT_CONFIRMED:
        'Na de daadwerkelijke bevestiging van de betaling, automatisch of handmatig. Bulkacties blijven stil.',
      PAYMENT_FAILED:
        'Als een betalingspoging mislukt en de bestelling onbetaald blijft.',
      REFUNDED: 'Alleen nadat de terugbetaling daadwerkelijk is afgerond.',
      SCHEDULE_REMINDER:
        'Vóór het geplande tijdstip, alleen voor geaccepteerde of in bereiding zijnde bestellingen. Onvoltooide kaartbetalingen worden uitgesloten.',
    },
    ordering: {
      configurationHint:
        'Schakel dit bericht in voor de bestellingen van deze vestiging. Opslaan verstuurt geen bericht.',
      initialize: 'Berichten voor Delivery en afhalen aanmaken',
      initialized: 'Berichten aangemaakt. Niets verzonden.',
      loadError:
        'Berichten voor Delivery en afhalen konden niet worden geladen. Probeer het opnieuw.',
      missing:
        'Maak ontbrekende berichten aan. Bestaande aangepaste inhoud blijft behouden. Nieuwe berichten starten uitgeschakeld.',
      minutes: 'Minuten vóór het geplande besteltijdstip',
      scheduleError: 'Voer een geheel getal van 1 tot 10080 minuten in.',
      exclusive:
        'Kies onofficiële WhatsApp of een officieel sjabloon, niet beide.',
      emailPreset: 'Standaard e-mailinhoud',
      paymentHint:
        'Laat dit uitgeschakeld als je de betaallink al handmatig via WhatsApp stuurt.',
    },

    searchPlaceholder: 'Zoek op naam, code, categorie of kanaal…',
    allCategories: 'Alle categorieën',
    browseCategories: 'Berichtcategorieën',
    searchEmpty:
      'Geen resultaten. Probeer een andere zoekterm of selecteer alle categorieën.',

    giftCards: {
      scheduleTitle: 'Herinnering voor vervaldatum',
      daysBeforeExpiry: 'Dagen voor vervaldatum',
      scheduleHint:
        'Stuur de herinnering zoveel dagen voor de vervaldatum op het gekozen uur in de tijdzone van de vestiging. Sluit gebruikte, geannuleerde kaarten en kaarten zonder saldo uit.',
      scheduleError:
        'Voer een positief geheel aantal dagen en een uur tussen 00:00 en 23:00 in.',

      creation: 'Cadeaukaart aanmaken',
      expired: 'Cadeaukaart verlopen',
      walletHint:
        'De backend vult wallet_link bij verzending in. De Wallet-knop gebruikt het kaartspecifieke achtervoegsel, nooit een vaste klantlink.',

      initialize: 'Ontbrekende berichten maken',
      initializeSuccess: 'Configuraties aangemaakt.',
      initializeError: 'De backend kon de configuraties niet maken.',
      missing:
        'Deze configuraties ontbreken. Maak ze aan om officiële WhatsApp- en e-mailsjablonen te koppelen.',
      notCreated: 'Nog niet aangemaakt op de backend',
      loadError: 'Kan Marketingconfiguraties niet laden. Probeer opnieuw.',
    },
    title: 'Berichtencentrum',
    subtitle:
      'Beheer alle automatische WhatsApp-, SMS- en e-mailberichten van de vestiging.',
    configuredLabel: 'Geconfigureerde berichten',
    refresh: 'Vernieuwen',
    emptyCategory: 'Nog geen berichten in deze categorie.',
    emptyTitle: 'Geen berichten gevonden',
    emptyDescription:
      'Er zijn nog geen automatische berichten geconfigureerd voor deze vestiging.',
    noBranchTitle: 'Geen vestiging geselecteerd',
    noBranchDescription:
      'Selecteer een vestiging om automatische berichten te bekijken en te beheren.',
    loadErrorTitle: 'Berichten konden niet worden geladen',
    loadErrorDescription:
      'Het laden van de berichtenlijst is mislukt. Probeer het opnieuw.',
    categories: {
      restaurantOrdering: 'Delivery en afhalen',
      supplierOrders: 'Leveranciersbestellingen',
      giftCards: 'Cadeaukaartberichten',
      standard: 'Standaardberichten',
      cta: 'CTA',
      marketing: 'Marketingberichten',
      preBooking: 'Communicatie vóór boeking',
      postBooking: 'Communicatie na boeking',
      switchboard: 'Centrale berichten',
    },
    categoryDescriptions: {
      marketing: 'Promotieberichten, loyaliteit en automatische kaartacties.',
    },
    items: {
      ORDER_SENT: 'Bestelling verzonden',
      ORDER_UPDATED: 'Bestelling bijgewerkt',
      ORDER_DELIVERY: 'Levering van bestelling',
      ORDER_GENERIC: 'Leveranciersbestelling',

      ATT: 'Bericht in afwachting van bevestiging',
      ARR: 'Aankomstbevestiging',
      CONF: 'Boekingsbevestiging',
      REF: 'Geweigerde boeking',
      DEL: 'Verwijderde boeking',
      FQ1: 'Wachtrijbeheer 1',
      FQ2: 'Wachtrijbeheer 2',
      EDTU: 'Boekingsupdate (gebruiker)',
      EDTC: 'Boekingsupdate (klant)',
      EDTR: 'Boekingsupdate (restaurant)',
      ANS1: 'No-show-melding 1',
      ANS2: 'No-show-melding 2',
      ANS3: 'No-show-melding 3',
      RPR: 'Promoherinnering',
      DOB: 'Verjaardagspromo',
      AAD: 'Reviewverzoek',
      RRP: 'We missen je-promo',
      CBP: 'Uitnodiging om terug te komen',
      CENTRALINO1: 'Centrale functie 1',
      CENTRALINO2: 'Centrale functie 2',
      CENTRALINO3: 'Centrale functie 3',
    },
    variables: {
      nomelocale: '@nomelocale@',
      ospiti: '@ospiti@',
      bambini: '@bambini@',
      nome: '@nome@',
      cognome: '@cognome@',
      oraprenotazione: '@oraprenotazione@',
      dataprenotazione: '@dataprenotazione@',
      servizioextra: '@servizioextra@',
      linkmodificaprenotazione: '@linkmodificaprenotazione@',
      linkrichiestacartadicredito: '@linkrichiestacartadicredito@',
      avviso_uscita: '@avviso_uscita@',
    },
    detail: {
      close: 'Sluiten',
      pageSubtitle:
        'Configureer automatische berichtkanalen voor deze sjabloon.',
      marketingTitle: 'Marketingbericht',
      enableConfiguration: 'Deze configuratie inschakelen',
      enableConfigurationHint:
        'De wijzigingen gelden voor alle programma’s die {{type}} gebruiken.',
      enableWhatsApp: 'WhatsApp inschakelen',
      enableTemplates: 'Sjablonen inschakelen',
      enableEmail: 'E-mail inschakelen',
      emailObject: 'Onderwerp',
      configuration: 'Configuratie',
      configurationHint: 'Kies hoe je berichten wilt verzenden',
      configurationHintPreBooking: 'Configureer verzendmethoden en timing',
      configurationHintPostBooking:
        'Configureer verzending en planning van berichten na boeking',
      sendingMethods: 'Verzendmethoden',
      sendingMode: 'Verzendmodus',
      sendingTiming: 'Verzendtiming',
      whenToSend: 'Wanneer verzenden',
      sendingNow: 'Verzendtijd',
      timingImmediate: 'Bericht direct na de boeking verzenden 🕒',
      timingOneHourBefore: 'Bericht 1 uur van tevoren verzenden 🕒',
      timingHoursAdvance: 'Bericht {{count}} uur van tevoren verzenden 🕒',
      timingOneDayAdvance: 'Bericht 1 dag van tevoren verzenden 🕒',
      timingDaysAdvance: 'Bericht {{count}} dagen van tevoren verzenden 🕒',
      whenToSendOneDayLater: 'Bericht 1 dag later verzenden',
      whenToSendDaysLater: 'Bericht {{count}} dagen later verzenden',
      sendingWhatsAppOnly: 'Alleen WhatsApp verzenden',
      sendingWhatsAppAndEmail: 'WhatsApp en e-mail verzenden',
      whatsappMessage: 'WhatsApp-bericht',
      whatsappMessageDesc:
        'Configureer het WhatsApp-bericht dat naar de klant wordt gestuurd',
      whatsappForeign: 'WhatsApp buitenlandse klanten',
      whatsappForeignDesc:
        'Engelse versie. Automatisch bericht in het Engels voor klanten met een internationaal netnummer.',
      whatsappBusinessTemplate: 'WhatsApp Business-sjabloon',
      whatsappBusinessTemplateDesc:
        'Selecteer uit door Meta goedgekeurde sjablonen voor verzending via WhatsApp Business API.',
      smsMessage: 'SMS-bericht',
      smsMessageDesc:
        'Configureer het SMS-bericht dat naar de klant wordt gestuurd',
      email: 'E-mail',
      emailDesc:
        'Configureer het e-mailbericht dat naar de klant wordt gestuurd',
      emailSubject: 'E-mailonderwerp',
      emailSubjectPlaceholder: 'Bijv.: Communicatie van @hotel-name',
      bodyPlaceholder: 'Schrijf hier je bericht...',
      inspireMe: 'Inspireer me',
      testSending: 'Testverzending',
      testSendPrompt: 'Voer het nummer in waarnaar je het bericht wilt sturen',
      testSendAction: 'Verzenden',
      selectTemplate: 'Sjabloon selecteren',
      selectTemplateTooltip: 'Selecteer een door Meta goedgekeurde sjabloon',
      selectTemplateHint:
        'Kies een door Meta goedgekeurde sjabloon voor dit bericht.',
      selectedTemplate: 'Geselecteerde sjabloon',
      noTemplateSelected: 'Geen sjabloon geselecteerd',
      noTemplateHint:
        'Geen sjabloon geselecteerd. Selecteer een door Meta goedgekeurde sjabloon om verzending via WhatsApp Business API in te stellen.',
      templateCategory: 'Categorie',
      templateType: 'Type',
      templateLanguages: 'Talen',
      templateLanguage: 'Taal',
      templateVariableMap: 'Variabelenkaart',
      templateBody: 'Body',
      templateBodyEmpty: 'Geen body-voorbeeld beschikbaar voor deze taal.',
      ctaButton: 'Call-to-action',
      ctaDynamicPath: 'Dynamisch pad',
      ctaDynamicPathPlaceholder:
        'Dynamisch pad, bijv. /menu/xxxx0111 of /booking/0000101111',
      ctaDynamicPathHint:
        'Dit wordt bij verzending aan de sjabloon-URL toegevoegd, in plaats van {{1}}, zodat je links kunt maken op basis van de context van het bericht.',
      ctaDynamicPathRequired: 'Het dynamische pad mag niet leeg zijn.',
      ctaUrlValid: '{{url}} is geldig',
      ctaUrlInvalid: 'Ongeldige URL. Controleer het formaat.',
      ctaLinksTooltip: 'Welke opties heb ik?',
      ctaLinksTitle:
        'Beschikbare links voor services die op Pro20 zijn gemaakt',
      ctaLinksDesc: 'Selecteer een link om die als dynamisch pad te gebruiken.',
      ctaLinksEmpty:
        'Je hebt nog geen service op Pro20 gemaakt, dus er is niets voor te stellen.',
      ctaLinkUnnamed: 'Link zonder naam',
    },
    toast: {
      refreshed: 'Berichtenlijst vernieuwd.',
      saveSuccess: 'Berichtconfiguratie opgeslagen.',
      saveError: 'Berichtconfiguratie kon niet worden opgeslagen.',
      testSent: 'Testbericht succesvol verzonden.',
      sendError: 'Bericht kon niet worden verzonden.',
      templateSelected: 'Sjabloon geselecteerd.',
      resetSuccess: 'Configuratie hersteld naar standaardwaarden.',
      resetError: 'Configuratie kon niet worden gereset. Probeer het opnieuw.',
      applyDefaultsSuccess:
        'Standaardteksten toegepast en kanaal ingeschakeld.',
      applyDefaultsError:
        'Kon standaardwaarden niet toepassen. Probeer het opnieuw of annuleer.',
      detailUnavailable:
        'Berichtdetail is nog niet beschikbaar voor deze categorie.',
    },
    validation: {
      whatsappBodyRequired:
        'Voer een WhatsApp-bericht in wanneer WhatsApp is ingeschakeld.',
      templateRequired:
        'Selecteer een WhatsApp Business-sjabloon wanneer sjablonen zijn ingeschakeld.',
      emailSubjectRequired:
        'Voer het e-mailonderwerp in wanneer E-mail is ingeschakeld.',
      emailBodyRequired:
        'Voer de e-mailinhoud in wanneer E-mail is ingeschakeld.',
    },
    reset: {
      tooltip: 'Standaardconfiguratie herstellen',
      title: 'Configuratie resetten?',
      description:
        'Bestaande configuraties worden vervangen door de standaardconfiguratie.',
      confirm: 'Ja, doorgaan',
      cancel: 'Annuleren',
      resetting: 'Bezig met resetten…',
    },
    applyDefaults: {
      whatsappTooltip:
        'Standaardteksten toepassen en WhatsApp inschakelen voor deze groep',
      emailTooltip:
        'Standaardonderwerp en -tekst toepassen en e-mail inschakelen voor deze groep',
      whatsappAria: 'Standaard WhatsApp-configuratie toepassen',
      emailAria: 'Standaard e-mailconfiguratie toepassen',
      whatsappTitle: 'WhatsApp-standaardwaarden toepassen?',
      emailTitle: 'E-mailstandaardwaarden toepassen?',
      description:
        'Teksten van het gekozen kanaal worden vervangen en het kanaal wordt voor de hele groep ingeschakeld. Berichtactivatie, andere kanalen en verzendregels blijven ongewijzigd.',
      marketingWhatsappNote:
        'WhatsApp gebruikt het vrije bericht; gegevens van de officiële sjabloon blijven bewaard.',
      confirm: 'Toepassen en inschakelen',
      cancel: 'Annuleren',
      applying: 'Bezig met toepassen…',
    },
  },
  configureWhatsApp: {
    title: 'WhatsApp configureren',
    subtitle: 'Koppel een WhatsApp Business-nummer voor dit filiaal',
    disabled: {
      badge: 'Configuratie uitgeschakeld',
      title: 'WhatsApp-configuratie niet beschikbaar',
      description:
        'Ga verder via de mobiele app om deze functie in te schakelen.',
    },
    onboarding: {
      eyebrow: 'WhatsApp Business',
      title: 'Koppel het lokale WhatsApp-nummer',
      description:
        'Maak een nieuwe instantie aan, scan de QR-code en schakel het verzenden van automatische berichten vanuit het PRO20-platform in.',
      create: 'Configuratie aanmaken',
      guided: 'Begeleide en omkeerbare handeling',
      featureSecureTitle: 'Veilig & betrouwbaar',
      featureSecureDesc: 'Beveiliging op ondernemingsniveau',
      featureQuickTitle: 'Snelle setup',
      featureQuickDesc: 'Minder dan 2 minuten',
      featureReversibleTitle: 'Omkeerbaar',
      featureReversibleDesc: 'Wijzig of ontkoppel op elk moment',
      step1Title: 'Instantie aanmaken',
      step1Desc:
        'Genereer de technische configuratie gekoppeld aan het geselecteerde filiaal.',
      step1Time: '~ 30 sec',
      step2Title: 'QR scannen',
      step2Desc:
        'Open WhatsApp op je telefoon en koppel je apparaat via QR-code.',
      step2Time: '~ 60 sec',
      step3Title: 'Probeer te verzenden',
      step3Desc: 'Stuur een testbericht en controleer of alles werkt.',
      step3Time: '~ 30 sec',
    },
    creating: {
      title: 'Configuratie bezig…',
      description: 'Even geduld terwijl we je WhatsApp-instantie aanmaken.',
      starting:
        'WhatsApp wordt gestart. De eerste verbinding kan een paar minuten duren.',
    },
    success: {
      title: 'Voltooid',
      description: 'Instantie succesvol aangemaakt!',
      banner:
        'Je kunt nu je apparaat koppelen met een QR-code of een koppelingscode.',
      continue: 'Doorgaan',
    },
    configured: {
      heading: 'WhatsApp-instantie',
      subtitle: 'Beheer koppeling, status en testberichten voor dit filiaal.',
      statusInstanceCreated: 'Instantie aangemaakt',
      statusQr: 'Wachten op QR-scan',
      statusReady: 'Verbonden',
      statusCreating: 'Aanmaken…',
      statusFailed: 'Verbinding mislukt',
      restart: 'Verbinding opnieuw starten',
      stillStarting:
        'WhatsApp wordt nog gestart. Deze pagina blijft controleren. Gebruik QR scannen wanneer de code klaar is, of start de verbinding opnieuw.',
      failureHint:
        'De WhatsApp-engine is niet gestart. Start de verbinding opnieuw om het nog eens te proberen.',
      scanQr: 'QR scannen',
      delete: 'Verwijderen',
      deleting: 'Verwijderen...',
      statusDeleting: 'Verwijderen...',
      sessionName: 'Sessienaam',
      instanceId: 'Instantie-ID',
      connectedNumber: 'Gekoppeld nummer',
      operatingStatus: 'Operationele status',
      nextStepTitle: 'Volgende stap',
      nextStepQr:
        'Scan de QR-code met WhatsApp op je telefoon om het apparaat te koppelen.',
      notLinked: 'Nog niet gekoppeld',
    },
    link: {
      title: 'Apparaat koppelen',
      description:
        'Gebruik een QR-code of een telefoonkoppelingscode om WhatsApp te verbinden.',
      tabQr: 'QR-code',
      tabPhone: 'Mobiele code',
      qrInstructions:
        'Open WhatsApp op je telefoon → Gekoppelde apparaten → Apparaat koppelen en scan deze code.',
      waiting: 'Wachten op scan…',
      phoneInstructions:
        'Voer het mobiele nummer in dat gekoppeld wordt en vraag een koppelingscode aan.',
      phoneLabel: 'Mobiel nummer',
      phonePlaceholder: '333 123 4567',
      getCode: 'Code ophalen',
      pairingCode: 'Je koppelingscode',
      pairingHint:
        'Voer deze code in bij WhatsApp → Gekoppelde apparaten → Koppelen met telefoonnummer.',
    },
    delete: {
      title: 'WhatsApp-configuratie verwijderen?',
      description:
        'Hiermee wordt de instantie voor dit filiaal verwijderd. Je kunt later een nieuwe aanmaken.',
      confirm: 'Ja, verwijderen',
    },
    test: {
      title: 'Testbericht verzenden',
      phone: 'Ontvangernummer',
      phonePlaceholder: '333 123 4567',
      message: 'Bericht',
      defaultMessage: 'Hallo! Dit is een testbericht van Proventi.',
      send: 'Test verzenden',
      success: 'Testbericht succesvol verzonden.',
      disabledHint:
        'Scan de QR-code en koppel WhatsApp voordat je een testbericht verzendt.',
      notConnected:
        'De WhatsApp-sessie is niet verbonden. Scan de QR-code om eerst je apparaat te koppelen.',
    },
    toast: {
      linked: 'WhatsApp gekoppeld.',
      stillStarting:
        'WhatsApp wordt nog gestart. Deze pagina blijft controleren.',
      createFailed: 'WhatsApp-sessie aanmaken mislukt.',
      qrFailed: 'WhatsApp-QR voorbereiden mislukt.',
    },
  },
  configureWhatsAppOfficial: {
    title: 'WhatsApp Officieel configureren',
    subtitle:
      'Verbind je officiële WhatsApp Business-nummer (Meta) voor deze vestiging.',
    loading: 'Onboardingstatus laden…',
    loadError: 'Onboardingstatus laden mislukt.',
    retry: 'Opnieuw proberen',
    toast: {
      onboardSuccess: 'WhatsApp-onboarding succesvol voltooid.',
      onboardError: 'WhatsApp-onboarding voltooien mislukt.',
      registerSuccess: 'Telefoonnummer succesvol geregistreerd.',
      registerError: 'Telefoonnummer registreren mislukt.',
      businessDetailsSuccess: 'Bedrijfsgegevens succesvol geladen.',
      businessDetailsError: 'Bedrijfsgegevens laden mislukt.',
      missingPhoneNumberId: 'Telefoonnummer-ID ontbreekt.',
      metaError: 'WhatsApp-aanmeldfout: {{message}}',
      missingBranch: 'Vestigingscode ontbreekt.',
      sdkNotReady: 'Facebook SDK is nog niet klaar.',
    },
    onboarding: {
      eyebrow: 'WhatsApp Business API',
      title: 'Verbinden met WhatsApp Business API',
      description:
        'In dit gedeelte kun je een nummer aanmaken en verbinden met het officiële WhatsApp Business-kanaal. Met de configuratie koppel je je zakelijke nummer aan het platform om berichten professioneel, geautomatiseerd en beleidsconform te versturen en te ontvangen.',
      connect: 'Configuratie aanmaken',
      connecting: 'Verbinden…',
      howToConnect: 'Hoe verbinden',
      featuresTitle: 'Met Meta kun je',
      featureExisting: 'Een bestaand WhatsApp Business-nummer verbinden',
      featureNew: 'Een nieuw account bij Meta aanmaken',
      step1Title: 'Registreer je nummer',
      step1Desc:
        'Voeg je zakelijke telefoonnummer toe en verifieer het bij Meta om het kanaal te activeren.',
      step1Time: '~ 2 min',
      step2Title: 'Template maken en laten goedkeuren',
      step2Desc:
        'Maak een berichttemplate en dien deze in bij Meta voor beoordeling en goedkeuring.',
      step2Time: '~ 1 dag',
      step3Title: 'Tekst-template versturen',
      step3Desc:
        'Verstuur een goedgekeurde template om een nieuw gesprek te starten.',
      step3Time: '~ 30 sec',
      step4Title: 'Naar een telefoonnummer versturen',
      step4Desc:
        'Bezorg berichten aan elk telefoonnummer van een klant en begin te chatten.',
      step4Time: '~ 30 sec',
    },
    configured: {
      heading: 'WhatsApp-onboardinginformatie',
      subtitle: 'Je officiële WhatsApp Business-account is verbonden.',
      connectedBadge: 'Verbonden',
      phoneNumberId: 'Telefoonnummer-ID',
      wabaId: 'WhatsApp Business-account-ID',
      businessId: 'Bedrijfs-ID',
      onboardingStatus: 'Onboardingstatus',
      phoneRegistrationStatus: 'Registratiestatus nummer',
      statusCompleted: 'Voltooid',
      statusSuccess: 'Geslaagd',
      statusPending: 'In behandeling',
      statusRegistered: 'Geregistreerd',
      statusNotRegistered: 'Niet geregistreerd',
      registerPhone: 'Registreer je nummer',
      registeringPhone: 'Registreren…',
      businessDetailsHeading: 'Bedrijfsgegevens',
      businessDetailsEmpty:
        'Bedrijfsgegevens zijn nog niet beschikbaar. Haal ze op via Meta met je telefoonnummer-ID.',
      fetchBusinessDetails: 'Bedrijfsgegevens ophalen',
      fetchingBusinessDetails: 'Ophalen…',
      verifiedName: 'Geverifieerde naam',
      displayPhoneNumber: 'Weergegeven telefoonnummer',
      qualityRating: 'Kwaliteitsbeoordeling',
      codeVerificationStatus: 'Codeverificatie',
      platformType: 'Platformtype',
      throughputLevel: 'Doorvoer',
      phoneStatus: 'Telefoonstatus',
      accountMode: 'Accountmodus',
      nameStatus: 'Naamstatus',
      messagingLimitTier: 'Berichtenlimiet',
      officialBusinessAccount: 'Officieel zakelijk account',
      businessProfileHeading: 'Bedrijfsprofiel',
      email: 'Zakelijk e-mailadres',
      vertical: 'Bedrijfscategorie',
      description: 'Bedrijfsomschrijving',
      about: 'Over',
      address: 'Adres',
      websites: 'Websites',
      metaPhoneNumberId: 'Meta-telefoonnummer-ID',
      webhookApplication: 'Webhook-URL',
      reset: 'Configuratie opnieuw instellen',
      resetting: 'Opnieuw instellen…',
    },
    reset: {
      title: 'Configuratie opnieuw instellen?',
      description:
        'Dit reset alleen de lokale weergave zodat je de configuratie opnieuw kunt uitvoeren. De backend-onboarding wordt niet verwijderd.',
      confirm: 'Ja, opnieuw instellen',
    },
    guide: {
      title: 'Onboardinggids WhatsApp Business API',
      intro:
        'Welkom! Volg de onderstaande stappen om je WhatsApp Business-account met ons platform te verbinden.',
      prerequisitesTitle: 'Vereisten',
      prerequisitesIntro: 'Zorg voordat je begint dat je het volgende hebt:',
      prereq1: 'Een Facebook-account met beheerderstoegang.',
      prereq2:
        'Een Meta Business Manager-account (of klaar om er een aan te maken).',
      prereq3:
        'Een geldig mobiel nummer dat verificatie via sms of spraakoproep kan ontvangen.',
      prereq4:
        'Een telefoonnummer dat momenteel niet is geregistreerd bij WhatsApp Messenger, de WhatsApp Business-app of een andere WhatsApp Business API-provider.',
      prereq5:
        'Je bedrijfsgegevens gereed: bedrijfsnaam, website (optioneel), e-mail en categorie.',
      prereq6:
        'Een geldige credit-/debetkaart om als betaalmethode aan Meta toe te voegen.',
      prereq7: 'Een stabiele internetverbinding.',
      prereqNote:
        'Belangrijk: als je telefoonnummer momenteel in de WhatsApp Business-app wordt gebruikt, verwijder het dan uit de app voordat je het verbindt met het WhatsApp Business Platform.',
      stepsTitle: 'Onboardingstappen',
      step1Title: 'Stap 1 – Log in op ons platform',
      step1Body:
        'Log in op je account, ga naar WhatsApp-integratie en klik op WhatsApp verbinden.',
      step2Title: 'Stap 2 – Log in met Facebook',
      step2Body:
        'Er wordt een beveiligd Meta (Facebook)-inlogvenster geopend. Log in met je Facebook-account en klik op Doorgaan.',
      step3Title: 'Stap 3 – Selecteer of maak een Business Manager',
      step3Body:
        'Selecteer je bestaande Meta Business Manager of klik op Bedrijfsaccount aanmaken en voer je bedrijfsnaam, e-mail, website en land in, en klik daarna op Volgende.',
      step4Title: 'Stap 4 – Maak of selecteer een WhatsApp Business-account',
      step4Body:
        'Gebruik een bestaand WhatsApp Business-account of maak een nieuw account aan door de accountnaam, weergavenaam en bedrijfscategorie op te geven, en klik daarna op Doorgaan.',
      step5Title: 'Stap 5 – Voeg je telefoonnummer toe',
      step5Body:
        'Selecteer je land, voer je telefoonnummer in, kies verificatie via sms of spraakoproep en voer daarna de 6-cijferige verificatiecode in.',
      step6Title: 'Stap 6 – Verleen machtigingen',
      step6Body:
        'Meta vraagt de benodigde machtigingen aan. Klik op Doorgaan en Voltooien; ons platform voltooit de installatie automatisch.',
      step7Title: 'Stap 7 – Verbinding voltooid',
      step7Body:
        'Zodra de verbinding tot stand is gebracht, zie je je verbonden telefoonnummer, WhatsApp Business-account en verbindingsstatus en ben je klaar om berichten te versturen en te ontvangen.',
      nextStepsTitle: 'Aanbevolen volgende stappen',
      nextStep1: 'Maak je WhatsApp-berichttemplates.',
      nextStep2: 'Voeg een geldige betaalmethode toe in Meta Business Manager.',
      nextStep3: 'Voltooi de Meta-bedrijfsverificatie (aanbevolen).',
      nextStep4: 'Verstuur een test-templatebericht.',
      nextStep5:
        'Configureer je chatbot of automatisering (indien van toepassing).',
      notesTitle: 'Belangrijke opmerkingen',
      note1:
        'Eén telefoonnummer kan slechts met één WhatsApp-account tegelijk worden verbonden.',
      note2:
        'Verwijder je WhatsApp Business-account niet uit Meta na de onboarding.',
      note3:
        'Houd je betaalmethode actief om onderbrekingen in de bezorging van berichten te voorkomen.',
      note4:
        'Bedrijfsverificatie wordt aanbevolen voor hogere berichtlimieten.',
      note5:
        'Goedkeuring van de weergavenaam wordt door Meta afgehandeld en kan enige tijd duren.',
      issuesTitle: 'Veelvoorkomende problemen',
      issue1Title: 'Verificatiecode niet ontvangen',
      issue1Body:
        'Controleer of het telefoonnummer correct is, probeer de optie spraakoproep en wacht een paar minuten voordat je een nieuwe code aanvraagt.',
      issue2Title: 'Telefoonnummer al geregistreerd',
      issue2Body:
        'Het nummer is verbonden met WhatsApp Messenger, de WhatsApp Business-app of een andere WhatsApp Business API-provider. Verwijder het uit de bestaande service vóór de onboarding.',
      issue3Title: 'Machtigingsfout',
      issue3Body:
        'Zorg ervoor dat je beheerder bent van zowel het Facebook-account als de Meta Business Manager.',
      issue4Title: 'Betaling vereist',
      issue4Body:
        'Meta vereist een geldige betaalmethode voordat bepaalde gesprekken kunnen worden gestart. Voeg je betaalmethode toe in Meta Business Manager.',
      issue5Title: 'Bedrijfsverificatie in behandeling',
      issue5Body:
        'Je account kan nog steeds worden verbonden en gebruikt, maar het voltooien van de bedrijfsverificatie wordt sterk aanbevolen om hogere berichtlimieten te ontgrendelen en het vertrouwen in het account te vergroten.',
      needHelpTitle: 'Hulp nodig?',
      needHelpIntro:
        'Als je tijdens de onboarding een probleem tegenkomt, neem dan contact op met ons supportteam met:',
      help1: 'Een screenshot van de fout.',
      help2: 'Telefoonnummer (inclusief landcode).',
      help3: 'Meta Business Manager-ID (indien beschikbaar).',
      help4: 'WhatsApp Business-account-ID (indien beschikbaar).',
      needHelpOutro:
        'Door deze informatie te verstrekken kunnen we je probleem sneller oplossen.',
    },
  },
  whatsAppOfficial: {
    title: 'WhatsApp Official',
    subtitle: 'Manage inbox conversations for the official WhatsApp channel.',
    systemBadge: 'System',
    disabled: {
      badge: 'Sectie niet ingeschakeld',
      title: 'Deze sectie is niet ingeschakeld',
      description:
        'Deze functie is momenteel uitgeschakeld voor je account. Neem contact op met support voor meer informatie.',
      infoTitle: 'Toegang tot deze sectie nodig?',
      infoDescription:
        'Neem contact op met ons supportteam en we helpen je op weg.',
      footerHelp: 'Nog hulp nodig?',
      footerHelpDesc: 'Ons supportteam is 24/7 beschikbaar om je te helpen.',
    },
    inbox: {
      searchPlaceholder: 'Search conversations...',
      noConversations: 'No conversations found',
      noConversationsDesc:
        'New chats will appear here as soon as customers contact this branch.',
      emptySelectionTitle: 'Select a chat',
      emptySelectionDescription:
        'Choose a conversation from the list to view the thread and reply.',
    },
    thread: {
      sessionLabel: 'State 24 hours',
      sessionTooltip:
        'When the 24-hour service window is active, you can send free text replies. Otherwise, you must send an approved template first.',
      active: 'Active',
      inactive: 'Inactive',
      today: 'Today',
      yesterday: 'Yesterday',
      emptyTitle: 'No messages yet',
      emptyDescription:
        'This conversation is ready to receive the first inbound or outbound message.',
      pending: 'Versturen…',
      retry: 'Opnieuw proberen',
      retryConfirmTitle: 'Bericht opnieuw proberen?',
      retryConfirmMessage:
        'Weet je zeker dat je dit bericht opnieuw wilt versturen?',
      billingFree: 'Gratis',
      billingPaid: 'Betaald',
    },
    delivery: {
      warningTooltip: 'Niet bezorgd — tik voor details',
      detailsTitle: 'Bezorgingsprobleem',
      fallbackTitle: 'Bericht is niet bezorgd',
      technicalDetails: 'Technische details',
      noTechnicalDetails: 'Geen technische details beschikbaar.',
    },
    composer: {
      placeholder: 'Schrijf een bericht...',
      send: 'Versturen',
      submitTemplate: 'Sjabloon versturen',
      templateOnlyTitle: 'Sjabloon vereist',
      templateOnlyDescription:
        'Het 24-uurs servicevenster is inactief. Stuur een goedgekeurd sjabloon om het gesprek te heropenen.',
      attach: 'Bestand bijvoegen',
      attachImage: 'Afbeelding',
      attachAudio: 'Audio',
      attachDocument: 'Document',
      removeAttachment: 'Bijlage verwijderen',
    },
    templateSend: {
      title: 'Sjabloon versturen',
      fillVariablesTitle: 'Variabelen invullen',
      customerLabel: 'Klant',
      phoneLabel: 'Telefoon ontvanger',
      prefix: 'Voorvoegsel',
      number: 'Nummer',
      sendingTo: 'Verzenden naar',
      phoneInvalid: 'Voer voorvoegsel en telefoonnummer in vóór verzenden.',
      enterValuePlaceholder: 'Voer waarde in',
      fieldRequired: 'Dit veld is verplicht',
      ctaUrlSuffix: 'CTA-URL-achtervoegsel',
      ctaUrlSuffixPlaceholder: 'URL-achtervoegsel',
      ctaUrlSuffixHint:
        'Wordt toegevoegd aan de knop-URL wanneer het sjabloon een dynamische link heeft.',
      noVariables: 'Dit sjabloon heeft geen variabelen om in te vullen.',
      sendMessage: 'Bericht versturen',
      cancel: 'Annuleren',
      previewTitle: 'Voorbeeld',
      infoCategory: 'Categorie',
      infoLanguage: 'Taal',
      infoType: 'Type',
      infoSendingTo: 'Verzenden naar',
    },
    toast: {
      sendSuccess: 'Bericht verzonden.',
      sendError: 'Bericht kon niet worden verzonden.',
      templateSendSuccess: 'Sjabloon verzonden.',
      templateSendError: 'Sjabloon kon niet worden verzonden.',
      loadConversationsError: 'Gesprekken konden niet worden geladen.',
      loadMessagesError: 'Berichten konden niet worden geladen.',
    },
  },
  templateslist: {
    import_no_branch: 'Branch code is missing. Please try again.',
    processing: 'Processing…',
    import_global_template: 'Import global template',
    bulk_import_started: 'Bulk import started',
    import_failed: 'Could not import templates.',
    bulk_import_job_not_found: 'Bulk import job not found.',
    bulk_import_poll_timeout: 'Bulk import timed out. Please try again.',
    bulk_import_bad_accept_response:
      'Invalid response from bulk import request.',
    bulk_import_failed_with_reason: 'Bulk import failed: {{message}}',
    bulk_import_completed_summary:
      'Bulk import completed. Success: {{success}}, Failed: {{failed}}',
    import_success: 'Global templates imported.',
  },
  whatsAppTemplates: {
    browseGroups: 'Sjabloongroepen',
    allGroups: 'Alle sjablonen',
    allStatuses: 'Alle statussen',
    filterStatus: 'Filter op status',
    searchEmpty: 'Geen sjablonen komen overeen met de zoekopdracht of filters.',
    groups: {
      UTILITY: 'Service',
      MARKETING: 'Marketing',
      AUTHENTICATION: 'Authenticatie',
      OTHER: 'Overige',
    },
    title: 'Sjablonen',
    subtitle:
      'Organiseer sjablonen voor snelle toegang en schaal communicatie met vooraf gedefinieerde berichten.',
    learnMore: 'Meer informatie',
    importGlobal: 'Globaal sjabloon importeren',
    addTemplate: 'Sjabloon toevoegen',
    tableTitle: 'Sjablonen',
    sync: 'Sjablonen synchroniseren',
    searchPlaceholder: 'Zoek sjablonen...',
    emptyTitle: 'Geen sjablonen gevonden',
    emptyDescription:
      'Maak of synchroniseer sjablonen om klanten te berichten.',
    detailsTitle: 'Sjabloondetails',
    deleteTitle: 'Sjabloon verwijderen?',
    deleteDescription:
      'Weet je zeker dat je "{{name}}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
    columns: {
      statusDate: 'Status / Datum',
      nameType: 'Naam / Type',
      category: 'Categorie',
      language: 'Taal',
      actions: 'Acties',
    },
    stats: {
      approved: 'Goedgekeurde sjablonen',
      pending: 'Sjablonen in afwachting',
      draft: 'Conceptsjablonen',
      rejected: 'Afgewezen sjablonen',
    },
    status: {
      APPROVED: 'APPROVED',
      PENDING: 'PENDING',
      DRAFT: 'DRAFT',
      REJECTED: 'REJECTED',
    },
    languages: {
      en: 'English',
      it: 'Italiano',
      es: 'Español',
      fr: 'Français',
      nl: 'Nederlands',
      zh: '中文',
    },
    actions: {
      menu: 'Sjabloonacties',
      edit: 'Bewerken',
      delete: 'Verwijderen',
    },
    toast: {
      learnMoreSoon: 'Documentatie is binnenkort beschikbaar.',
      importSoon: 'Globale import is binnenkort beschikbaar.',
      createSoon: 'Sjablonen maken is binnenkort beschikbaar.',
      syncSuccess: 'Sjablonen succesvol gesynchroniseerd.',
      syncError: 'Sjablonen konden niet worden gesynchroniseerd.',
      importSuccess: 'Globale sjablonen geïmporteerd.',
      importError: 'Globale sjablonen konden niet worden geïmporteerd.',
      loadError: 'Sjablonen konden niet worden geladen.',
      deleteSuccess: 'Sjabloon verwijderd.',
      deleteError: 'Sjabloon kon niet worden verwijderd.',
    },
    create: {
      title: 'Nieuw sjabloon',
      subtitle: 'Maak eenvoudig nieuwe sjablonen volgens je behoeften.',
      backToList: 'Terug naar sjablonen',
      formTitle: 'Nieuw sjabloon toevoegen',
      editTitle: 'Sjabloon bewerken',
      editSubtitle: 'Werk een afgewezen sjabloon bij en dien het opnieuw in.',
      editFormTitle: 'Sjabloon bewerken',
      category: 'Categorie',
      name: 'Sjabloonnaam',
      namePlaceholder: 'bijv. booking_confirmation_v1',
      language: 'Sjabloontaal',
      typeLabel: 'Type sjabloon',
      types: {
        text: 'Tekst',
        image: 'Afbeelding',
        video: 'Video',
        audio: 'Audio',
        document: 'Document',
      },
      categories: {
        UTILITY: 'Utility',
        MARKETING: 'Marketing',
        AUTHENTICATION: 'Authenticatie',
      },
      uploadFile: 'Bestand uploaden',
      browse: 'Bladeren…',
      uploading: 'Uploaden…',
      clearMedia: 'Wissen',
      noFileChosen: 'Geen bestand gekozen',
      orPasteUrl: 'Of plak handle / URL',
      mediaUrlPlaceholder: 'https://…',
      previewHandlePlaceholder:
        'Headermedia geüpload (voorbeeld niet beschikbaar)',
      mediaHintImage: 'Alleen JPEG of PNG, maximaal 5 MB.',
      mediaHintVideo: 'Alleen MP4, maximaal 16 MB, max 60 seconden.',
      mediaHintAudio: 'MP3, OGG of M4A, maximaal 16 MB.',
      mediaHintDocument: 'PDF of Office-documenten, maximaal 100 MB.',
      heading: 'Koptekst (optioneel)',
      headingHint: 'Wordt boven de berichttekst getoond.',
      body: 'Tekst',
      footer: 'Voettekst',
      variablesTitle: 'Variabelen',
      variables: {
        requested_amount: 'Gevraagde aanbetaling',
        balance_due: 'Resterend saldo',
        paid_total: 'Totaal ontvangen',
        deposit_percentage: 'Aanbetalingspercentage',
        payment_link: 'Betaallink voor aanbetaling',

        service_name: 'Service',
        order_number: 'Bestelnummer',
        ordering_channel: 'Afhalen / Delivery',
        scheduled_label: 'Datum en tijd van de bestelling',
        total_amount: 'Besteltotaal',
        order_link: 'Link naar besteloverzicht',
        delivery_address: 'Bezorgadres',
        pickup_address: 'Afhaaladres',
        paid_amount: 'Betaald bedrag',
        refund_amount: 'Terugbetaald bedrag',
        cancellation_reason: 'Annuleringsreden',
        failure_reason: 'Reden mislukte bezorging',
        amount_due: 'Te betalen bedrag',

        customerName: 'Klantnaam',
        branchName: 'Vestigingsnaam',
        bookingId: 'Boekings-ID',
        discountCode: 'Kortingscode',
        promoCode: 'Promocode',
        bookingDate: 'Boekingsdatum',
        bookingTime: 'Boekingstijd',
        bookingUpdateLink: 'Link boeking wijzigen',
        branchCode: 'Vestigingscode',
        guestCount: 'Aantal gasten',
        childrenCount: 'Aantal kinderen',
        creditCardRequestUrl: 'URL creditcardaanvraag',
        depositRequestLink: 'Link stortingsaanvraag',
        depositAmount: 'Stortingsbedrag',
        expectedLeaveTime: 'Verwachte vertrektijd',
        qrCodeImageUrl: 'URL QR-codeafbeelding',
      },
      buttonsTitle: 'Knoppen',
      buttonType: 'Knoptype',
      buttonText: 'Knoptekst',
      buttonUrl: 'Knop-URL',
      quickReply: 'Snel antwoord',
      ctaUrl: 'CTA URL',
      addQuickReply: 'Snel antwoord',
      addCta: 'CTA',
      removeButton: 'Verwijderen',
      buttonsHint: 'Je kunt tot 3 knoppen per sjabloon toevoegen.',
      submit: 'Versturen',
      previewTitle: 'WhatsApp-voorbeeld',
      previewEmpty: 'Je sjabloonvoorbeeld verschijnt hier.',
      validation: {
        categoryRequired: 'Selecteer een categorie.',
        nameMin: 'Naam moet minstens 3 tekens hebben.',
        nameMax: 'Naam mag maximaal 60 tekens hebben.',
        nameFormat: 'Gebruik alleen kleine letters, cijfers en underscores.',
        languageRequired: 'Selecteer een taal.',
        bodyRequired: 'Tekst is verplicht.',
        bodyMax: 'Tekst mag maximaal 1024 tekens hebben.',
        mediaRequired: 'Media is verplicht voor afbeelding- en videosjablonen.',
        invalidImageType: 'Afbeelding moet JPEG of PNG zijn.',
        invalidVideoType: 'Video moet MP4 zijn.',
        imageTooLarge: 'Afbeelding mag maximaal 5 MB zijn.',
        videoTooLarge: 'Video mag maximaal 16 MB zijn.',
        videoDurationMax: 'Video mag maximaal 60 seconden duren.',
        videoDurationUnreadable: 'Videoduur kon niet worden gelezen.',
        buttonLabelRequired: 'Knoptekst is verplicht.',
        buttonUrlRequired: 'CTA-knoppen vereisen een URL.',
        qrPlaceholder: 'Snelantwoordtekst mag geen variabelen bevatten.',
        qrMax: 'Snelantwoordtekst mag maximaal 25 tekens hebben.',
        urlInvalid: 'CTA-URL moet een geldig http(s)-adres zijn.',
      },
      toast: {
        success: 'Sjabloon succesvol aangemaakt.',
        error: 'Sjabloon kon niet worden aangemaakt.',
        updateSuccess: 'Sjabloon succesvol bijgewerkt.',
        updateError: 'Sjabloon kon niet worden bijgewerkt.',
        uploadSuccess: 'Headermedia geüpload.',
        uploadFailed: 'Headermedia kon niet worden geüpload.',
      },
    },
  },
  notifications: {
    title: 'Meldingen',
    allCaughtUp: 'Alles bijgewerkt',
    unread: 'ongelezen melding',
    unreadPlural: 'ongelezen meldingen',
    markAllRead: 'Alles als gelezen markeren',
    noNotifications: 'Geen meldingen',
    noNotificationsDesc: 'Je bent bij. Nieuwe meldingen verschijnen hier.',
    viewAll: 'Alle meldingen bekijken',
  },
  bookings: {
    history: {
      title: 'Boekingsgeschiedenis',
      subtitle: 'Boekingen voor {{branch}}',
      subtitleDefault: 'Bekijk en beheer boekingsgeschiedenis',
      totalBookings: '{{count}} boekingen',
      bookingsForDateBefore: '{{count}} boekingen voor ',
      bookingsForDateAfter: '',
      previousDay: 'Vorige dag',
      nextDay: 'Volgende dag',
      calendar: {
        title: 'Selecteer boekingsdatum',
        openCalendar: 'Open boekingskalender',
        previousMonth: 'Vorige maand',
        nextMonth: 'Volgende maand',
        bookings: '{{count}} boekingen',
        arrived: '{{count}} gearriveerd',
        refused: '{{count}} geweigerd',
        legendBookings: 'Boekingen',
        legendArrived: 'Gearriveerd',
        legendRefused: 'Geweigerd',
      },
      calendarConfig: {
        eyebrow: 'Reserveringskalender',
        title: 'Deze dag configureren',
        subtitle: 'Bewerk alleen de geselecteerde datum in de kalender.',
        branchLabel: 'Vestiging',
        singleDay: 'Alleen deze dag',
        updateDate: 'Bijwerken {{date}}',
        dateRange: 'Datumbereik',
        dateRangeHint: 'Meerdere dagen maken of overschrijven',
        eventLabel: 'Herhaling of evenement',
        eventPlaceholder: 'Voeg een herhaling of evenementnaam toe',
        daysSelected: '{{count}} dag geselecteerd',
        daysSelectedPlural: '{{count}} dagen geselecteerd',
        rangePlaceholder: 'Selecteer datumbereik',
        dateRangeRequired: 'Selecteer een begin- en einddatum.',
        rulesTitle: 'Boekingsregels',
        manual: 'Handmatig',
        acceptTo: 'Accepteren tot',
        acceptToValue: 'Nummer',
        acceptToRequired: 'Dit veld is verplicht',
        acceptAll: 'Alles accepteren',
        refuseAll: 'Alles weigeren',
        manualHint:
          'Handmatig laat de beslissing aan de normale app/portaalstroom.',
        close: 'Sluiten',
        saveDay: 'Dag opslaan',
        saveRange: 'Bereik opslaan',
        loadError: 'Dagconfiguratie laden mislukt.',
        saveSuccess: 'Dagconfiguratie opgeslagen.',
        saveError: 'Dagconfiguratie opslaan mislukt.',
      },
      exportPdf: 'PDF van de reserveringen van vandaag downloaden',
      exportPdfGeneratingTitle: 'PDF genereren',
      exportPdfGeneratingDescription:
        'Het reserveringsbestand wordt voorbereid...',
      exportPdfReadyTitle: 'PDF klaar',
      exportPdfReadyDescription: 'Het bestand is succesvol gegenereerd.',
      exportPdfOpen: 'PDF openen',
      exportPdfClose: 'Sluiten',
      exportPdfError: 'PDF genereren mislukt. Probeer het opnieuw.',
      addBooking: 'Boeking toevoegen',
      shifts: {
        all: 'Alles',
        filterLabel: 'Service',
        breakfast: 'Ontbijt',
        daily: 'Lunch',
        happyhour: 'Happy hour',
        night: 'Diner',
      },
      searchPlaceholder: 'Zoek klant, e-mail, telefoon of boekingscode',
      filterStatus: 'Filter statussen',
      callCustomer: 'Bel {{name}}',
      moreActions: 'Meer acties',
      snapshot: {
        bookings: 'Reserveringen',
        confirmed: 'Bevestigd',
        arrived: 'Aangekomen',
        waiting: 'In afwachting',
        issues: 'No-show / geweigerd',
        configureShifts: 'Dagregels configureren',
        filterShift: 'Filter {{shift}}',
        shiftSelected: '{{shift}} geselecteerd — klik om te wissen',
        shiftUnavailable:
          '{{shift}} is niet geconfigureerd voor dit restaurant',
      },
      itemsPerPage: 'Items per pagina',
      range: '{{start}} - {{end}} van {{total}}',
      date: 'Datum',
      time: 'Tijd',
      timeAndGuests: 'Tijd en gasten',
      customer: 'Klant',
      guests: 'Gasten',
      guestCount: '{{count}} gasten',
      childrenCount: '+ {{count}} kinderen',
      event: 'Evenement',
      noEvent: 'no event',
      contacts: 'Contacten',
      details: 'Details',
      openImage: 'Afbeelding openen',
      openPdf: 'PDF van de reservering openen',
      googleReservation: 'Reservering via Google Reservation',
      walkIn: 'Walk in',
      associatedServicesProducts: 'Gekoppelde diensten/producten',
      generatedBooking: 'Gegenereerde reservering {{score}}/100',
      extraServices: 'Extra diensten {{count}}',
      extraServicesOnly: 'Extra diensten',
      tables: 'Toegewezen tafels',
      noTables: 'Geen tafels toegewezen',
      notes: 'Privénotities',
      noNotes: 'Geen privénotities',
      manageBooking: 'Booking beheren',
      eventDetails: {
        title: 'Evenementdetails',
        eventName: 'Evenementnaam',
        eventCode: 'Evenementcode',
        startDate: 'Start',
        maxCapacity: 'Capaciteit',
        eventDescription: 'Beschrijving',
        eventImage: 'Evenementafbeelding',
        formCodes: 'Formulieren',
        noImage: 'Geen afbeelding',
      },
      menuDetails: {
        title: 'Menudetails',
        menuName: 'Menunaam',
        peopleCount: 'Aantal personen',
      },
      viewBookingImage: 'Boekingsafbeelding bekijken',
      bookingImage: 'Boekingsafbeelding',
      users: 'users',
      status: 'Status',
      processedBy: 'Verwerkt door',
      source: 'Bron',
      noBranch: 'Selecteer een vestiging om de geschiedenis te bekijken',
      empty: 'Geen boekingen gevonden',
      emptyDesc: 'Er zijn geen reserveringen voor de dag {{date}}.',
      updateStatus: 'Boekingsstatus bijwerken',
      confirmOperationTitle: 'Bewerking bevestigen?',
      confirmStatusChange: 'Boekingsstatus bijwerken naar "{{status}}"?',
      notifyCustomerTitle: 'Klant op de hoogte stellen?',
      notifyCustomerMessage:
        'Wilt u de klant op de hoogte stellen van deze wijziging?',
      statusUpdateSuccess: 'Boekingsstatus succesvol bijgewerkt.',
      statusUpdateError:
        'Boekingsstatus bijwerken mislukt. Probeer het opnieuw.',
      updatingStatus: 'Status bijwerken...',
      associatedServices: {
        title: 'Gekoppelde diensten/producten',
        reference: 'Boeking {{code}}',
        quantity: '×{{count}}',
        empty: 'Geen gekoppelde diensten voor deze boeking.',
        paid: 'Betaald',
        unpaid: 'Niet betaald',
      },
      messageHistory: {
        open: 'Berichtgeschiedenis',
        communicationError: 'Berichtbezorging mislukt',
        title: 'Berichtgeschiedenis',
        reference: 'Referentie {{code}}',
        refresh: 'Vernieuwen',
        empty: 'Geen berichten verzonden voor deze boeking.',
        loadError: 'Berichtgeschiedenis laden mislukt.',
        endpoint: 'Endpoint',
        eventCode: 'Eventcode',
        payload: 'Payload',
        response: 'Antwoord',
        statusProcessed: 'Verzonden',
        statusPending: 'In behandeling',
        messageText: 'Bericht',
        messageTextUnavailable: 'Berichttekst is niet beschikbaar.',
        httpStatus: 'HTTP {{status}}',
        attempts: '{{count}} pogingen',
        resubmit: 'Bericht opnieuw verzenden',
        resubmitSuccess: 'Bericht opnieuw verzonden.',
        resubmitError:
          'Bericht opnieuw verzenden mislukt. Probeer het opnieuw.',
        resubmitConfirmTitle: 'Bericht opnieuw verzenden?',
        resubmitConfirmMessage:
          'Weet u zeker dat u dit bericht opnieuw wilt verzenden?',
      },
      statusActions: {
        confirm: 'Bevestiging',
        refuse: 'Weigeren',
        eliminate: 'Elimineren',
        arrived: 'Gearriveerd',
        notArrived: 'Niet gearriveerd',
        notifyUser: 'Gebruiker melden om naar de locatie te komen',
        bookingAlreadyProcessed: 'Boeking al verwerkt',
        moveToConfirm: 'Verplaats naar bevestigd',
      },
      statuses: {
        IN_ATTESA: 'In afwachting',
        MODIFICATO_DA_UTENTE: 'Gewijzigd door gebruiker',
        ELIMINATO_DA_UTENTE: 'Verwijderd door gebruiker',
        MODIFICA_CONFERMATA: 'Wijziging bevestigd',
        MODIFICA_RIFIUTATA: 'Wijziging geweigerd',
        PRE_CONFERMA_PAGAMENTO: 'Voorbevestiging betaling',
        CONFERMATO: 'Bevestigd',
        AVVISATO_LISTA_ATTESA: 'Wachtlijst gemeld',
        ARRIVATO: 'Aangekomen',
        NON_ARRIVATO: 'Niet verschenen',
        RIFIUTATO: 'Geweigerd',
        LISTA_ATTESA: 'Wachtlijst',
        ELIMINATO: 'Geannuleerd',
      },
    },
    payment: {
      column: 'Betalingen',
      close: 'Sluiten',
      connectRequired:
        'Stripe Connect is niet klaar voor deze locatie. Rond de onboarding af om betalingen te beheren.',
      loadError: 'Kon de betalingsoperaties van deze reservering niet laden.',
      linkCopied: 'Betaallink gekopieerd.',
      linkCopyError: 'Kon de betaallink niet kopiëren.',
      linkReadyTitle: 'Aanbetalingslink klaar',
      linkReadyDescription:
        'De link is klaar om via jouw kanalen te versturen.',
      badge: {
        title: 'Betalingen',
        cardPresent: 'Kaart aanwezig',
        cardMissing: 'Geen kaart',
        paidPendingOps: 'Betaald · openstaande operaties',
        manage: 'Beheren',
        noCard: 'Geen kaart gekoppeld',
        verifiedSuccess: 'Succesvol geverifieerd',
        depositAuthorizedCapture: 'Aanbetaling geautoriseerd · Innen',
        depositCaptured: 'Aanbetaling geïnd',
        depositRefunded: 'Aanbetaling terugbetaald',
        depositPartiallyRefunded: 'Aanbetaling gedeeltelijk terugbetaald',
        depositPrefix: 'Aanbetaling',
      },
      tooltip: {
        cardPresent: 'Open opgeslagen kaartgegevens van deze locatie',
        cardMissing: 'Stuur de klant een verzoek om kaartgegevens',
        openOperations:
          'Open aanbetaling of betalingsoperaties van deze reservering',
        openVerified: 'Open betalingsdetails en geverifieerde kaart',
        noCard: 'Geen kaart gekoppeld. Open om betalingen te beheren',
      },
      status: {
        notRequired: 'Niet vereist',
        pending: 'Wacht op betaling',
        awaitingConfirmation: 'Betaling verzonden, wacht op Stripe-bevestiging',
        cardVerified: 'Kaart geverifieerd',
        authorized: 'Niet geïnd',
        paid: 'Betaald',
        partiallyPaid: 'Gedeeltelijk betaald',
        failed: 'Mislukt',
        canceled: 'Geannuleerd',
        refundPending: 'Terugbetaling bezig',
        partiallyRefunded: 'Gedeeltelijk terugbetaald',
        refunded: 'Terugbetaald',
        refundFailed: 'Terugbetaling mislukt',
      },
      actions: {
        copyLink: 'Link kopiëren',
        refund: 'Terugbetalen',
      },
      modal: {
        title: 'Aanbetalingen',
        description:
          'Bekijk pre-autorisaties, genereer een link en beslis of je de aanbetaling int of vrijgeeft.',
        bookingLabel: 'Reservering {{code}}',
        pendingWarning:
          'Een of meer betalingen zijn voltooid, maar er zijn nog openstaande operaties of links.',
        requested: 'Aangevraagd',
        captured: 'Geïnd',
        authorized: 'Geautoriseerd',
        netCaptured: 'Netto geïnd',
        refunded: 'Terugbetaald',
        descriptionLabel: 'Beschrijving',
        operationsTab: 'Operaties ({{count}})',
        newRequestTab: 'Nieuwe aanvraag',
        historyTitle: 'Operatiegeschiedenis',
        historyDescription:
          'Aanbetalingen en autorisaties gekoppeld aan deze reservering.',
        emptyTitle: 'Nog geen operaties',
        emptyDescription:
          'Open het tabblad “Nieuwe aanvraag” om de eerste link te genereren.',
        createRequest: 'Aanvraag maken',
      },
      create: {
        title: 'Maak een aanbetalingslink',
        description:
          'De klant autoriseert het bedrag; daarna kun je het innen of vrijgeven.',
        amountLabel: 'Bedrag aanbetaling (€)',
        descriptionLabel: 'Optionele beschrijving',
        descriptionPlaceholder: 'Voorbeeld: aanbetaling voor de reservering',
        authorizeOnlyHint:
          'Het bedrag wordt alleen geautoriseerd. Je kunt het later innen of vrijgeven.',
        submit: 'Aanbetalingslink genereren',
        invalidAmount: 'Voer een bedrag groter dan nul in.',
        error: 'Kon de Stripe-link niet maken.',
        success: 'Aanbetalingslink gemaakt.',
        sent: 'Aanbetalingslink gemaakt en verzonden.',
        linkCreatedMessageFailed:
          'Link gemaakt, maar het aanbetalingsbericht is niet verzonden.',
      },
      refund: {
        title: 'Betaling terugbetalen',
        maxAmount: 'Maximaal terugbetaalbaar bedrag: {{amount}}',
        amountLabel: 'Bedrag (€)',
        reasonLabel: 'Reden',
        invalidAmount:
          'Voer een geldig bedrag in binnen het terugbetaalbare maximum.',
        started: 'Terugbetaling gestart.',
        completed: 'Terugbetaling voltooid.',
        error:
          'Terugbetaling mislukt. Controleer de status op Stripe en probeer opnieuw.',
        reasons: {
          requested_by_customer: 'Aangevraagd door klant',
          duplicate: 'Dubbele betaling',
          fraudulent: 'Frauduleus',
        },
      },
    },
    create: {
      title: 'Reservering maken',
      cancel: 'Annuleren',
      submit: 'Reservering maken',
      customerDetails: 'Klantgegevens',
      bookingDetails: 'Reserveringsgegevens',
      firstName: 'Voornaam *',
      firstNamePlaceholder: 'Voer voornaam in',
      lastName: 'Achternaam *',
      lastNamePlaceholder: 'Voer achternaam in',
      email: 'E-mail',
      emailPlaceholder: 'Voer e-mail in',
      phone: 'Telefoonnummer *',
      phonePlaceholder: 'Voer telefoonnummer in',
      nickName: 'Bijnaam',
      nickNamePlaceholder: 'Voer bijnaam in',
      bookingImage: 'Reserveringsafbeelding',
      pdfDocument: 'PDF-document',
      bookingTime: 'Reserveringstijd *',
      bookingTimePlaceholder: 'Selecteer tijd',
      guests: 'Gasten *',
      guestsPlaceholder: 'Voer aantal gasten in',
      children: 'Kinderen *',
      childrenPlaceholder: 'Voer aantal kinderen in',
      customerRequests: 'Klantverzoeken',
      customerRequestsPlaceholder: 'Voer klantverzoeken in',
      restaurantNotes: 'Restaurantnotities',
      restaurantNotesPlaceholder: 'Voer restaurantnotities in',
    },
    calendar: {
      selected: 'Geselecteerd: {{date}}',
      dotsHint: 'Stippen geven beschikbare reserveringen aan',
    },
  },
  customers: {
    title: 'Mijn klanten',
    subtitle: 'Klanten van {{branch}}',
    subtitleDefault: 'Bekijk en beheer je klantenlijst',
    statsCount: '{{count}} klanten',
    searchPlaceholder: 'Zoek op naam, e-mail of telefoon',
    customer: 'Klant',
    surname: 'Achternaam',
    name: 'Voornaam',
    email: 'E-mail',
    phone: 'Telefoon',
    contacts: 'Contacten',
    dateOfBirth: 'Geboortedatum',
    reservations: 'Reserveringen',
    noShow: 'No Show',
    tags: 'Tags',
    visits: 'Bezoeken',
    presenceCount: '{{count}} bezoeken',
    noShowCount: '{{count}} No show',
    registrationDate: 'Geregistreerd',
    itemsPerPage: 'Items per pagina',
    range: '{{start}} - {{end}} van {{total}}',
    noBranch: 'Selecteer een vestiging om klanten te bekijken',
    empty: 'Geen klanten gevonden',
    emptyDesc: 'Er zijn nog geen klanten voor deze vestiging.',
    downloadPdf: 'Pdf downloaden',
    downloadPdfSuccess: 'PDF met boekingsgeschiedenis gedownload.',
    downloadPdfError: 'Downloaden van PDF met boekingsgeschiedenis mislukt.',
    downloadPdfEmpty: 'Geen boekingen gevonden voor deze klant.',
    downloadPdfMissingPhone: 'Deze klant heeft geen telefoonnummer.',
    importExport: {
      menuLabel: 'Importeren/exporteren',
      import: 'Klantenlijst importeren',
      export: 'Klantenlijst exporteren',
      downloadSample: 'Voorbeeldlijst downloaden',
      downloadSampleSuccess: 'Voorbeeldbestand gedownload.',
      downloadSampleError: 'Downloaden van voorbeeldbestand mislukt.',
      exportSuccess: 'Klantenlijst geëxporteerd.',
      exportError: 'Exporteren van klantenlijst mislukt.',
      dialogTitle: 'Klantenlijst importeren',
      dialogDescription:
        'Selecteer een CSV- of XLSX-bestand om klanten te importeren.',
      chooseFile: 'Bestand kiezen',
      selectedFile: 'Geselecteerd bestand',
      upload: 'Uploaden',
      uploading: 'Klantenlijst uploaden...',
      cancel: 'Annuleren',
      close: 'Sluiten',
      invalidType: 'Selecteer een CSV- of XLSX-bestand.',
      importError: 'Importeren van klantenlijst mislukt. Probeer het opnieuw.',
      importAnother: 'Nog een bestand importeren',
      totalRecords: 'Totaal aantal records',
      successfulImports: 'Geslaagde importen',
      failedImports: 'Mislukte importen',
    },
    create: {
      title: 'Klant aanmaken',
      action: 'Klant',
      tooltip: 'Klant toevoegen',
      submit: 'Klant aanmaken',
      cancel: 'Annuleren',
      firstName: 'Voornaam',
      firstNamePlaceholder: 'Voer voornaam in',
      lastName: 'Achternaam',
      lastNamePlaceholder: 'Voer achternaam in',
      mobile: 'Mobiel nummer',
      mobilePlaceholder: 'Voer mobiel nummer in',
      emailPlaceholder: 'Voer e-mailadres in',
      nickname: 'Klantnickname',
      nicknamePlaceholder: 'Voer nickname in',
      tagsTitle: 'Klanttags',
      tagsSubtitle: 'Selecteer de tags die aan de klant zijn gekoppeld.',
      tagsPlaceholder: 'Selecteer tags',
      success: 'Klant succesvol aangemaakt.',
      error: 'Klant aanmaken mislukt. Probeer het opnieuw.',
      loading: 'Klant wordt aangemaakt...',
    },
    bookingHistoryPdf: {
      title: 'Boekingsgeschiedenis',
      generatedMeta: 'Gegenereerd op {{date}} · {{count}} boekingen',
      customerCode: 'Klantcode: {{code}}',
      phone: 'Telefoon',
      email: 'E-mail',
      birthDate: 'Geboortedatum',
      address: 'Adres',
      registration: 'Registratie',
      tags: 'Tags',
      notAvailable: 'Niet beschikbaar',
      guestsWithChildren: '{{guests}} + {{children}} kinderen',
      columns: {
        date: 'Datum',
        time: 'Tijd',
        code: 'Code',
        status: 'Status',
        guests: 'Gasten',
        source: 'Bron',
      },
      sources: {
        WEB: 'Web',
        APP_CUSTOMER: 'Portaal',
        APP: 'App',
      },
    },
    filters: {
      title: 'Filters',
      tags: 'Tags',
      birthYears: 'Geboortejaren',
      birthMonth: 'Geboortemaand',
      clearAll: 'Alle filters wissen',
      noTags: 'Geen tags beschikbaar',
      onlyWithEmail: 'e-mail geconfigureerd',
      marketingConsent: 'marketingtoestemming',
      blacklist: 'blacklist klanten',
      phonePrefix: 'Landcode',
      allPhonePrefixes: 'Alle landcodes',
      searchOptions: 'Zoeken...',
      noSearchResults: 'Geen overeenkomende opties',
      minimumBookings: 'Minimum aantal boekingen',
      selectMinimumBookings: 'Selecteer minimum boekingen',
      form: 'Formulier',
      selectForm: 'Alle formulieren',
      noForms: 'Geen formulieren beschikbaar',
      bookingPeriodTitle: 'Reserveringen in de periode',
      bookingRangeLabel: 'Boekingsperiode',
      bookingRangePlaceholder: 'Selecteer boekingsperiode',
      removePeriod: 'Periode verwijderen',
      minimumBookingsOptions: {
        '1': 'Minstens 1 boeking',
        '2': 'Minstens 2 boekingen',
        '3': 'Minstens 3 boekingen',
        '4': 'Minstens 4 boekingen',
        '5': 'Minstens 5 boekingen',
        '10': 'Minstens 10 boekingen',
        '15': 'Minstens 15 boekingen',
        '20': 'Minstens 20 boekingen',
        '30': 'Minstens 30 boekingen',
        '50': 'Minstens 50 boekingen',
      },
      months: {
        '1': 'Januari',
        '2': 'Februari',
        '3': 'Maart',
        '4': 'April',
        '5': 'Mei',
        '6': 'Juni',
        '7': 'Juli',
        '8': 'Augustus',
        '9': 'September',
        '10': 'Oktober',
        '11': 'November',
        '12': 'December',
      },
    },
  },
  customerDetails: {
    title: 'Klantgegevens',
    subtitle: 'Klantgegevens voor {{branch}}',
    subtitleDefault: 'Bekijk en beheer klantgegevens',
    deleteModelTitle: 'Klant verwijderen?',
    deleteModelDescription:
      'Door de klant te verwijderen worden ook de gekoppelde boekingen verwijderd.',
    deleteModelConfirmText: 'Ja, verwijderen',
    deleteModelCancelText: 'Annuleren',
    card: {
      title: 'Klantkaart',
      close: 'Sluiten',
      delete: 'Verwijderen',
      update: 'Klant bijwerken',
      name: 'Voornaam',
      surname: 'Achternaam',
      nickname: 'Bijnaam',
      mobile: 'Mobiel',
      address: 'Adres',
      city: 'Stad',
      province: 'Provincie',
      postalCode: 'Postcode',
      country: 'Land',
      personalDataTitle: 'Persoonlijke gegevens',
      personalDataSubtitle: 'Voornaam, achternaam en hoofdcontacten.',
      phoneTitle: 'Telefoon',
      phoneSubtitle: 'Landcode en mobiel nummer.',
      addressTitle: 'Adres',
      addressSubtitle: 'Geografische gegevens en klantcontact.',
      birthDateTitle: 'Geboortedatum',
      birthDateSubtitle: 'Opslagformaat: yyyy-MM-dd.',
      tagsTitle: 'Klanttag',
      tagsSubtitle: 'Selecteer de tags gekoppeld aan de klant.',
      tagsPlaceholder: 'Selecteer tags',
      updateSuccess: 'Klant succesvol bijgewerkt.',
      updateError: 'Klant bijwerken mislukt. Probeer het opnieuw.',
      deleteSuccess: 'Klant succesvol verwijderd.',
      deleteError: 'Klant verwijderen mislukt. Probeer het opnieuw.',
    },
  },
  forms: {
    title: 'Formulieren van reserveringen',
    subtitle: 'Beheer alle actieve, opgeschorte of gearchiveerde formulieren',
    addDataCollectionForm: 'Formulier toevoegen',
    searchPlaceholder: 'Zoek formulieren op naam',
    noDataTitle: 'Geen formulier',
    noDataDesc:
      'Je hebt nog geen formulieren aangemaakt. Begin met het maken van je eerste formulier om boekingen en interacties te verzamelen.',
    status: 'Status',
    active: 'Actief',
    suspended: 'Opgeschort',
    url: 'URL',
    qrCode: 'QR-code',
    preview: 'Voorbeeld',
    googleBooking: 'Google Booking',
    googleReservation: 'Google Reservation',
    copyUrl: 'URL kopiëren',
    downloadQr: 'QR downloaden',
    editForm: 'Formulier bewerken',
    dublicateUrl: 'Formulier dupliceren',
    statusActive: 'Actief',
    statusInactive: 'Inactief',
    statusDraft: 'Concept',
    statsCount: '{{count}} formulieren',
    updatedAgo: '2 minuten geleden bijgewerkt',
    deleteForm: 'Formulier verwijderen',
    share: {
      label: 'Formulier delen',
      whatsapp: 'WhatsApp',
      instagram: 'Instagram',
      twitter: 'Twitter / X',
      facebook: 'Facebook',
      defaultText: 'Boek bij ons',
      instagramCopied: 'Link gekopieerd. Plak deze in Instagram om te delen.',
      instagramOpened:
        'Instagram geopend. Formulierlink gekopieerd — plak om te delen.',
      copyFailed: 'Link kon niet worden gekopieerd. Probeer opnieuw.',
    },
    createTitle: 'Formulier aanmaken',
    formName: 'Formuliernaam',
    formNamePlaceholder: 'Voer de formuliernaam in',
    createForm: 'Formulier aanmaken',
    toast: {
      createSuccess: 'Formulier succesvol aangemaakt.',
      createError: 'Kan het formulier niet aanmaken. Probeer het opnieuw.',
      deleteSuccess: 'Formulier succesvol verwijderd.',
      deleteError: 'Kan het formulier niet verwijderen. Probeer het opnieuw.',
      duplicateSuccess: 'Formulier succesvol gedupliceerd.',
      duplicateError: 'Kan het formulier niet dupliceren. Probeer het opnieuw.',
      statusActivateSuccess: 'Formulier succesvol geactiveerd.',
      statusSuspendSuccess: 'Formulier succesvol opgeschort.',
      statusActivateError:
        'Kan het formulier niet activeren. Probeer het opnieuw.',
      statusSuspendError:
        'Kan het formulier niet opschorten. Probeer het opnieuw.',
      googleReservationEnabled: '{{name}} Google Reservation ingeschakeld',
      googleReservationDisabled: '{{name}} Google Reservation uitgeschakeld',
      googleReservationError:
        'Google Reservation kon niet worden bijgewerkt. Probeer het opnieuw.',
      logoUploadSuccess: 'Logo succesvol geüpload.',
      logoUploadError: 'Kan het logo niet uploaden.',
      logoRemoveSuccess: 'Logo succesvol verwijderd.',
      logoRemoveError: 'Kan het logo niet verwijderen.',
      bannerUploadSuccess: 'Banner succesvol geüpload.',
      bannerUploadError: 'Kan de banner niet uploaden.',
      bannerRemoveSuccess: 'Banner succesvol verwijderd.',
      bannerRemoveError: 'Kan de banner niet verwijderen.',
    },
    deleteModel: {
      deleteTitle: 'Formulier verwijderen?',
      deleteDescription:
        'Weet u zeker dat u dit formulier wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
    },
    validation: {
      formNameMin: 'De naam moet minimaal 2 tekens bevatten',
      formNameMax: 'De naam mag niet langer zijn dan 50 tekens',
    },
    scanQrAccess: 'Scan de QR-code om toegang te krijgen:',
    qrClose: 'Sluiten',
    qrDownload: 'Downloaden',
    edit: {
      pageTitle: 'Toestemming persoonsgegevens',
      editTitle: '{{name}} bewerken',
      subtitle: 'Configureer het reserveringsformulier',
      saveChanges: 'Wijzigingen opslaan',
      close: 'Sluiten',
      toast: {
        saveSuccess: 'Formulier succesvol opgeslagen.',
        saveError: 'Kan het formulier niet opslaan.',
      },
      tabListLabel: 'Secties voor formulierbewerking',
      uploading: 'Uploaden...',
      removing: 'Verwijderen...',
      tabs: {
        general: 'Algemeen',
        capacity: 'Capaciteit',
        schedule: 'Rooster',
        formFields: 'Formuliervelden',
        preferences: 'Voorkeuren',
        services: 'Diensten',
      },
      general: {
        media: 'Media',
        logo: 'Logo',
        banner: 'Bannerafbeelding',
        bannerHint: 'Bannerafbeelding — sleep en zet neer of blader',
        basicDetails: 'Basisgegevens',
        formName: 'Formuliernaam',
        customerName: 'Emoji-formulier',
        address: 'Adres van de locatie om op het formulier te tonen',
        customThankYouPage: 'Aangepaste bedankpagina',
        buttonTextOnBookingForm: 'Knoptekst op het reserveringsformulier',
        policy: 'Beleid',
        webLink: 'Weblink',
      },
      capacity: {
        hint: 'Reserveringslimieten en tafelregels voor dit formulier.',
        sections: {
          coverLimits: 'Couvertlimieten',
          timeSettings: 'Tijdslotinstellingen',
          policies: 'Beleid',
        },
        maxGuests: 'Maximaal aantal couverts vóór het gesprek',
        maxGuestsHint:
          'Wat is het maximale aantal couverts dat uw zaak accepteert voordat een telefoontje nodig is?',
        maxTableGuests: 'Max. gasten per tafel',
        maxTableGuestsHint:
          'Stel het maximale aantal gasten in dat je aan één tafel kunt ontvangen. Als je de waarde op 0 laat, is het maximum gelijk aan de totale zitcapaciteit van het restaurant.',
        minGuests: 'Min. couverts per tafel',
        minGuestsHint: 'Kleinste groepsgrootte voor één tafel.',
        phoneOverbooking: 'Mobiel Overbooking',
        phoneOverbookingHint:
          'Telefoon getoond wanneer gasten het maximum per tafel overschrijden. Formaat: PREFIX + NUMMER (bijv. +39 3444393343).',
        cut: 'Interval',
        cutHint:
          'Verdeelt de beschikbaarheid in reserveringsslots van deze lengte.',
        cutSlotOption: 'Verdeel tijdsloten in slots van {{minutes}} minuten',
        stayOnTable: 'Verblijf aan tafel',
        stayOnTableHint: 'Verwachte eetduur per reservering.',
        minutes: '{{count}} min',
        dogsAccepted: 'Honden toegestaan',
        dogsAcceptedHint:
          'Sta klanten toe aan te geven dat ze een hond meebrengen.',
        menuSelectionMandatory: 'Menuselectie verplicht',
        menuSelectionMandatoryHint:
          'Indien ingeschakeld moet de gast per persoon een menu (of “Geen menu”) kiezen zodat het totaal gelijk is aan gasten + kinderen om verder te gaan.',
        showRestaurantNoMenu: 'Toon restaurant zonder-menu kaart',
        showRestaurantNoMenuHint:
          'Indien ingeschakeld toont het openbare formulier een kaart die uitlegt dat het restaurant geen vast menu heeft.',
      },
      schedule: {
        shiftOverview: 'Overzicht diensten',
        weeklyHours: 'Weekuren',
        addTimeSlot: 'Tijdslot toevoegen',
        day: 'Dag',
        open: 'Open',
        closed: 'Gesloten',
        hours: 'Uren',
        editSlot: 'Slot bewerken',
        deleteSlot: 'Slot verwijderen',
        deleteSlotConfirmTitle: 'Dit tijdslot verwijderen?',
        deleteSlotConfirmDesc:
          'Het geselecteerde tijdslot wordt van deze dag verwijderd.',
        deleteSlotConfirmMultiDay:
          'Deze service gebruikt dezelfde code op {{count}} dagen. Verwijderen haalt hem van al die dagen weg.',
        extraordinarySection: 'Buitengewone openingen / sluitingen',
        addException: 'Uitzondering toevoegen',
        dateRange: 'Datumbereik',
        type: 'Type',
        closedOpenDay: 'Gesloten/open dag',
        timeSlots: 'Tijdsloten',
        cuttingMin: 'Interval (min)',
        maxGuests: 'Max. gasten',
        noShifts: 'Nog geen diensten geconfigureerd.',
        emptyWeeklyHours:
          'Geen weekuren geconfigureerd. Gebruik + om een rooster voor alle dagen te maken.',
        emptyExtraordinary:
          'Geen buitengewone openingen of sluitingen geconfigureerd.',
        createForAllDays: 'Algemene configuratie voor elke dag maken',
        resetConfiguration: 'Configuratie resetten',
        saveConfiguration: 'Configuratie opslaan',
        resetConfirmTitle: 'Alle bestaande configuraties verwijderen?',
        resetConfirmDesc:
          'Hiermee worden alle roosterinstellingen voor dit formulier verwijderd.',
        resetProceed: 'Doorgaan',
        tags: 'Tags',
        tagsPlaceholder: 'Voer tags in gescheiden door komma’s',
        tagsPickerDesc: 'Selecteer tags voor dit tijdslot.',
        tagsPickerEmpty: 'Geen tags beschikbaar voor deze vestiging.',
        allowOverbook: 'Overboeken toestaan',
        alertMessage: 'Waarschuwingsbericht',
        alertMessagePlaceholder:
          'Voer een waarschuwingsbericht in voor dit tijdsbereik',
        enableOverbookBlocking: 'Overboekingsblokkering inschakelen',
        disableOverbookBlocking: 'Overboekingsblokkering uitschakelen',
        deposit: {
          tooltipInactive: 'Borg / kaartcontrole configureren',
          tooltipValidation: 'Stripe: alleen kaartverificatie',
          tooltipDeposit: 'Stripe-borg: {{amount}} per gast',
          tooltipMixed: 'Gemengde Stripe-configuratie over tijdsvakken',
          allStandardBands: 'Alle standaardtijdsvakken',
          allExtraordinaryBands: 'Alle buitengewone tijdsvakken',
          singleBand: 'Geselecteerd tijdsvak',
          depositTitle: 'Borg',
          checkCardTitle: 'Kaart controleren',
          bulkHint:
            'De configuratie wordt in één bewerking toegepast op alle tijdsvakken in deze sectie.',
          requestTitle: 'Creditcard aanvragen',
          requestDesc:
            'U kunt alleen kaartverificatie vragen of ook een vooraf geautoriseerde borg.',
          validationTitle: 'Kaartvalidatie',
          validationDesc:
            'Verifieer en koppel de kaart zonder een bedrag te blokkeren.',
          depositOptionTitle: 'Borg',
          depositOptionDesc:
            'Valideer de kaart en autoriseer vooraf het geconfigureerde bedrag.',
          amountLabel: 'Bedrag EUR',
          amountHint:
            'Voer een bedrag groter dan nul in om de borg te activeren.',
          saveConfiguration: 'Configuratie opslaan',
          close: 'Sluiten',
          toastSaveSuccess: 'Borgconfiguratie opgeslagen.',
          toastSaveError: 'Borgconfiguratie opslaan mislukt.',
        },
        legacyCreditCard: {
          enable: 'Direct verzoek om creditcardgegevens inschakelen',
          disable: 'Direct verzoek om creditcardgegevens uitschakelen',
        },
        maxGuestsPerSlot: 'Max. dekking per tijdslot',
        customizeCutter:
          'Pas het tijdslotinterval aan op de standaardconfiguratie',
        noSlotsForDay:
          'Geen tijdsloten voor deze dag. Gebruik + om er een toe te voegen.',
        slotClosedOnDate: 'Tijdslot niet beschikbaar op {{date}}',
        alertModalTitle:
          'Waarschuwing voor klant bij selectie van een tijdslot',
        maxGuestsModalTitle:
          'Stel het maximum aantal plaatsen in voor dit tijdslot {{range}}',
        maxGuestsModalDesc:
          'Kies het maximum aantal toegestane gasten voor dit tijdslot.',
        cutterModalTitle: 'Tijdslotinterval bijwerken',
        days: {
          monday: 'Maandag',
          tuesday: 'Dinsdag',
          wednesday: 'Woensdag',
          thursday: 'Donderdag',
          friday: 'Vrijdag',
          saturday: 'Zaterdag',
          sunday: 'Zondag',
        },
        addTimeAllDays: {
          title: 'Tijd aan alle dagen toevoegen',
          subtitle:
            'De beschikbare tijden respecteren de tijdsloten van de locatie en de reeds geconfigureerde slots.',
          chosenTime: 'Gekozen tijd',
          duration: 'Duur: {{duration}}',
          startAt: '1. Begint om',
          endsAt: '2. Eindigt om',
          allOkDesc: 'U kunt dit rooster voor alle dagen opslaan.',
          saveOnAllDays: 'Opslaan voor alle dagen',
        },
        slotTime: {
          title: 'Slottijd',
          subtitle:
            'Kies eerst wanneer het begint. Kies dan wanneer het eindigt.',
          allOk: 'Alles in orde',
          allOkDesc: 'U kunt deze tijd opslaan.',
          saveTime: 'Tijd opslaan',
          timeRangeInvalid: 'De eindtijd moet na de begintijd liggen.',
          timeRangeConflict:
            'Dit tijdsbereik conflicteert met een ander. Bewerk of verwijder een van de bereiken.',
          occupiedTitle: 'Tijden al bezet',
          occupiedHint: 'De tweede lijst vermijdt deze tijden automatisch.',
          cannotSave: 'Wijzig de tijd',
          cannotSaveDesc: 'Er is geen geldig einde voor dit begin.',
          cannotSaveButton: 'Kan niet opslaan',
          durationMinutes: '{{count}} minuten',
          durationHours: '{{count}} uur',
          durationHoursMinutes: '{{hours}} uur en {{minutes}} minuten',
          selectService: 'Selecteer een service',
          selectServiceDesc:
            'Kies een maaltijdperiode om de openingstijden toe te voegen.',
          noLocalSlots:
            'Tijdsloten zijn verwijderd in de lokale configuratie. Maak ze daar opnieuw aan en voeg ze daarna toe aan dit schema.',
          selectedCount: '{{count}} services geselecteerd',
          alreadyAdded: 'Al toegevoegd',
        },
        extraordinary: {
          badge: 'Buitengewone configuratie',
          createRange: 'Bereik maken',
          editRange: 'Bereik bewerken',
          description: 'Beschrijving',
          descriptionPlaceholder: 'Bijv. Ferragosto, privé-evenement...',
          rangeDate: 'Bereikdatum',
          rangeDatePlaceholder: 'Selecteer datumbereik',
          clearDates: 'Wissen',
          applyDates: 'Toepassen',
          open: 'Open',
          create: 'Maken',
          update: 'Bijwerken',
          validation: 'Beschrijving en datumbereik zijn verplicht.',
          dateRequired: 'Selecteer een datumbereik.',
          dateOrder: 'De einddatum kan niet vóór de begindatum liggen.',
          timeRangeInvalid: 'De eindtijd moet na de begintijd liggen.',
          addConfiguration: 'Buitengewone configuratie toevoegen',
          saveConfiguration: 'Configuratie opslaan',
          addTimeSlot: 'Tijdslot toevoegen',
          deleteRange: 'Bereik verwijderen',
          deleteConfirmTitle: 'Buitengewoon bereik verwijderen?',
          deleteConfirmDesc:
            'Hiermee wordt de configuratie van dit datumbereik permanent verwijderd.',
          toast: {
            createSuccess: 'Buitengewoon bereik aangemaakt.',
            updateSuccess: 'Buitengewoon bereik bijgewerkt.',
            saveError: 'Kan het buitengewone bereik niet opslaan.',
            deleteSuccess: 'Buitengewoon bereik verwijderd.',
            deleteError: 'Kan het buitengewone bereik niet verwijderen.',
          },
        },
        toast: {
          saveSuccess: 'Roosterconfiguratie opgeslagen.',
          saveError: 'Kan het rooster niet opslaan.',
          resetSuccess: 'Roosterconfiguratie gereset.',
          resetError: 'Kan het rooster niet resetten.',
          addSlotSuccess: 'Tijdslot toegevoegd.',
          addSlotError: 'Kan het tijdslot niet toevoegen.',
          deleteSlotSuccess: 'Tijdslot verwijderd.',
          deleteSlotError: 'Kan het tijdslot niet verwijderen.',
          updateTimeSuccess: 'Tijdslot bijgewerkt.',
          updateTimeError: 'Kan het tijdslot niet bijwerken.',
          dayToggleSuccess: 'Dagstatus bijgewerkt.',
          dayToggleError: 'Kan de dagstatus niet bijwerken.',
          configurationMissing: 'De dagconfiguratie is nog niet beschikbaar.',
          slotNotReady:
            'Het tijdslot is niet gereed. Vernieuw en probeer opnieuw.',
          overbookToggleSuccess: 'Overboekingsinstelling bijgewerkt.',
          overbookToggleError: 'Kan de overboekingsinstelling niet bijwerken.',
          legacyCreditCardToggleSuccess: 'Creditcardverzoek bijgewerkt.',
          legacyCreditCardToggleError: 'Creditcardverzoek bijwerken mislukt.',
          tagsSaveSuccess: 'Tags opgeslagen.',
          tagsLoadError: 'Kan de tags niet laden.',
          tagsSaveError: 'Kan de tags niet opslaan.',
          tagsDeleteSuccess: 'Tags verwijderd.',
          alertSaveSuccess: 'Waarschuwingsbericht opgeslagen.',
          alertSaveError: 'Kan het waarschuwingsbericht niet opslaan.',
          alertDeleteSuccess: 'Waarschuwingsbericht verwijderd.',
          maxGuestsInvalid: 'Voer een geldig aantal gasten in.',
          maxGuestsSaveSuccess: 'Maximum aantal gasten bijgewerkt.',
          maxGuestsSaveError: 'Kan het maximum aantal gasten niet bijwerken.',
          cutterSaveSuccess: 'Tijdslotinterval bijgewerkt.',
          cutterSaveError: 'Kan het tijdslotinterval niet bijwerken.',
        },
      },
      formFields: {
        hint: 'Kies welke velden op het openbare reserveringsformulier verschijnen.',
        configure: 'Formuliervelden configureren',
        configureDesc:
          'Schakel velden in die tijdens het boeken aan klanten worden getoond.',
        name: 'Voornaam',
        surname: 'Achternaam',
        phone: 'Telefoon',
        email: 'E-mail',
        dateOfBirth: 'Geboortedatum',
        zipCode: 'Postcode (CAP)',
        customerNotes: 'Klantnotities',
        field: 'Veld',
        mandatory: 'Verplicht',
        nonMandatory: 'Niet verplicht',
      },
      preferences: {
        sectionTitle: 'Aanvullende opties',
        sectionDescription:
          'Aanvullende configuraties; laat uw klanten gemakkelijker voorkeuren, allergieën of intoleranties communiceren.',
        singleChoicePredefined:
          'Vul de lijst in zoals voorgesteld om opties voor enkelvoudige voorkeurskeuze te maken.',
        singleChoiceAdditional:
          'Maakt een extra voorkeursoptie met enkelvoudige keuze',
        multiChoicePredefined:
          'Vul de lijst in zoals voorgesteld om opties voor meervoudige voorkeurskeuze te maken.',
        multiChoiceAdditional:
          'Maak een extra voorkeursoptie met meervoudige keuze',
        singleChoiceOptionsPlaceholder:
          'Voorbeelden: Buitenruimte | Binnenruimte | Bar',
        multiChoiceOptionsPlaceholder: 'Bijv: Lactose | Coeliakie | Pinda’s',
        singleChoiceFieldPlaceholder:
          'Bijv: Naam van veld met enkelvoudige keuze',
        multiChoiceFieldPlaceholder: 'Bijv: Veldnaam bij meervoudige keuze',
        pipeSeparatedNote: 'Let op: scheid waarden met het teken |.',
        importRooms: 'Mijn ruimtes importeren',
        importRoomsSuccess: 'Ruimtes succesvol geïmporteerd.',
        importRoomsError: 'Kan de ruimtes niet importeren.',
        importRoomsEmpty: 'Geen ruimtes gevonden voor deze vestiging.',
        importRoomsNotFound:
          'Geen ruimtes gevonden voor deze vestiging. Controleer de vestigingscode of maak eerst ruimtes aan.',
        suggestAllergens: 'Stel me een volledige allergenenlijst voor',
        validation: {
          fillRequired:
            'Als Mandatory of Non-mandatory is aangevinkt, vul zowel het label als de opties in.',
          labelRequired:
            'Label is verplicht wanneer deze optie is ingeschakeld.',
          optionsRequired:
            'Voeg minimaal één optie toe, gescheiden door |, wanneer deze optie is ingeschakeld.',
        },
      },
      services: {
        sectionTitle: 'Sectie aanvullende producten en diensten',
        sectionDescription:
          'Maak een lijst van producten en diensten om aan het reserveringsformulier te koppelen.',
        mandatoryLabel:
          'Ik wil dat de keuze van producten of diensten verplicht is',
        title: 'Aanvullende producten en diensten',
        description: 'Bied extra’s aan tijdens het boekingsproces.',
        add: 'Product of dienst toevoegen',
        name: 'Naam',
        descriptionCol: 'Beschrijving',
        price: 'Prijs',
        pax: 'Pax',
        empty: 'Nog geen producten of diensten gekoppeld aan dit formulier.',
        pickerTitle: 'Lijst met diensten/producten',
        pickerDesc:
          'Selecteer een of meer items om aan dit formulier te koppelen.',
        pickerEmpty:
          'Geen diensten of producten beschikbaar voor deze vestiging.',
        deleteTitle: 'Dienst/product verwijderen?',
        deleteDesc:
          'Hiermee wordt het geselecteerde item uit dit reserveringsformulier verwijderd.',
        toast: {
          saveSuccess: 'Diensten en producten bijgewerkt.',
          saveError: 'Kan de diensten en producten niet bijwerken.',
          deleteSuccess: 'Dienst/product verwijderd.',
          deleteError: 'Kan de dienst/product niet verwijderen.',
          loadError: 'Kan de diensten en producten niet laden.',
        },
      },
      formConfiguration: 'Formulierconfiguratie',
      formConfigurationDesc: 'Algemene formulierconfiguraties',
      formImage: 'Formulierafbeelding',
      formImageHint:
        'We raden een PNG-logo met transparante achtergrond aan. Dubbelklik op de afbeelding om direct door te sturen.',
      formBackgroundImage: 'Achtergrondafbeelding formulier',
      imageUpload: 'Afbeelding uploaden',
      removeImage: 'Afbeelding verwijderen',
      redirectAfterCompilation: 'Doorverwijzing na invullen',
      linkForm: 'Formulierlink',
      buttonText: 'Knoptekst',
      dataFields: 'Gegevensvelden',
      requestEmail: 'E-mail vragen',
      requestAddress: 'Adres vragen',
      requestNationality: 'Nationaliteit vragen',
      requestZipCode: 'Postcode vragen',
      requestDateOfBirth: 'Geboortedatum vragen',
      tagsApplied: 'Tags toegepast op het formulier',
      tagsPlaceholder: 'Selecteer tags',
      tagsHint: 'Tag-namen gescheiden door komma’s',
      validation: {
        buttonTextRequired: 'Knoptekst is verplicht',
      },
    },
  },
  dcfQuestions: {
    title: 'Vragen en antwoordtags',
    description:
      'Verzamel klantgegevens en antwoorden in één formulier. Koppel tags aan beantwoorde vragen en gekozen opties.',
    backendRequired:
      'Vragen vereisen een API-update. Je kunt ze hier instellen, maar pas opslaan wanneer de service ze ondersteunt.',
    saveMismatch:
      'De API heeft de opgeslagen vragen niet teruggegeven. Je wijzigingen staan hier nog. Neem contact op met het API-team.',
    unavailable:
      'Dit formulier kan nog geen antwoorden ontvangen. Probeer het later opnieuw.',
  },
  dataCollectionForms: {
    title: 'Gegevensverzamelingsformulier',
    subtitle: 'Formulieren aanwezig',
    addDataCollectionForm: 'Formulier Toevoegen',
    searchPlaceholder: 'Zoek gegevensverzamelingsformulieren op naam',
    noDataTitle: 'Geen formulier aanwezig',
    noDataDesc:
      'Maak je eerste gegevensverzamelingsformulier om leads en interacties te starten.',
    form: 'Formulier',
    tags: 'Tags',
    tagsMore: '+{{count}} meer',
    tagsLess: 'Minder',
    status: 'Status',
    interactions: 'Interacties',
    interactionsCount: '{{count}} interacties',
    url: 'URL',
    qrCode: 'QR-code',
    preview: 'Voorbeeld',
    copyUrl: 'URL kopiëren',
    downloadQr: 'QR downloaden',
    editDataCollectionForm: 'Gegevensverzamelingsformulier bewerken',
    statusActive: 'Actief',
    statusInactive: 'Inactief',
    statusSuspended: 'Opgeschort',
    statusDraft: 'Concept',
    statsCount: '{{count}} gegevensverzamelingsformulieren',
    updatedAgo: '2 minuten geleden bijgewerkt',
    deleteDataCollectionForm: 'Gegevensverzamelingsformulier verwijderen',
    duplicate: 'Dupliceren',
    createTitle: 'Gegevensverzamelingsformulier aanmaken',
    formName: 'Formuliernaam',
    formNamePlaceholder: 'Voer de formuliernaam in',
    createForm: 'Formulier aanmaken',
    deleteModel: {
      deleteTitle: 'Gegevensverzamelingsformulier verwijderen?',
      deleteDescription:
        'Weet u zeker dat u dit formulier wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
    },
    validation: {
      formNameMin: 'De naam moet minimaal 2 tekens bevatten',
      formNameMax: 'De naam mag niet langer zijn dan 255 tekens',
    },
    scanQrAccess: 'Scan de QR-code om toegang te krijgen tot:',
    qrClose: 'Sluiten',
    qrDownload: 'Downloaden',
    toast: {
      createSuccess: 'Formulier succesvol aangemaakt.',
      createError: 'Kan het formulier niet aanmaken. Probeer het opnieuw.',
      updateSuccess: 'Formulier succesvol bijgewerkt.',
      updateError: 'Kan het formulier niet bijwerken. Probeer het opnieuw.',
      logoUploadSuccess: 'Logo succesvol geüpload.',
      logoUploadError: 'Kan het logo niet uploaden.',
      logoRemoveSuccess: 'Logo succesvol verwijderd.',
      logoRemoveError: 'Kan het logo niet verwijderen.',
      bgUploadSuccess: 'Achtergrond succesvol geüpload.',
      bgUploadError: 'Kan de achtergrond niet uploaden.',
      bgRemoveSuccess: 'Achtergrond succesvol verwijderd.',
      bgRemoveError: 'Kan de achtergrond niet verwijderen.',
      statusUpdateSuccess: 'Status succesvol bijgewerkt.',
      statusUpdateError: 'Kan de status niet bijwerken. Probeer het opnieuw.',
      deleteSuccess: 'Formulier succesvol verwijderd.',
      deleteError: 'Kan het formulier niet verwijderen. Probeer het opnieuw.',
      duplicateSuccess: 'Formulier succesvol gedupliceerd.',
      duplicateError: 'Kan het formulier niet dupliceren. Probeer het opnieuw.',
    },
    edit: {
      pageTitle: 'Toestemming persoonsgegevens',
      subtitle: 'Configureer het gegevensverzamelingsformulier',
      close: 'Sluiten',
      formConfiguration: 'Formulierconfiguratie',
      formConfigurationDesc:
        'Algemene configuratie van het gegevensverzamelingsformulier',
      formImage: 'Formulierafbeelding',
      formImageHint:
        'We raden een PNG-logo met transparante achtergrond aan. Dubbelklik op de afbeelding om door te sturen.',
      formBackgroundImage: 'Achtergrondafbeelding formulier',
      imageUpload: 'Afbeelding uploaden',
      removeImage: 'Afbeelding verwijderen',
      redirectAfterCompilation: 'Doorverwijzing na invullen',
      linkForm: 'Formulierlink',
      buttonText: 'Knoptekst',
      description: 'Beschrijving',
      descriptionPlaceholder: 'Tekst links op het openbare formulier',
      dataFields: 'Gegevensvelden',
      requestEmail: 'E-mail vragen',
      requestAddress: 'Adres vragen',
      requestNationality: 'Nationaliteit vragen',
      requestZipCode: 'Postcode vragen',
      requestDateOfBirth: 'Geboortedatum vragen',
      tagsApplied: 'Tags toegepast op het formulier',
      tagsOnDownload: 'Tags toegepast bij download',
      tagsOnUse: 'Tags toegewezen na het opslaan van gegevens',
      tagsPlaceholder: 'Selecteer tags',
      tagsHint: "Tag-namen gescheiden door komma's",
      uploading: 'Uploaden...',
      removing: 'Verwijderen...',
      validation: {
        redirectUrlRequired: 'Doorverwijs-URL is verplicht.',
        redirectUrlInvalid:
          'Voer een geldige URL in (bijv. https://example.com).',
      },
    },
  },
  publicDataCollectionForm: {
    documentTitle: 'Gegevensverzamelingsformulier',
    loading: 'Formulier laden',
    missingFormCode: 'Formuliercode ontbreekt in de URL.',
    loadError: 'Dit gegevensverzamelingsformulier kan niet worden geladen.',
    unavailableTitle: 'Formulier niet beschikbaar',
    unavailableMessage:
      'Dit gegevensverzamelingsformulier is momenteel opgeschort of niet beschikbaar.',
    personalInfo: 'Persoonlijke info',
    prefixLabel: 'Landcode',
    phoneLabel: 'Mobiel',
    phonePlaceholder: 'Je nummer',
    firstName: 'Voornaam',
    firstNamePlaceholder: 'Je voornaam',
    lastName: 'Achternaam',
    lastNamePlaceholder: 'Je achternaam',
    email: 'E-mail',
    emailPlaceholder: 'naam@voorbeeld.nl',
    optional: 'Optioneel',
    cap: 'Postcode',
    capPlaceholder: 'Bijv. 09124',
    birthDate: 'Geboortedatum',
    address: 'Adres',
    addressPlaceholder: 'Straat en nummer',
    nationality: 'Nationaliteit',
    nationalityPlaceholder: 'Je nationaliteit',
    requiredNote: '* Verplichte velden',
    consentsTitle: 'Je voorkeuren',
    privacyConsent: 'Privacy en gegevensverwerking',
    marketingConsent: 'Marketing- en communicatietoestemming',
    profilingConsent: 'Toestemming voor profilering',
    detailsLink: 'Informatie',
    selectAll: 'Alles selecteren',
    submit: 'Naar de pagina',
    welcomeBackTitle: 'Welkom terug, {{name}}!',
    welcomeBackTitleGeneric: 'Welkom terug!',
    welcomeBackMessage:
      'We hebben je gegevens al. Je kunt een ander contact toevoegen als dit voor iemand anders is.',
    addAnotherContact: 'Ander contact toevoegen',
    toast: {
      success: 'Gegevens succesvol verzonden.',
      error: 'Verzenden van gegevens mislukt. Probeer opnieuw.',
    },
    thankYou: {
      title: 'Bedankt!',
      message: 'Je gegevens zijn succesvol verzonden.',
      continue: 'Doorgaan',
    },
    validation: {
      prefixRequired: 'Selecteer een landcode.',
      phoneMin: 'Voer een geldig mobiel nummer in.',
      firstNameRequired: 'Voornaam is verplicht.',
      lastNameRequired: 'Achternaam is verplicht.',
      emailRequired: 'E-mail is verplicht.',
      emailInvalid: 'Voer een geldig e-mailadres in.',
      birthDateInvalid:
        'Voer een geldige datum in die niet in de toekomst ligt.',
      privacyRequired: 'Privacytoestemming is verplicht.',
    },
  },
  bulkMessage: {
    title: 'Bulkbericht',
    subtitle: 'Beheer WhatsApp-, e-mail- en sms-campagnes voor {{branch}}',
    subtitleDefault: 'Bekijk en beheer je bulkberichtcampagnes',
    statsCount: '{{count}} campagnes',
    searchPlaceholder: 'Zoek naar bulkmailing...',
    campaignName: 'Campagnenaam',
    campaignNamePlaceholder: 'Voer de campagnenaam in',
    addCampaign: 'Nieuwe campagne',
    creating: 'Aanmaken...',
    createTitle: 'Nieuwe berichtencampagne maken',
    createDesc: 'Voeg een nieuwe bulkberichtcampagne toe voor je vestiging.',
    close: 'Sluiten',
    duplicate: 'Dupliceren',
    editCampaign: 'Campagne bewerken',
    viewDetails: 'Details bekijken',
    pause: 'Campagne pauzeren',
    resume: 'Verzending hervatten',
    delete: 'Campagne verwijderen',
    noBranch: 'Selecteer een vestiging om bulkberichten te bekijken',
    empty: 'Geen campagnes gevonden',
    emptyDesc: 'Er zijn nog geen bulkberichtcampagnes voor deze vestiging.',
    channels: {
      whatsappWithImage: 'WHATSAPP MET AFBEELDING',
    },
    columns: {
      name: 'Naam',
      type: 'Type',
      state: 'Status',
      progress: 'Voortgang',
      selectedCustomers: 'Geselecteerde klanten',
      status: 'Status',
      channel: 'Kanaal',
      messageType: 'Berichttype',
      customer: 'Klant',
      reference: 'Referentie',
      attempts: 'Pogingen',
      createdAt: 'Aangemaakt op',
    },
    statusFilter: {
      all: 'Alle statussen',
      pending: 'In behandeling',
      sent: 'Verzonden',
      delivered: 'Afgeleverd',
      failed: 'Mislukt',
    },
    noDataTitle: 'Geen berichten gevonden',
    noDataDesc:
      'Er zijn nog geen berichtafleveringsgebeurtenissen voor deze vestiging.',
    deleteModal: {
      title: 'Campagne verwijderen?',
      description:
        'Weet je zeker dat je deze campagne wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
    },
    details: {
      pageTitle: 'Campagne {{name}}',
      subtitle: 'Voortgang van de bulkberichtcampagne',
      empty: 'Geen resultaten gevonden',
      emptyDesc:
        'Er zijn nog geen afleveringsgebeurtenissen voor deze campagne.',
      loadError: 'Afleveringsgebeurtenissen laden mislukt. Probeer opnieuw.',
      emailLabel: 'E-mail:',
      phoneLabel: 'Tel:',
      chatId: 'Chat {{id}}',
      httpStatus: 'HTTP {{status}}',
      resubmit: 'Bericht opnieuw verzenden',
      resubmitSuccess: 'Gebeurtenis opnieuw in de wachtrij geplaatst.',
      resubmitError:
        'Bericht kon niet opnieuw in de wachtrij worden geplaatst.',
      resubmitConfirmTitle: 'Vorige poging is geslaagd',
      resubmitConfirmDescription:
        'Weet je zeker dat je dit bericht opnieuw wilt verzenden?',
      resubmitConfirmAction: 'Ja, opnieuw verzenden',
      columns: {
        recipient: 'Ontvanger',
        message: 'Bericht',
        channel: 'Kanaal',
        status: 'Resultaat',
        details: 'Details',
      },
    },
    validation: {
      nameMin: 'Campagnenaam moet minstens 2 tekens bevatten',
      nameMax: 'Campagnenaam mag maximaal 120 tekens bevatten',
    },
    toast: {
      createSuccess: 'Campagne succesvol aangemaakt.',
      createError: 'Campagne aanmaken mislukt. Probeer opnieuw.',
      updateSuccess: 'Campagne succesvol opgeslagen.',
      updateError: 'Campagne opslaan mislukt. Probeer opnieuw.',
      deleteSuccess: 'Campagne succesvol verwijderd.',
      deleteError: 'Campagne verwijderen mislukt. Probeer opnieuw.',
      duplicateSuccess: 'Campagne succesvol gedupliceerd.',
      duplicateError: 'Campagne dupliceren mislukt. Probeer opnieuw.',
      imageRemoveSuccess: 'Bijgevoegde afbeelding succesvol verwijderd.',
      imageRemoveError: 'Bijgevoegde afbeelding verwijderen mislukt.',
      imageUploadSuccess: 'Afbeelding succesvol geüpload.',
      imageUploadError: 'Afbeelding uploaden mislukt. Probeer opnieuw.',
      imageTypeError: 'Alleen JPG- en PNG-afbeeldingen zijn toegestaan.',
      imageSizeError: 'Afbeelding mag maximaal 5 MB zijn.',
      startSuccess:
        'Het verzenden van bulkberichten is gestart. Volg de voortgang in de lijst.',
      startError: 'Campagne starten mislukt. Probeer opnieuw.',
      pauseSuccess: 'Campagne succesvol gepauzeerd.',
      pauseError:
        'Campagne pauzeren mislukt. Probeer het over een paar minuten opnieuw.',
      resumeSuccess: 'Campagne succesvol hervat.',
      resumeError:
        'Campagne hervatten mislukt. Probeer het over een paar minuten opnieuw.',
    },
    edit: {
      pageTitle: 'Bewerk {{name}}',
      subtitle: 'Configureer campagnedetails en berichtinhoud',
      startCampaign: 'Campagne starten',
      start: {
        confirmTitle: 'Doorgaan?',
        confirmDescription:
          'Je staat op het punt een bulkbericht van type {{channel}} naar {{count}} klanten te sturen.',
        confirmAction: 'Ja, doorgaan',
        noRecipients:
          'Er zijn geen ontvangers. Voeg ontvangers toe om de campagne te starten.',
        emptyMessage: 'Voer een bericht in om de campagne te starten.',
        emptySubject: 'Voer een onderwerp in om de e-mailcampagne te starten.',
        missingImage:
          'Voeg een afbeelding toe om de WhatsApp-met-afbeelding-campagne te starten.',
        missingTemplate:
          'Selecteer een sjabloon om de WhatsApp Business-campagne te starten.',
        corruptTemplateVariables:
          'De variabelen van deze sjabloon zijn leeg of niet beschikbaar. De sjabloon lijkt beschadigd. Kies een andere sjabloon.',
        corruptTemplateLanguages:
          'Deze sjabloon heeft geen gekoppelde talen en lijkt beschadigd. Kies een andere sjabloon.',
      },
      saveConfiguration: 'Configuratie opslaan',
      configTitle: 'Bulkberichtconfiguratie',
      configDesc:
        'Stel WhatsApp- en e-mailcampagnes in om naar geselecteerde klanten te sturen.',
      configuration: 'Configuratie',
      configurationDesc: 'Stel de campagnenaam en het bulkberichtkanaal in.',
      audience: 'Doelgroep',
      selectedCustomers: 'Geselecteerde klanten',
      customersReady: '{{count}} klanten klaar voor bulkverzending',
      selectCustomers: 'Klanten selecteren',
      selectCustomersSoon: 'Klantselectie is binnenkort beschikbaar.',
      channel: 'Kanaal',
      whatsappTitle: 'WhatsApp-bericht',
      whatsappDesc: 'Configureer de inhoud van het WhatsApp-bericht.',
      whatsappPlaceholder: 'Schrijf hier je bericht...',
      whatsappBusinessTitle: 'WhatsApp Business-sjabloon',
      whatsappBusinessDesc:
        'Selecteer Meta-goedgekeurde sjablonen voor verzending via WhatsApp Business API.',
      whatsappBusinessEmptyDesc:
        'Selecteer een Meta-goedgekeurde sjabloon om verzending via WhatsApp Business API in te stellen.',
      selectedTemplate: 'Geselecteerde sjabloon',
      noTemplateSelected: 'Geen sjabloon geselecteerd',
      selectTemplate: 'Sjabloon selecteren',
      selectTemplateTooltip:
        'Selecteer een sjabloon uit de door Meta goedgekeurde sjablonen',
      selectTemplateSoon: 'Sjabloonselectie is binnenkort beschikbaar.',
      templateModal: {
        title: 'Selecteer een sjabloon',
        description:
          'Kies een van de sjablonen die zijn goedgekeurd op WhatsApp Business API.',
        messageTypeBadge: 'Voor {{messageType}}',
        countBadge: '{{count}} sjablonen',
        countBadgeFiltered: '{{count}} van {{total}}',
        searchPlaceholder: 'Zoek sjabloon op naam...',
        empty: 'Geen sjablonen beschikbaar',
        selectAction: 'Sjabloon selecteren',
        recommended:
          'Aanbevolen sjabloon op basis van het geselecteerde berichttype ({{messageType}})',
        bodyPreview: 'Body Preview',
        noPreview: 'Geen body-voorbeeld beschikbaar.',
        noCta: 'No CTA',
        ctaEnabled: 'CTA Enabled',
        corruptHint:
          'Deze sjabloon lijkt beschadigd en kan niet worden gebruikt. Waarschijnlijk is deze handmatig gewijzigd in Facebook Business Manager en voldoet deze niet meer aan de structuur van WhatsApp Business API.',
        loadError: 'Sjablonen laden mislukt',
        loadErrorDesc:
          'WhatsApp Business-sjablonen ophalen is mislukt. Probeer het opnieuw.',
        selectSuccess: 'Sjabloon succesvol geselecteerd.',
        selectError: 'Sjabloon selecteren mislukt. Probeer het opnieuw.',
      },
      whatsappWithImageTitle: 'WhatsApp met afbeelding',
      whatsappWithImageDesc:
        'Configureer bericht, afbeeldingsbijlage en dynamische placeholders.',
      whatsappMessageText: 'WhatsApp-berichttekst',
      emailTitle: 'Bulk-e-mail',
      emailDesc: 'Configureer e-mail, bijlagen en HTML-inhoud.',
      emailSubject: 'E-mailonderwerp',
      emailSubjectPlaceholder: 'Bijv.: Speciale promo voor jou 🎉',
      emailBody: 'E-mailinhoud',
      emailBodyHint: 'Als je een link invoegt, laat een spatie achter',
      applyTemplate: 'Sjabloon toepassen',
      sendTest: 'Testbericht verzenden',
      attachedImage: 'Bijgevoegde afbeelding',
      uploadImage: 'Afbeelding uploaden',
      removeImage: 'Afbeelding verwijderen',
      uploading: 'Uploaden...',
      removing: 'Verwijderen...',
      test: {
        title: 'Testverzending',
        emailTitle: 'Testverzending',
        oopsTitle: 'Oops...',
        ok: 'OK',
        send: 'Verzenden',
        testSending: 'Testverzending',
        backwards: 'Terug',
        emptyMessage: 'Voer een bericht in om te testen.',
        emptySubject: 'Voer een e-mailonderwerp in om te testen.',
        missingImage:
          'Er is geen afbeelding gekoppeld aan deze campagne. Voeg er een toe om te testen met afbeelding.',
        mobilePlaceholder: 'Mobiele telefoon',
        emptyPhone: 'Voer een mobiel nummer in om te testen.',
        emptyEmail: 'Voer een e-mailadres in om te testen.',
        emailLabel: 'E-mail:',
        emailPlaceholder:
          'Voer het e-mailadres in waar je de test wilt ontvangen',
        apiSoon: 'Testverzending-API is binnenkort beschikbaar.',
        sendSuccess: 'Testbericht succesvol verzonden.',
        sendError: 'Kon het testbericht niet verzenden. Probeer het opnieuw.',
      },
    },
    selectCustomers: {
      pageTitle: 'Klanten toewijzen aan {{name}}',
      subtitle: 'Filter en selecteer klanten voor deze campagne',
      subtitleWithBranch: 'Filter en selecteer klanten voor {{branch}}',
      searchPlaceholder: 'Zoek klant...',
      selected: 'Geselecteerd',
      clearSelection: 'Selectie wissen',
      removeAll: 'Alles verwijderen',
      saveList: 'Klantenlijst opslaan',
      bookingPeriodTitle: 'Reserveringen in de periode',
      bookingRangeLabel: 'Reserveringsperiode',
      bookingRangePlaceholder: 'Selecteer reserveringsperiode',
      removePeriod: 'Periode verwijderen',
      marketingConsentShort: 'Marketingtoestemming',
      listTitle: 'Klantenlijst',
      listDesc: 'Beheer klantselectie',
      selectPage: 'Pagina selecteren',
      deselectPage: 'Pagina deselecteren',
      selectAll: 'Alles selecteren',
      deselectAll: 'Alles deselecteren',
      progressAdding: '{{current}} van {{total}} toevoegen',
      selectRow: 'Klant selecteren',
      results: 'Resultaten',
      page: 'Pagina',
      noEmail: 'Geen e-mail',
      columns: {
        customer: 'Klant',
        phone: 'Telefoon',
        birth: 'Geboorte',
        bookings: 'Boekingen',
        tags: 'Tags',
      },
      toast: {
        saveSuccess: 'Klantenlijst succesvol opgeslagen.',
        saveError: 'Klantenlijst opslaan mislukt. Probeer opnieuw.',
        updateError: 'Klantselectie bijwerken mislukt. Probeer opnieuw.',
        selectAllSuccess: '{{count}} klanten geselecteerd.',
        selectAllPartial:
          '{{added}} klanten toegevoegd, daarna mislukte het verzoek. Probeer Alles selecteren opnieuw.',
        clearSuccess: 'Alle klanten uit de selectie verwijderd.',
        clearError: 'Selectie wissen mislukt. Probeer opnieuw.',
      },
    },
  },
  fidelity: {
    title: 'Programma’s loyaliteit',
    subtitleDefault: 'Bekijk en beheer je loyaliteitsprogramma’s',
    statsCount: '{{count}} programma’s',
    searchPlaceholder: 'Zoek een programma op naam...',
    addProgram: 'Nieuw programma',
    creating: 'Aanmaken...',
    saving: 'Opslaan...',
    createTitle: 'Loyaliteitsprogramma maken',
    createDesc: 'Voeg een nieuw loyaliteitsprogramma toe voor je vestiging.',
    createActiveConflict:
      'Er bestaat al een actief {{type}}-programma "{{name}}" voor deze vestiging.',
    createActiveConflictAction:
      'Deactiveer eerst het bestaande programma, of houd dit inactief.',
    createActiveBlocked:
      'Zet Actief programma uit om door te gaan, of deactiveer "{{name}}" in de lijst.',
    edit: {
      pageTitle: 'Loyaliteitsprogramma wijzigen',
    },
    programName: 'Programmanaam',
    programNamePlaceholder: 'Voer de programmanaam in',
    description: 'Beschrijving',
    descriptionPlaceholder: 'Voer de programmabeschrijving in',
    policyMessageCard: 'Beleid / Berichtkaart',
    tagsOnDownload: 'Tags toegepast bij download',
    tagsOnUse: 'Tags toegepast bij gebruik',
    tagsPlaceholder: 'Selecteer tags',
    type: 'Type',
    typePoints: 'Punten',
    typeDiscount: 'Korting',
    typeDiscountPrepaid: 'Korting / prepaid',
    customerCards: 'Klantkaarten',
    noThresholds: 'Geen puntendrempels geconfigureerd.',
    codeLinePrepaid: 'Programma met prepaidtegoed en korting op de kaart.',
    color: 'Kleur',
    value: 'Waarde',
    active: 'Actief',
    inactive: 'Inactief',
    activeProgram: 'Actief programma',
    discountApplied: 'Toegepaste korting (%)',
    codeLineRange: 'Van {{from}} tot {{to}} euro',
    codeLineDiscount: '{{value}}% korting',
    thresholdCount: '{{count}} drempels',
    customersCount: '{{count}} klanten',
    thresholdsTitle: 'Puntendrempels',
    thresholdsHint: 'De backend herschikt de drempels en vult gaten op.',
    addThreshold: 'Drempel',
    updateThreshold: 'Drempel bijwerken',
    thresholdFrom: 'Vanaf euro*',
    thresholdTo: 'Tot euro*',
    thresholdPointsLabel: 'Punten*',
    thresholdLabel: 'Label',
    thresholdColor: 'Drempelkleur',
    editProgram: 'Programma bewerken',
    deleteProgram: 'Programma verwijderen',
    duplicate: 'Dupliceren',
    editThreshold: 'Drempel bewerken',
    deleteThreshold: 'Drempel verwijderen',
    noBranch: 'Selecteer een vestiging om loyaliteitsprogramma’s te bekijken',
    empty: 'Geen loyaliteitsprogramma geconfigureerd',
    emptyDesc:
      'Maak een punten- of kortingsprogramma om klantenkaarten te genereren.',
    cardsTitle: 'Klantkaarten',
    cardsSubtitle: 'Uitgegeven kaarten, QR en saldi',
    cardsEmpty: 'Geen loyaliteitskaarten gevonden voor dit programma.',
    searchCustomerPlaceholder: 'Over de klant, de kaart...',
    createCard: 'Kaart maken',
    refreshCards: 'Kaarten vernieuwen',
    update: 'Bijwerken',
    scanQrCode: 'QR-code scannen',
    useCard: 'Kaart gebruiken',
    toggleCardStatus: 'Status gebruikt/beschikbaar wijzigen',
    operationHistory: 'Bewerkingsgeschiedenis',
    qrCodeTitle: 'QR-code scannen',
    downloadQr: 'Downloaden',
    historyTitle: 'Geschiedenis loyaliteitskaart',
    historyEmpty: 'Geen bewerkingen beschikbaar voor deze loyaliteitskaart.',
    postedBy: 'Geplaatst door',
    branchCode: 'Vestiging',
    fidelityCode: 'Loyaliteit',
    customerId: 'Klant-ID',
    pointsSuffix: 'punten',
    amountSuffix: 'euro',
    cardAvailable: 'beschikbaar',
    cardUsed: 'gebruikt',
    columns: {
      qr: 'QR',
      customer: 'Klant',
      contacts: 'Contacten',
      card: 'Kaart',
      balance: 'Saldo',
      state: 'Status',
      activation: 'Activatie',
      threshold: 'Drempel',
      label: 'Label',
      points: 'Punten',
      color: 'Kleur',
    },
    createCardPage: {
      title: 'Klant kiezen',
      subtitle:
        'Selecteer een klant om een nieuwe loyaliteitskaart uit te geven.',
      subtitleWithProgram: 'Klanten voor {{branch}} · {{program}}',
      newCard: 'Nieuwe kaart',
      chooseCustomer: 'Klant kiezen',
      searchPlaceholder: 'Zoek op naam, telefoonnummer of e-mail...',
      results: 'Resultaten',
      page: 'Pagina',
      availableCustomers: 'Beschikbare klanten',
      availableCustomersDesc:
        'Selecteer een klant om de bevestiging voor kaartaanmaak te openen.',
      noEmail: 'Geen e-mail',
      columns: {
        customer: 'Klant',
        telephone: 'Telefoon',
        wood: 'Bezoeken',
        tags: 'Tags',
      },
    },
    createCardConfirm: {
      title: 'Wil je een kaart voor deze klant maken?',
      confirm: 'Ja, kaarten maken',
      creating: 'Aanmaken...',
    },
    statCards: {
      programs: 'Programma’s',
      active: 'Actief',
      types: 'Types',
    },
    deleteModal: {
      title: 'Loyaliteitsprogramma verwijderen?',
      description:
        'Weet je zeker dat je dit loyaliteitsprogramma wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
    },
    deleteThresholdModal: {
      title: 'Drempel verwijderen?',
      description:
        'Weet je zeker dat je deze drempel wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
    },
    thresholdModal: {
      createTitle: 'Puntendrempel toevoegen',
      editTitle: 'Puntendrempel wijzigen',
      description:
        'Configureer het bereik, de punten, het label en de drempelkleur.',
      lowerLimit: 'Ondergrens*',
      upperLimit: 'Bovengrens*',
      pointsApplied: 'Toegepaste punten*',
    },
    toast: {
      createSuccess: 'Loyaliteitsprogramma succesvol aangemaakt.',
      createError:
        'Aanmaken van loyaliteitsprogramma mislukt. Probeer opnieuw.',
      updateSuccess: 'Loyaliteitsprogramma succesvol bijgewerkt.',
      updateError:
        'Bijwerken van loyaliteitsprogramma mislukt. Probeer opnieuw.',
      deleteSuccess: 'Loyaliteitsprogramma succesvol verwijderd.',
      deleteError:
        'Verwijderen van loyaliteitsprogramma mislukt. Probeer opnieuw.',
      duplicateSuccess: 'Loyaliteitsprogramma succesvol gedupliceerd.',
      duplicateError:
        'Dupliceren van loyaliteitsprogramma mislukt. Probeer opnieuw.',
      thresholdCreateSuccess: 'Drempel succesvol aangemaakt.',
      thresholdCreateError: 'Aanmaken van drempel mislukt.',
      thresholdUpdateSuccess: 'Drempel succesvol bijgewerkt.',
      thresholdUpdateError: 'Bijwerken van drempel mislukt.',
      thresholdDeleteSuccess: 'Drempel succesvol verwijderd.',
      thresholdDeleteError: 'Verwijderen van drempel mislukt.',
      cardCreateSuccess: 'Loyaliteitskaart succesvol aangemaakt.',
      cardCreateError: 'Aanmaken van loyaliteitskaart mislukt.',
      cardUseSuccess: 'Kaart gemarkeerd als gebruikt.',
      cardUseError: 'Bijwerken van kaartgebruik mislukt.',
      cardToggleSuccess: 'Kaartstatus succesvol bijgewerkt.',
      cardToggleError: 'Bijwerken van kaartstatus mislukt.',
    },
    operations: {
      addPoints: 'Punten toegevoegd',
      updatePoints: 'Punten bijgewerkt',
      removePoints: 'Punten verwijderd',
      addBillAmount: 'Factuurbedrag toegevoegd',
      addPrepaidAmount: 'Vooruitbetaald bedrag toegevoegd',
      updatePrepaidAmount: 'Vooruitbetaald bedrag bijgewerkt',
      disableAccount: 'Account uitgeschakeld',
      createAccount: 'Account aangemaakt',
      used: 'Kaart gebruikt',
    },
    registration: {
      title: 'Zelfregistratie voor klanten',
      description:
        'Deel deze link: klanten kunnen zelf een fidelitykaart aanmaken.',
      linkLabel: 'Fidelity-inschrijvingslink',
      openForm: 'Formulier openen',
      copy: 'Link kopiëren',
      qr: 'QR-code',
    },
    views: {
      label: 'Fidelityprogramma',
      configuration: 'Configuratie',
      configurationHint: 'Regels, voordelen en berichten',
      cards: 'Klantkaarten',
      cardsHint: 'Uitgegeven kaarten, QR en bewegingen',
      allPrograms: 'Alle fidelityprogramma’s',
    },
    wizard: {
      program: 'Programma en voordelen',
      messages: 'Berichten',
      review: 'Overzicht',
      back: 'Terug',
      continue: 'Doorgaan',
      save: 'Configuratie opslaan',
      stepOf: '{{current}} / {{total}}',
      stepListLabel: 'Stappen voor loyaliteitsinstelling',
      pointsIntro:
        'Bepaal het programma, de regels om punten te sparen en het welkomstbericht.',
      discountIntro:
        'Bepaal het percentage, het uiterlijk van de kaart en het welkomstbericht.',
      discountNote:
        'Dit programma gebruikt een kortingspercentage en spaart geen punten. Het prepaidtegoed wordt op de klantkaart beheerd.',
      benefitPoints: 'Punten sparen',
      benefitDiscount: '{{value}}% korting',
      messageLabel: 'Welkomstbericht',
      messagePlaceholder: 'Kies een fidelitybericht',
      messageStatus: '{{status}} · WhatsApp {{whatsapp}} · E-mail {{email}}',
      messageActive: 'Actief',
      messageInactive: 'Inactief',
      channelOn: 'aan',
      channelOff: 'uit',
      messageWarning:
        'Schakel WhatsApp of e-mail voor dit bericht in voordat u opslaat.',
      messageLoadError: 'Berichten laden is mislukt.',
      configureMessages: 'Berichten configureren',
      rulesLabel: 'Programmaregels',
      reviewTitle: 'Programmaoverzicht',
      name: 'Naam',
      status: 'Status',
      benefit: 'Voordeel',
      message: 'Bericht',
      autoUpdates: 'Automatische updates',
      autoUpdatesValue:
        'Een apart bericht voor elke handeling, instelbaar bij Berichten.',
      createProgram: 'Het programma',
      createBenefits: 'Voordelen',
      createPoints: 'Bestedingsstaffels',
      createRewards: 'Inwisselbare beloningen',
      createHistorical: 'Levenslange niveaus',
      createProgramDesc: 'Kies de naam en het type programma.',
      createPointsDesc:
        'Stel één spaarregel in. Dezelfde punten vullen zowel het beschikbare als het historische saldo.',
      createRewardsDesc:
        'Configureer drempels en inwisselbare beloningen. Bij inwisseling worden punten van het beschikbare saldo afgetrokken.',
      createHistoricalDesc:
        'Stel punten, naam, kleur en korting per niveau in, ook vanaf 0 punten. De Fidelity-QR herkent de korting zonder historische punten af te trekken.',
      createBenefitsDesc:
        'Stel de bestedingsstaffels voor punten in, of het kortingspercentage.',
      createMessagesDesc:
        'Stel het WhatsApp- en e-mailwelkomstbericht in met de QR van de kaart.',
      createReviewDesc:
        'Controleer voordelen, status en bericht voordat u het programma maakt.',
      createAction: 'Programma maken',
      afterSaveNote:
        'U kunt klantkaarten maken nadat u het programma hebt opgeslagen.',
      thresholdStepHint:
        'Geef per bestedingsstaffel aan hoeveel punten de klant ontvangt.',
      thresholdReviewLine: 'Van {{from}} tot {{to}} €: {{points}} punten',
    },
    validation: {
      nameMin: 'Programmanaam moet minstens 2 tekens bevatten',
      nameMax: 'Programmanaam mag niet meer dan 100 tekens bevatten',
      colorInvalid: 'Voer een geldige hex-kleur in (bijv. #FFFFFF)',
      thresholdRequired: 'Voeg minstens één drempel toe.',
      thresholdRange:
        'De bovengrens moet groter dan of gelijk aan de ondergrens zijn.',
      thresholdLower: 'Voer een geldige ondergrens in.',
      thresholdUpper: 'Voer een geldige bovengrens in.',
      thresholdPoints: 'Voer geldige punten in.',
      thresholdLabelRequired: 'Drempellabel is verplicht.',
      thresholdLabelMax: 'Drempellabel mag niet meer dan 100 tekens bevatten.',
    },
  },
  marketingGuide: {
    infoButton: 'Marketinggids',
    title: 'Kies het juiste marketingmiddel',
    intro:
      'Promoties en Fidelity kunnen allebei voordeel bieden, maar dienen verschillende doelen.',
    promotionTitle: 'Promoties · een campagne met einddatum',
    promotionBody:
      'Gebruik een promotie om nieuwe klanten aan te trekken, incidentele klanten terug te winnen of een rustige periode te stimuleren. De klant doet mee via een openbare link of QR-code en ontvangt een coupon, zonder punten te sparen.\nVoorbeeld: “10% korting tot zondag, bij minimaal €30 besteding, één keer per klant.”',
    fidelityTitle: 'Fidelity · een reden om terug te komen',
    fidelityBody:
      'Gebruik Fidelity om terugkerende bezoeken te belonen. Aankopen leveren punten op volgens de ingestelde regels; bij een drempel kan de klant een beloning inwisselen. Saldo en transacties blijven bij de kaart bewaard.\nVoorbeeld: “Spaar punten met elke bon en kies een beloning zodra je een mijlpaal bereikt.”',
    rulesTitle: 'Praktische marketingregels',
    rulesBody:
      '1. Stel één duidelijk doel: klanten werven, terugwinnen, vaker laten komen of de gemiddelde besteding verhogen.\n2. Bescherm je marge: kies een haalbare korting en minimale besteding, en stel een aantal- of budgetlimiet in.\n3. Maak voorwaarden duidelijk: datums, geldige producten of kanalen, minimum besteding en combineerbaarheid.\n4. Houd de aanbieding begrijpelijk en eenvoudig te verzilveren; te veel voorwaarden verminderen deelname.\n5. Meet verzilveringen en vervolguitgaven, niet alleen uitgegeven coupons of verzamelde punten.',
    togetherTitle: 'Ze vullen elkaar aan',
    togetherBody:
      'Gebruik een tijdelijke promotie om iemand binnen te halen en nodig die klant daarna uit voor Fidelity als blijvende reden om terug te komen. Maak de promotiecoupon en Fidelity-beloning duidelijk herkenbaar.',
  },
  promotions: {
    title: 'Promoties',
    titleWithBranch: 'Promo {{branch}}',
    subtitle: 'Promotielijst voor {{branch}}',
    subtitleDefault: 'Bekijk en beheer de promotielijst',
    pageDescription:
      'Beheer promoties, coupons en promo-configuraties van de locatie',
    statsCount: '{{count}} promoties',
    searchPlaceholder: 'Zoek promoties op naam...',
    promoName: 'Promonaam',
    promoNamePlaceholder: 'Voer de promotienaam in',
    description: 'Beschrijving',
    descriptionPlaceholder: 'Voer de promotiebeschrijving in',
    used: 'Gebruikt',
    usedCouponCount: '{{count}} kortingsbon',
    downloaded: 'Gedownload',
    messages: 'Berichten',
    messagesTooltip: 'Berichten verzonden via deze promo',
    messageHistory: {
      title: 'Berichtgeschiedenis',
      reference: 'Referentie {{code}}',
      empty: 'Geen berichten beschikbaar.',
      refresh: 'Vernieuwen',
      loadError: 'Kan berichten niet laden.',
      attempts: '{{count}} pogingen',
    },
    customerCoupons: {
      title: 'Klantcoupons',
      subtitle: 'Promo: {{name}} · {{count}} coupons uitgegeven',
      discount: '{{percent}}% korting',
      redeemed: 'Ingewisseld',
      notRedeemed: 'Niet ingewisseld',
      generatedOn: 'Gegenereerd op {{date}}',
      redeemedOn: 'Ingewisseld op {{date}}',
      operator: 'Operator: {{name}}',
      empty: 'Nog geen coupons uitgegeven.',
      loadError: 'Klantcoupons laden mislukt.',
      loading: 'Coupons laden...',
    },
    status: 'Status',
    active: 'Actief',
    expired: 'Verlopen',
    url: 'URL',
    qrCode: 'QR-code',
    preview: 'Voorbeeld',
    copyUrl: 'URL kopiëren',
    duplicate: 'Dupliceren',
    stop: 'Stoppen',
    restart: 'Herstarten',
    editPromotion: 'Promotie bewerken',
    delete: 'Promotie verwijderen',
    addPromotion: 'Promotie toevoegen',
    creating: 'Aanmaken...',
    createTitle: 'Promotie aanmaken',
    createDesc: 'Voeg een nieuwe berichtencampagne toe voor uw vestiging.',
    createWizard: {
      detailsDesc:
        'Stel de naam, beschikbare coupons en korting in. U kunt het klantformulier na het aanmaken aanpassen.',
      messageDesc:
        'Kies een van de drie berichten die zijn ingesteld in Instellingen → Berichten.',
      reviewHint: 'Controleer de instellingen voordat u de promotie aanmaakt.',
      stepCount: '{{current}} / {{total}}',
      back: 'Terug',
      continue: 'Doorgaan',
      createAndCustomize: 'Aanmaken en aanpassen',
      couponHint: 'Gebruik 0 als de promotie geen procentuele korting heeft.',
      automaticUpdatesLabel: 'Automatische updates',
      automaticUpdates:
        'Een apart bericht voor elke actie, instelbaar bij Berichten.',
    },
    close: 'Sluiten',
    save: 'Opslaan',
    saveChanges: 'Wijzigingen opslaan',
    buttonLabel: 'Knoptekst',
    buttonLabelPlaceholder: 'Voer knoptekst in',
    redirectPage: 'Doorverwijspagina',
    redirectPagePlaceholder: 'Aangepaste bedankpagina-URL',
    percentageDiscount: 'Korting (%)',
    maxCouponCount: 'Max. coupons',
    maxUsageCount: 'Max. gebruik',
    itemsPerPage: 'Items per pagina',
    range: '{{start}} - {{end}} van {{total}}',
    noBranch: 'Selecteer een vestiging om promoties te bekijken',
    empty: 'Geen promo’s beschikbaar',
    emptyDesc:
      'Je hebt nog geen promoties voor deze locatie aangemaakt. Begin met het maken van je eerste promo.',
    deleteModal: {
      title: 'Promotie verwijderen?',
      description:
        'Weet u zeker dat u deze promotie wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
    },
    validation: {
      promotionNameMin: 'Naam moet minimaal 2 tekens bevatten',
      promotionNameMax: 'Naam mag niet langer zijn dan 120 tekens',
      couponMin: 'Voer een geheel getal groter dan nul in.',
      discountRange: 'Voer een geheel getal van 0 tot 100 in.',
      redirectPageInvalid:
        'Voer een geldige URL in (bijv. https://example.com).',
    },
    toast: {
      createSuccess: 'Promotie succesvol aangemaakt.',
      createError: 'Kan promotie niet aanmaken. Probeer het opnieuw.',
      updateSuccess: 'Promotie succesvol bijgewerkt.',
      updateError: 'Kan promotie niet bijwerken. Probeer het opnieuw.',
      deleteSuccess: 'Promotie succesvol verwijderd.',
      deleteError: 'Kan promotie niet verwijderen. Probeer het opnieuw.',
      duplicateSuccess: 'Promotie succesvol gedupliceerd.',
      duplicateError: 'Kan promotie niet dupliceren. Probeer het opnieuw.',
      stopSuccess: 'Promotie succesvol gestopt.',
      stopError: 'Kan promotie niet stoppen. Probeer het opnieuw.',
      restartSuccess: 'Promotie succesvol herstart.',
      restartError: 'Kan promotie niet herstarten. Probeer het opnieuw.',
      logoUploadSuccess: 'Logo succesvol geüpload.',
      logoUploadError: 'Kan logo niet uploaden.',
      logoRemoveSuccess: 'Logo succesvol verwijderd.',
      logoRemoveError: 'Kan logo niet verwijderen.',
      posterUploadSuccess: 'Poster succesvol geüpload.',
      posterUploadError: 'Kan poster niet uploaden.',
      posterRemoveSuccess: 'Poster succesvol verwijderd.',
      posterRemoveError: 'Kan poster niet verwijderen.',
    },
    edit: {
      pageTitle: 'Promotie bewerken',
      subtitle: 'Configureer formulier- en promotie-instellingen.',
      tabListLabel: 'Promotie bewerkingssecties',
      wizard: {
        stepListLabel: 'Stappen voor promotie-instelling',
        form: 'Klantformulier',
        formDesc:
          'Pas het formulier aan dat de klant invult om de coupon aan te vragen.',
        rules: 'Regels',
        rulesDesc:
          'Stel de periode, beschikbaarheid, korting en gevraagde gegevens in.',
        message: 'Bericht',
        messageDesc:
          'Kies een van de drie berichten die zijn ingesteld in Instellingen → Berichten.',
        review: 'Overzicht',
        reviewDesc:
          'Controleer de instellingen en sla op. Niet-opgeslagen wijzigingen blijven op deze pagina.',
        editStep: 'Bewerken',
        period: 'Periode',
        requestedFields: 'Gevraagde gegevens',
        noDate: 'Geen datum',
        none: 'Geen',
      },
      uploadImage: 'Afbeelding uploaden',
      removeImage: 'Afbeelding verwijderen',
      uploading: 'Uploaden...',
      removing: 'Verwijderen...',
      tabs: {
        formConfig: 'Formulierconfiguratie',
        promoConfig: 'Promotieconfiguratie',
      },
      formConfig: {
        media: 'Media',
        logo: 'Promo-logo',
        details: 'Formulierdetails',
        promoText: 'Promotekst',
        promoLink: 'Promolink',
      },
      promoConfig: {
        poster: 'Poster',
        posterLabel: 'Promo-poster',
        options: 'Opties',
        useOnlyOnce: 'Coupon slechts één keer bruikbaar',
        useOnlyOnceDesc: 'Beperk coupon-download tot één keer per klant',
        showEmail: 'E-mail vragen',
        showEmailDesc: 'Toon het e-mailveld in het promoformulier',
        showAddress: 'Adres vragen',
        showAddressDesc: 'Toon het adresveld in het promoformulier',
        showCap: 'Postcode vragen',
        showCapDesc: 'Toon het postcodeveld in het promoformulier',
        showDob: 'Geboortedatum vragen',
        showDobDesc: 'Toon het geboortedatumveld in het promoformulier',
        showCountry: 'Land vragen',
        limits: 'Data en limieten',
        startDate: 'Startdatum',
        endDate: 'Einddatum',
        tags: 'Tags',
        tagsOnDownload: 'Tags toegepast bij download',
        tagsOnUse: 'Tags toegepast bij gebruik',
        tagsPlaceholder: 'Selecteer tags',
        messaging: 'Berichten',
        messageLabel: 'Bericht voor de klant',
        messagePlaceholder: 'Kies een van de drie gedeelde configuraties.',
        messageStatus:
          '{{status}} · WhatsApp: {{whatsapp}} · E-mail: {{email}}',
        messageActive: 'Configuratie actief',
        messageInactive: 'Configuratie uitgeschakeld',
        channelOn: 'aan',
        channelOff: 'uit',
        messageWarning:
          'Schakel minstens één kanaal in bij Berichten om deze communicatie te versturen.',
        configureMessages:
          'Stel teksten, kanalen en sjablonen in bij Berichten',
        messageLoadError:
          'Configuraties laden is mislukt. Probeer het opnieuw via Berichten.',
        sharedMessageNote:
          'Elke actie gebruikt het speciale bericht dat is ingesteld bij Berichten. PROMO_INFO is gereserveerd voor statusupdates.',
        whatsappMessage: 'WhatsApp couponbericht',
        whatsappSubtitle: 'Bericht verzonden met de promo QR-code',
        whatsappPlaceholder: 'Schrijf hier uw bericht...',
        applyTemplate: 'Standaardsjabloon toepassen',
        sendMessage: 'Bericht verzenden',
      },
      detailsTitle: 'Promotiedetails',
      detailsDesc: 'Werk campagne-informatie en limieten bij.',
      branchHint: 'Vestiging: {{branch}}',
    },
  },
  supplierOrdersPage: {
    receivedModified: 'Ontvangen met wijzigingen',
    processedModified: 'Verwerkt maar gewijzigd',
    processed: 'Verwerkt',
    title: 'Leveranciersbestellingen',
    subtitle: 'Inkopen, leveringen en productcontrole',
    history: 'Bestelgeschiedenis',
    create: 'Bestelling maken',
    search: 'Bestelling of product zoeken…',
    supplier: 'Leverancier',
    allSuppliers: 'Alle leveranciers',
    invalidDates: 'Selecteer een geldig datumbereik.',
    onlyModified: 'Alleen gewijzigde bestellingen',
    loadError: 'Bestellingen kunnen niet worden geladen.',
    ordersOn: 'Bestellingen van {{date}} · {{count}}',
    empty: 'Geen bestellingen gevonden',
    order: 'Bestelling',
    delivery: 'Levering',
    status: 'Status',
    totalVat: 'Totaal incl. btw',
    perPage: 'Per pagina',
    orderCount: '{{count}} bestellingen',
    modified: 'Gewijzigd',
    recipient: 'Ontvanger',
    chooseRecipient: 'Kies een leverancier of vestiging',
    branchSuffix: 'Vestiging',
    deliveryDate: 'Leverdatum',
    futureDate: 'Kies vandaag of een toekomstige datum.',
    pastDate: 'De leverdatum moet vandaag of een toekomstige datum zijn.',
    receiveFrom: 'Ontvangst vanaf',
    receiveTo: 'Tot',
    catalogSearch: 'Zoeken in de catalogus…',
    catalogError: 'De catalogus kan niet worden geladen.',
    noProducts: 'Geen producten beschikbaar',
    product: 'Product',
    vatLine: '{{unit}} · btw {{vat}}%',
    net: 'Netto',
    quantity: 'Aantal {{name}}',
    removeNamed: '{{name}} verwijderen',
    remove: 'Verwijderen',
    add: 'Toevoegen',
    productCount: '{{count}} producten',
    totalLine: 'Totaal incl. btw: {{amount}}',
    createHint:
      'Kies een ontvanger, een datum en minstens één product met een positief aantal. De eindtijd moet na de starttijd liggen.',
    createError: 'De bestelling kan niet worden gemaakt.',
    calendarTitle: 'Bestelkalender',
    otherDate: 'Andere datum',
    loadPeriod: 'Andere periode laden',
    today: 'Vandaag',
    previousDays: 'Naar eerdere dagen',
    nextDays: 'Naar latere dagen',
    window: '(±20 dagen)',
    incoming: 'Onderweg',
    toReview: 'Te controleren',
    calendarTotals: 'Totalen · alle leveranciers',
    calendarError: 'Kalendertotalen zijn niet beschikbaar.',
    calendarDays: 'Dagen van de bestelkalender',
    incomingSummary: '{{count}} onderweg',
    reviewSummary: '{{count}} te controleren',
    processedSummary: '{{count}} verwerkt',
    ordersSummary: '{{count}} bestellingen, statusdetail niet beschikbaar',
    countsUnavailable: 'totalen niet beschikbaar',
    calendarHint:
      'Elke dag telt geplande, inkomende en verwerkte bestellingen. Zoeken en leverancier filteren de onderstaande lijst.',
    exportJson: 'JSON exporteren',
    importJson: 'JSON importeren',
    exported: 'Bestand geëxporteerd.',
    importedCount: '{{count}} items geïmporteerd.',
    exportError: 'Exporteren is mislukt. Probeer het opnieuw.',
    invalidJson: 'Ongeldige JSON.',
    importProducts:
      'Voer een array van producten in met een geldige naam, nettoprijs, btw en eenheid.',
    importSuppliers:
      'Voer een array van leveranciers in met een naam op elke rij.',
    importError:
      'Importeren is mislukt. Controleer de gegevens en probeer het opnieuw.',
    importTitle: '{{label}} importeren',
    importAction: 'Importeren',
    jsonFile: 'JSON-bestand',
    fileTooLarge: 'Het bestand is groter dan 5 MB.',
    fileReadError: 'Het bestand kan niet worden gelezen.',
    importOrdersHint:
      'Importeren maakt nieuwe bestellingen voor de geselecteerde vestiging. Opnieuw importeren maakt meer bestellingen.',
    jsonArray: 'JSON-array',
    ordersLabel: 'bestellingen',
    productsLabel: 'producten',
    suppliersLabel: 'leveranciers',
    statuses: {
      BOZZA: 'Concept',
      PROGRAMMATO: 'Ingepland',
      IN_PREPARAZIONE: 'In voorbereiding',
      IN_CONSEGNA: 'Onderweg',
      DA_VERIFICARE: 'Te controleren',
      RICEVUTO: 'Ontvangen',
      NON_CONSEGNATO: 'Niet geleverd',
      ANNULLATO: 'Geannuleerd',
      INVIATO: 'Verzonden',
      DA_CONFERMARE: 'Te bevestigen',
      CONSEGNATO: 'Geleverd',
    },
  },
  supplierOrderDetail: {
    matchProduct: 'Match to product',
    skipLine: 'Skip this line',
    duplicateMatch: 'Match each line to a different product.',
    continue: 'Continue',
    category: 'Category',
    close: 'Close',
    allCategories: 'All categories',
    personalWhatsApp: 'Personal WhatsApp',
    localWhatsApp: 'Restaurant WhatsApp',
    confirmSend: 'Send the order to the supplier using this channel?',
    confirmEdit: 'Save the updated order quantities?',
    checkQuantities: 'Check quantities',
    alreadyDelivered: 'Goods have arrived: proceed to verification',
    createReview: 'Order review',
    discardDraft: 'Discard the selected products?',
    savedShare:
      'Order saved. Would you like to choose how to send it to the supplier?',
    title: 'Leveranciersbestelling',
    orderTitle: 'Bestelling #{{id}}',
    back: 'Bestellingen',
    unavailable: 'Deze bestelling is niet beschikbaar.',
    requestedDelivery: 'Gevraagde levering',
    changeDate: 'Datum wijzigen',
    orderTotal: 'Besteltotaal incl. btw',
    netLine: 'Netto {{net}} · btw {{vat}}',
    products: 'Bestelde producten',
    addReceived: 'Ontvangen product toevoegen',
    verifyAll: 'Alles controleren',
    ordered: 'Besteld',
    sent: 'Verzonden',
    unitNet: 'Netto per eenheid',
    received: 'Ontvangen',
    receivedPrice: 'Ontvangen nettoprijs',
    verified: 'Gecontroleerd',
    verifyNamed: '{{name}} controleren',
    receivedNamed: 'Ontvangen aantal {{name}}',
    receivedPriceNamed: 'Ontvangen prijs {{name}}',
    editOrder: 'Bestelling bijwerken',
    prepare: 'Bestelling voorbereiden',
    dispatch: 'Verzending bevestigen',
    arrived: 'Levering aangekomen',
    cancelOrder: 'Bestelling annuleren',
    notDelivered: 'Niet geleverd',
    receiptTitle: 'Ontvangst controleren',
    invoiceTotal: 'Factuurtotaal incl. btw',
    receivedProductsTotal: 'Berekend producttotaal (incl. btw)',
    invoiceTotalHint: 'Bedrag vastgelegd bij bevestiging van ontvangst.',
    invoiceDraftHint:
      'Ingevoerd ontvangstbedrag; wordt bij bevestiging opgeslagen.',
    insertedAmount: 'Ingevoerd totaal',
    expectedAmount: 'Verwacht besteltotaal',
    recordedAmount: 'Geregistreerd totaal',
    receivedProductsTitle: 'Overzicht ontvangen producten',
    lineTotal: 'Regeltotaal incl. btw',
    receivedLineCalculation: '{{quantity}} × {{price}} + {{vat}}% btw',
    invoiceGoodsDifference: 'Factuur minus producttotaal: {{amount}}',
    pendingVerification: 'Te controleren',
    verifiedProgress: '{{checked}} van {{total}} regels gecontroleerd',
    receiptBlockedTitle: 'Om “Ontvangst bevestigen” in te schakelen:',
    receiptNeedsProducts: 'Voeg minstens één ontvangen product toe.',
    receiptNeedsChecks:
      'Vink “Gecontroleerd” aan bij de resterende regels: {{count}}.',
    receiptInvalidLines:
      'Corrigeer ongeldige ontvangen aantallen of prijzen (nul of hoger). Regels: {{count}}.',
    receiptInvalidInvoice: 'Voer een geldig factuurtotaal van nul of hoger in.',
    receiptSaving: 'Wacht totdat de bestelling is opgeslagen.',
    receiptUploading: 'Wacht totdat de factuurfoto is geüpload.',
    receiptAnalyzing: 'Wacht totdat de factuuranalyse is voltooid.',
    goToVerification: 'Ga naar de volgende te controleren regel',
    receiptReady:
      'Alle regels zijn gecontroleerd. Je kunt de ontvangst bevestigen.',
    receiptTotalDifference:
      'Het factuurtotaal {{invoice}} wijkt af van het totaal ontvangen producten {{products}}. Controleer de bedragen; dit verschil blokkeert de bevestiging niet.',
    movedToHistory: 'Bericht verplaatst naar bestelgeschiedenis',
    receiptHint:
      'Controleer het ontvangen aantal en de nettoprijs en vink “Gecontroleerd” aan bij elke productregel. Bij een wijziging van aantal of prijs vervalt de controle van die regel.',
    confirmReceipt: 'Ontvangst bevestigen',
    invoice: 'Factuur',
    invoiceAlt: 'Factuurfoto',
    invoicePhoto: 'Factuurfoto',
    choosePhoto: 'Bestand kiezen',
    ocrLabel: 'Optionele notities',
    ocrPlaceholder:
      'Optionele notities voor AI-analyse. Laat leeg om alleen de foto te analyseren.',
    ocrRunning: 'Factuur analyseren met AI…',
    ocrFailed: 'Analyse mislukt. Probeer een andere foto of voeg notities toe.',
    clearPhotoAndText: 'Foto en tekst wissen',
    readingPhotosTitle: 'Foto laden…',
    ocrProcessingTitle: 'Factuur analyseren met AI…',
    ocrProcessingHint:
      'Foto op de server lezen. Dit kan enkele seconden duren.',
    aiAnalyzingTitle: 'Factuur analyseren met AI…',
    aiAnalyzingHint: 'Foto op de server lezen. Dit kan enkele seconden duren.',
    photoUploadingTitle: 'Factuurfoto opslaan…',
    photoUploadingHint: 'Afbeelding uploaden vóór analyse.',
    analyze: 'Factuur analyseren',
    reviewExtracted: 'Controleer de uitgelezen waarden voordat je ze toepast.',
    applyMatches: 'Toepassen op overeenkomende producten',
    photoType: 'Upload een JPG- of PNG-afbeelding van maximaal 5 MB.',
    imageInvalid: 'Ongeldige afbeelding.',
    imageReadError: 'De afbeelding kan niet worden gelezen.',
    photoSaved: 'Factuurfoto opgeslagen.',
    photoSaveError: 'De factuurfoto kan niet worden opgeslagen.',
    extractError: 'Factuuranalyse is mislukt.',
    communications: 'Berichten aan de leverancier',
    configureMessages: 'Berichten instellen',
    communicationsError: 'Berichten kunnen niet worden geladen.',
    openSupplier: 'Leverancierspagina openen',
    recipientEmail: 'E-mail van de ontvanger',
    sendEmail: 'E-mail verzenden',
    updated: 'Bestelling bijgewerkt.',
    operationError: 'De bewerking is mislukt.',
    sendFailed: 'Verzenden is mislukt.',
    messageSent: 'Bericht verzonden.',
    requestLogged:
      'Verzoek vastgelegd. Controleer het resultaat bij de berichten.',
    communicationError: 'Het bericht is mislukt.',
    saveDate: 'Datum opslaan',
    invalidDate: 'Ongeldige datum.',
    confirmTitle: 'Actie bevestigen',
    confirmAction: 'Bevestigen',
    confirmBody: 'Deze bestelling bijwerken?',
    confirmReceive:
      'De ontvangst wordt opgeslagen met de gecontroleerde aantallen en prijzen.',
    unexpectedProduct: 'Onverwacht ontvangen product',
    catalogUnavailable: 'De catalogus is niet beschikbaar.',
  },
  supplierOrderHistory: {
    title: 'Bestelgeschiedenis',
    subtitle: 'Zoek inkopen op leverancier, product en leverdatum',
    back: 'Leveranciersbestellingen',
    dateRange: 'Leverdatums',
    load: 'Geschiedenis laden',
    loading: 'Laden…',
    reset: 'Filters wissen',
    orders: 'Bestellingen',
    ordered: 'Besteld',
    received: 'Ontvangen',
    vatProducts: 'Incl. btw · gefilterde producten',
    allResults: 'Alle gefilterde resultaten',
    search: 'Zoeken op productnaam…',
    hint: 'Alle statussen, inclusief gearchiveerd · Bedragen incl. btw',
    summaryError: 'Het overzicht kan niet worden geladen.',
    dirty:
      'Filters gewijzigd: druk op “Geschiedenis laden” om de resultaten en exports te vernieuwen.',
    loadError: 'De geschiedenis kan niet worden geladen.',
    results: 'Zoekresultaten',
    resultsMeta:
      '{{count}} bestellingen · De export bevat alle gefilterde resultaten.',
    excel: 'Excel',
    pdf: 'PDF',
    exporting: 'Exporteren…',
    empty: 'Geen bestellingen gevonden met deze filters',
    deliveryDate: 'Leverdatum',
    products: 'Producten',
    filteredTotal: 'Gefilterde producten · incl. btw',
    exported: '{{orders}} bestellingen en {{rows}} productregels geëxporteerd.',
    exportError: 'Exporteren is mislukt.',
    allSuppliers: 'Alle leveranciers',
    exportSheetProducts: 'Producten',
    exportSheetSearch: 'Zoeken',
    exportTitle: 'Geschiedenis leveranciersbestellingen',
    exportOrder: 'Bestelling',
    exportDelivery: 'Leverdatum',
    exportSupplier: 'Leverancier',
    exportSupplierCode: 'Leverancierscode',
    exportStatus: 'Status',
    exportProduct: 'Product',
    exportUnit: 'Eenheid',
    exportOrderedQty: 'Besteld aantal',
    exportSentQty: 'Verzonden aantal',
    exportReceivedQty: 'Ontvangen aantal',
    exportOrderedNet: 'Bestelde nettoprijs',
    exportReceivedNet: 'Ontvangen nettoprijs',
    exportVat: 'btw %',
    exportOrderedTotal: 'Besteld totaal incl. btw',
    exportReceivedTotal: 'Ontvangen totaal incl. btw',
    exportQtyChange: 'Aantalwijziging',
    exportPriceChange: 'Prijswijziging',
    exportYes: 'Ja',
    exportNo: 'Nee',
    exportBranch: 'Vestiging',
    exportDeliveryFrom: 'Levering van',
    exportDeliveryTo: 'Levering tot',
    exportProductName: 'Productnaam',
    exportAllProducts: 'Alle',
    exportStatuses: 'Statussen',
    exportAllStatuses: 'Alle, inclusief gearchiveerd',
    exportOrderCount: 'Totaal bestellingen',
    exportOrderedAmount: 'Besteld bedrag incl. btw',
    exportReceivedAmount: 'Ontvangen bedrag incl. btw',
    exportProductRows: 'Productregels',
    exportPage:
      'Pagina {{page}} van {{pages}} · {{orders}} bestellingen · {{rows}} productregels',
  },
  suppliersPage: {
    title: 'Leveranciers',
    subtitle: 'Contacten en catalogi voor de bestellingen van deze vestiging',
    create: 'Nieuwe leverancier',
    active: 'Actief',
    archived: 'Gearchiveerd',
    search: 'Zoek leverancier, e-mail of btw-nummer…',
    noBranch: 'Selecteer een vestiging.',
    loadError: 'Leveranciers kunnen niet worden geladen.',
    supplier: 'Leverancier',
    contacts: 'Contacten',
    catalog: 'Catalogus',
    openCatalog: 'Catalogus openen',
    productCount: '{{count}} producten',
    vatLine: 'btw {{vat}}',
    editNamed: '{{name}} bewerken',
    archiveNamed: '{{name}} archiveren',
    restoreNamed: '{{name}} herstellen',
    empty: 'Geen leveranciers gevonden.',
    archiveTitle: 'Leverancier archiveren',
    restoreTitle: 'Leverancier herstellen',
    archiveAction: 'Archiveren',
    restoreAction: 'Herstellen',
    archiveDesc:
      '{{name}} archiveren? De catalogus en de bestelgeschiedenis blijven bewaard. De leverancier kan pas weer voor nieuwe bestellingen worden gekozen na herstel.',
    restoreDesc: '{{name}} terugzetten bij de actieve leveranciers?',
    saveError:
      'De leverancier kan niet worden opgeslagen. Probeer het opnieuw.',
    archiveError:
      'Het archief van de leverancier kan niet worden bijgewerkt. Probeer het opnieuw.',
    editTitle: 'Leverancier bewerken',
    groupContacts: 'Gegevens en contacten',
    groupFiscal: 'Fiscale gegevens',
    groupAddress: 'Adres',
    legalName: 'Bedrijfsnaam',
    email: 'E-mail',
    phone: 'Telefoon',
    pec: 'Gecertificeerde e-mail',
    vat: 'btw-nummer',
    taxCode: 'Fiscale code',
    street: 'Adres',
    city: 'Plaats',
    postcode: 'Postcode',
    country: 'Land',
    fromInvoice: 'Van factuur',
    invoiceTitle: 'Leverancier van factuur',
    createSupplier: 'Leverancier maken',
    invoicePhoto: 'Factuurfoto',
    photosSelected: '{{count}} foto’s geselecteerd',
    removePhotos: 'Foto’s verwijderen',
    readingPhotos: 'Foto’s lezen…',
    tooManyPhotos:
      'Selecteer maximaal 5 JPG- of PNG-afbeeldingen van 5 MB elk.',
    invalidImage: 'Ongeldige afbeelding.',
    imageReadError: 'De foto kan niet worden gelezen.',
    photosReadError:
      'De foto’s kunnen niet worden gelezen. Probeer het opnieuw.',
    analyze: 'Analyseren',
    existing: 'Bestaande leveranciers',
    analyzeError: 'De factuur kan niet worden geanalyseerd.',
    createError: 'De leverancier kan niet worden gemaakt.',
  },
  menu: {
    ingredientsPage: {
      title: 'Ingrediënten',
      subtitle: 'Keuze-ingrediënten, categorieën en supplementen',
      search: 'Ingrediënt zoeken…',
      createIngredient: 'Ingrediënt maken',
      createCategory: 'Categorie maken',
      ingredients: 'Ingrediënten',
      categories: 'Categorieën',
      loadError: 'De catalogus kan niet worden geladen.',
      operationError: 'De bewerking is mislukt. Probeer het opnieuw.',
      filterCategory: 'Categorie filteren',
      allCategories: 'Alle categorieën',
      pageHint:
        'Categorieën groeperen de ingrediënten op deze pagina. Een categorie kan doorlopen op de volgende pagina.',
      available: 'Beschikbaar',
      unavailable: 'Uitgeschakeld',
      editNamed: '{{name}} bewerken',
      deleteNamed: '{{name}} verwijderen',
      emptyCategory: 'Geen ingrediënten in deze categorie.',
      emptyPage:
        'Geen ingrediënten op deze pagina. Wijzig de zoekopdracht of ga terug naar de vorige pagina.',
      perPage: 'Ingrediënten per pagina',
      createCategoryFirst: 'Maak een categorie om te beginnen.',
      categoryOrder:
        'Catalogusvolgorde. Elk recept kan de volgorde van de eigen categorieën aanpassen.',
      moveUp: '{{name}} omhoog',
      moveDown: '{{name}} omlaag',
      newIngredient: 'Nieuw ingrediënt',
      editIngredient: 'Ingrediënt bewerken',
      newCategory: 'Nieuwe categorie',
      editCategory: 'Categorie bewerken',
      ingredientDesc: 'Naam, categorie en supplement.',
      categoryDesc: 'Naam en vertalingen van de categorie.',
      name: 'Naam *',
      category: 'Categorie *',
      select: 'Selecteren',
      supplement: 'Supplement € *',
      description: 'Beschrijving',
      translations: 'Vertalingen',
      english: 'English',
      spanish: 'Español',
      french: 'Français',
      deleteTitle: 'Verwijderen uit catalogus',
      deleteDesc:
        '“{{name}}” verwijderen? Ingrediënten die aan gerechten zijn gekoppeld en categorieën die niet leeg zijn, zijn beschermd.',
      delete: 'Verwijderen',
    },
    recipeEditor: {
      createTitle: 'Recept aanmaken',
      tabsLabel: 'Recept en vertalingen',
      configurationTab: 'Recept',
      basePrice: 'Basisprijs',
      dishName: 'Naam van het gerecht',
      typeTitle: 'Type gerecht',
      simpleTitle: 'Eenvoudig recept',
      simpleDesc: 'Naam, beschrijving en allergenen',
      customizableTitle: 'Zelf samen te stellen recept',
      customizableDesc: 'Basis + ingrediënten naar keuze',
      compositeTitle: 'Samengesteld menu',
      compositeDesc: 'Eenvoudige gerechten voor een vaste prijs',
      wineTitle: 'Wijn / Champagne',
      wineDesc: 'Fles, glas en kenmerken',
      includedDishes: 'Inbegrepen gerechten',
      searchDishes: 'Gerecht zoeken…',
      includedSections: 'Sectie van de inbegrepen gerechten',
      allSections: 'Alle secties',
      selectionHint:
        '{{count}} gerechten geselecteerd. Selecties blijven behouden bij het wisselen van pagina of zoekopdracht.',
      cannotInclude: '{{reason}}: kan niet worden opgenomen',
      weightedDish: 'Prijs op basis van gewicht',
      noDishes: 'Geen gerechten gevonden.',
      dishesPerPage: 'Gerechten per pagina',
      range: '{{from}}–{{to}} van {{total}}',
      previous: 'Vorige',
      next: 'Volgende',
      minTwo: 'Selecteer minimaal twee eenvoudige gerechten.',
      selectableIngredients: 'Te kiezen ingrediënten',
      minimumTotal: 'Totaal minimum',
      maximumTotal: 'Totaal maximum',
      searchIngredients: 'Ingrediënt zoeken…',
      ingredientLoadError: 'Ingrediënten laden mislukt. Opnieuw proberen',
      disabled: 'Uitgeschakeld',
      ingredientPage: '{{total}} ingrediënten · pagina {{page}}',
      categoryFilter: 'Categorie',
      allCategories: 'Alle categorieën',
      noIngredients: 'Geen ingrediënten gevonden.',
      categoryOrderHelp:
        'Gebruik de pijlen om de categorieën te ordenen die de klant ziet bij het samenstellen. Alleen categorieën met geselecteerde ingrediënten verschijnen. De volgorde verandert geen ingrediënten of prijzen.',
      categoryOrder: 'Volgorde van categorieën in het recept',
      moveEarlier: '{{name}} eerder plaatsen',
      moveLater: '{{name}} later plaatsen',
      availability: 'Beschikbaarheid en prijzen',
      restaurantMenu: 'Restaurantmenu',
      takeaway: 'Afhalen',
      delivery: 'Bezorgen',
      enabled: 'Ingeschakeld',
      channelPrice: 'Prijs voor {{channel}} (€)',
      invalidChoices:
        'Selecteer ingrediënten en controleer het minimale en maximale aantal keuzes.',
      invalidPrices: 'Ongeldige prijzen: gebruik maximaal twee decimalen.',
      invalidComposition:
        'Zelf samen te stellen recepten en samengestelde menu’s vereisen een vaste prijs en kunnen geen wijnen zijn.',
      invalidForm: 'Controleer de naam, prijs en verplichte velden.',
      numericError: 'Voer een geldig getal in.',
      nonNegativeError: 'De waarde moet nul of hoger zijn.',
      wineRed: 'Rood',
      wineWhite: 'Wit',
      wineRose: 'Rosé',
      wineChampagne: 'Champagne',
      wineSparkling: 'Mousserend',
      wineOther: 'Overig',
    },
    title: 'Menu',
    disabled: {
      badge: 'Sectie niet ingeschakeld',
      title: 'Deze sectie is niet ingeschakeld',
      description:
        'Deze functie is momenteel uitgeschakeld voor je account. Neem contact op met support voor meer informatie.',
      infoTitle: 'Toegang tot deze sectie nodig?',
      infoDescription:
        'Neem contact op met ons supportteam en we helpen je op weg.',
      footerHelp: 'Nog hulp nodig?',
      footerHelpDesc: 'Ons supportteam is 24/7 beschikbaar om je te helpen.',
    },
    subtitleDefault: 'Bekijk en beheer je digitale menu’s',
    statsCount: '{{count}} menu',
    searchPlaceholder: 'Zoek menu’s op naam...',
    menuName: 'Menunaam',
    menuNamePlaceholder: 'Voer de menunaam in',
    description: 'Beschrijving',
    descriptionPlaceholder: 'Voer de menubeschrijving in',
    newMenu: 'Nieuw menu',
    createTitle: 'Nieuw menu aanmaken',
    createAction: 'Aanmaken',
    url: 'URL',
    qrCode: 'QR-code',
    preview: 'Voorbeeld',
    copyUrl: 'URL kopiëren',
    editMenu: 'Menu bewerken',
    deleteMenu: 'Menu verwijderen',
    duplicate: 'Dupliceren',
    close: 'Sluiten',
    saveChanges: 'Opslaan',
    noBranch: 'Selecteer een vestiging om menu’s te bekijken',
    empty: 'Geen menu’s gevonden',
    emptyDesc: 'Er zijn nog geen menu’s voor deze vestiging.',
    validation: {
      nameRequired: 'Menunaam is verplicht',
      nameMax: 'Menunaam mag maximaal 100 tekens bevatten',
      descriptionMax: 'Beschrijving mag maximaal 500 tekens bevatten',
    },
    deleteModal: {
      title: 'Menu verwijderen?',
      description:
        'Weet je zeker dat je dit menu wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
    },
    toast: {
      createSuccess: 'Menu succesvol aangemaakt.',
      createError: 'Menu aanmaken mislukt. Probeer het opnieuw.',
      updateSuccess: 'Menu succesvol bijgewerkt.',
      updateError: 'Menu bijwerken mislukt. Probeer het opnieuw.',
      deleteSuccess: 'Menu succesvol verwijderd.',
      deleteError: 'Menu verwijderen mislukt. Probeer het opnieuw.',
      duplicateSuccess: 'Menu succesvol gedupliceerd.',
      duplicateError: 'Menu dupliceren mislukt. Probeer het opnieuw.',
      logoUploadSuccess: 'Logo succesvol geüpload.',
      logoUploadError: 'Logo uploaden mislukt.',
      logoRemoveSuccess: 'Logo succesvol verwijderd.',
      logoRemoveError: 'Logo verwijderen mislukt.',
      backgroundUploadSuccess: 'Achtergrond succesvol geüpload.',
      backgroundUploadError: 'Achtergrond uploaden mislukt.',
      backgroundRemoveSuccess: 'Achtergrond succesvol verwijderd.',
      backgroundRemoveError: 'Achtergrond verwijderen mislukt.',
      templateUpdateSuccess: 'Sjabloon succesvol bijgewerkt.',
      templateUpdateError: 'Sjabloon bijwerken mislukt.',
    },
    edit: {
      pageTitle: 'Menudetails bewerken',
      subtitle: 'Configureer menu-inhoud, stijl en secties.',
      tabListLabel: 'Menu-bewerkingstabbladen',
      withPhoto: 'met foto',
      withoutPhoto: 'zonder foto',
      uploading: 'Uploaden...',
      removing: 'Verwijderen...',
      tabs: {
        titleDescription: 'Titel en beschrijving',
        menuStyle: 'Menustijl',
        sectionsRecipes: 'Secties en recepten',
      },
      informationTitle: 'Menu-informatie',
      informationDesc: 'Configureer menunaam en vertalingen.',
      languages: {
        italian: 'Italiaans',
        italianMain: 'Hoofdtaal',
        english: 'Engels',
        englishTranslation: 'Engelse vertaling',
        spanish: 'Spaans',
        spanishTranslation: 'Spaanse vertaling',
        french: 'Frans',
        frenchTranslation: 'Franse vertaling',
      },
      descriptions: {
        italian: 'Menubeschrijving',
        english: 'Engelse beschrijving',
        spanish: 'Spaanse beschrijving',
        french: 'Franse beschrijving',
      },
      style: {
        appearanceTitle: 'Menu-uiterlijk',
        appearanceDesc:
          'Upload een logo en achtergrondafbeelding voor je menu.',
        logo: 'Selecteer menulogo',
        logoHint: 'We raden een vierkant logo aan (PNG), max. 5 MB.',
        background: 'Selecteer menu-achtergrondafbeelding',
        backgroundHint: 'We raden een horizontale afbeelding aan, max. 5 MB.',
        removeImage: 'Afbeelding verwijderen',
        templatesTitle: 'Selecteer stijl om toe te passen op het menu',
        templatesDesc: 'Kies een sjabloon voor de presentatie van je menu.',
        previewStyleMenu: 'Voorbeeld van menustijl',
        select: 'Selecteren',
        selected: 'Geselecteerd',
        active: 'Actief',
      },
    },
    sections: {
      title: 'Secties en recepten',
      description: 'Organiseer je menu in secties en voeg recepten toe.',
      addSection: 'Nieuwe sectie maken',
      searchPlaceholder:
        'Zoek secties of recepten op naam, beschrijving of code...',
      noSearchResults: 'Geen overeenkomende secties of recepten',
      name: 'Sectienaam',
      namePlaceholder: 'Voer de sectienaam in',
      descriptionPlaceholder: 'Voer de sectiebeschrijving in',
      createTitle: 'Sectie aanmaken op menu {{menu}}',
      createAction: 'Aanmaken',
      editTitle: 'Menu-object bewerken',
      editDesc: 'Beheer inhoud, vertalingen en ingrediënten',
      dishDrink: 'Gerecht/drank',
      missingTranslations: 'Ontbrekende vertalingen',
      missingTranslationsTooltip:
        'Naam of beschrijving in deze sectie is niet in alle talen van het menu vertaald; bij een taalwijziging wordt de Italiaanse versie getoond',
      addRecipe: 'Recept/product toevoegen aan {{section}}',
      sortAlphabetically: 'Recepten sorteren A-Z',
      moveLeft: 'Sectie naar links verplaatsen',
      moveRight: 'Sectie naar rechts verplaatsen',
      settings: 'Sectie-instellingen',
      delete: 'Sectie verwijderen',
      menuPrice: 'Menuprijs',
      sectionVisibility: 'Sectie zichtbaar',
      sectionVisibilityHint:
        'Toon deze sectie in het restaurant-, bezorg- en afhaalmenu.',
      sectionHidden: 'Sectie verborgen',
      hideSectionPrices: 'Sectieprijzen verbergen',
      showSectionPrices: 'Sectieprijzen tonen',
      visibleSection: 'Zichtbare sectie',
      visibility: 'Sectie tonen / verbergen',
      fixedMenuPrice: 'MENU FISSO ({{price}}€)',
      empty: 'Nog geen secties',
      emptyDesc: 'Gebruik de plusknop om je eerste sectie aan te maken.',
      emptyRecipes: 'Nog geen recepten toegevoegd aan deze sectie.',
      recipeCount: '{{count}} recepten in deze sectie',
      deleteModal: {
        title: 'Sectie verwijderen?',
        description:
          'Weet je zeker dat je deze sectie wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
      },
      validation: {
        nameRequired: 'Sectienaam is verplicht',
        nameMax: 'Sectienaam mag maximaal 100 tekens bevatten',
        descriptionMax: 'Beschrijving mag maximaal 500 tekens bevatten',
      },
      toast: {
        createSuccess: 'Sectie succesvol aangemaakt.',
        createError: 'Sectie aanmaken mislukt. Probeer het opnieuw.',
        updateSuccess: 'Sectie succesvol bijgewerkt.',
        updateError: 'Sectie bijwerken mislukt. Probeer het opnieuw.',
        deleteSuccess: 'Sectie succesvol verwijderd.',
        deleteError: 'Sectie verwijderen mislukt. Probeer het opnieuw.',
        sortSuccess: 'Recepten alfabetisch gesorteerd.',
        sortError: 'Recepten sorteren mislukt. Probeer het opnieuw.',
      },
    },
    recipes: {
      editTitle: 'Menu-object bewerken',
      editDesc: 'Beheer inhoud, vertalingen en ingrediënten',
      imageTitle: 'Afbeelding',
      productPhoto: 'Productfoto',
      winePhoto: 'Wijnfoto',
      wineChampagne: 'Wijn/champagne',
      imageHintCreate:
        'Selecteer een afbeelding om te uploaden. Je kunt de receptnaam toevoegen vóór het opslaan.',
      generalTitle: 'Algemene informatie',
      generalDesc: 'Naam, beschrijving en configuratie',
      priceSection: 'Prijs',
      priceHint: 'Voer de eindprijs van het product in',
      price: 'Prijs €',
      bottlePrice: 'Flesprijs',
      priceBottleLabel: 'Prijs fles (€)',
      chalice: 'Glas',
      pricePerGlass: 'Prijs per glas',
      alcoholPercentage: 'Alcohol (%) - Bijv.: 14',
      wineType: 'Soort wijn',
      wineTypePlaceholder: 'Soort wijn (bijv. rood, wit, rosé, champagne...)',
      blend: 'Assemblage',
      blendPlaceholder:
        'Assemblage (bijv. 50% Negroamaro, 20% Nero di Troia, 30% Malvasia Nera)',
      yearOfProduction: 'Productiejaar',
      cellar: 'Wijnhuis',
      countryRegion: 'Land/regio',
      provenance: 'Herkomst',
      pricePerWeight:
        'Is de prijs aangegeven per gewichtseenheid (bijv. kg, liter) in plaats van per portie?',
      unitOfMeasurement: 'Meeteenheid',
      unitPlaceholder: 'Meeteenheid (bijv.: Kg, Lt, St)',
      assignAllergens: 'Allergenen toewijzen',
      productConfiguration: 'Productconfiguratie',
      allergensTitle: 'Allergenen',
      bestSeller: 'Bestseller',
      bestSellerDesc:
        'Markeer dit product als een van de meest bestelde en maak het visueel prominenter op het menu.',
      available: 'Beschikbaar',
      availableDesc:
        'Toon dit recept aan klanten op je digitale menu en maak het bestelbaar.',
      notAvailable: 'Niet beschikbaar',
      active: 'Actief',
      visible: 'Zichtbaar',
      delete: 'Recept/product verwijderen',
      dragHandle: 'Sleep om het recept te herschikken',
      deleteModal: {
        title: 'Recept verwijderen?',
        description:
          'Weet je zeker dat je dit recept wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
      },
      validation: {
        nameRequired: 'Receptnaam is verplicht',
      },
      toast: {
        createSuccess: 'Recept succesvol aangemaakt.',
        createError: 'Recept aanmaken mislukt. Probeer het opnieuw.',
        updateSuccess: 'Recept succesvol bijgewerkt.',
        updateError: 'Recept bijwerken mislukt. Probeer het opnieuw.',
        deleteSuccess: 'Recept succesvol verwijderd.',
        deleteError: 'Recept verwijderen mislukt. Probeer het opnieuw.',
        moveSuccess: 'Receptvolgorde bijgewerkt.',
        moveError: 'Receptvolgorde bijwerken mislukt. Probeer het opnieuw.',
        saveBeforeImage:
          'Sla het recept op vóór het uploaden van een afbeelding.',
        imageUploadSuccess: 'Receptafbeelding succesvol geüpload.',
        imageUploadError: 'Receptafbeelding uploaden mislukt.',
        imageRemoveSuccess: 'Receptafbeelding succesvol verwijderd.',
        imageRemoveError: 'Receptafbeelding verwijderen mislukt.',
      },
    },
    allergens: {
      cereals: 'Granen met gluten',
      gluten: 'Gluten',
      crustaceans: 'Schaaldieren',
      molluscs: 'Weekdieren',
      fish: 'Vis',
      eggs: 'Eieren',
      peanuts: 'Pinda’s',
      soybean: 'Soja',
      mushrooms: 'Paddenstoelen',
      nuts: 'Boomnoten',
      celery: 'Selderij',
      mustard: 'Mosterd',
      sesame: 'Sesam',
      sulfur: 'Zwaveldioxide',
      lupins: 'Lupine',
      lactose: 'Lactose',
      corn: 'Maïs',
      pork: 'Varkensvlees',
      beef: 'Rundvlees',
      poultry: 'Gevogelte',
      nickel: 'Nikkel',
      legumes: 'Peulvruchten',
      citrus: 'Citrusvruchten',
      cacao: 'Cacao',
      honey: 'Honing',
    },
    translate: {
      action: 'Vertalen',
      progressTitle: 'Vertaling bezig...',
      progressDesc: 'Wacht tot de vertalingen zijn voltooid.',
      translationsTitle: 'Vertalingen',
      success: 'Vertalingen succesvol toegepast.',
      error: 'Vertalen mislukt. Probeer het opnieuw.',
    },
    previewPage: {
      pageTitle: 'Menuvoorbeeld',
      loading: 'Menu laden',
      menuLabel: 'Menu',
      sectionLabel: 'Sectie',
      priceLabel: 'Prijs',
      noPhoto: 'Geen foto beschikbaar',
      bestSeller: 'Best',
      allergensTitle: 'Allergenen',
      poweredBy: 'Mogelijk gemaakt door PRO20',
      scrollCue: 'Scroll naar menu',
      storyContinue: 'Menu',
      close: 'Sluiten',
      wine: {
        grape: 'Druivensoort',
        cellar: 'Wijnhuis',
        year: 'Jaar',
        region: 'Regio',
        alcohol: 'Alcohol',
        glass: 'Glas',
        bottle: 'Fles',
      },
    },
  },
  landingPages: {
    title: 'Landingspagina’s',
    subtitle: 'Landingspagina-lijst voor {{branch}}',
    subtitleDefault: 'Bekijk en beheer de lijst met landingspagina’s',
    searchPlaceholder: 'Zoek landingspagina’s op naam',
    searchPlaceholder2: 'Voer de naam van de landingspagina in',
    landingPageName: 'Naam van de landingspagina',
    itemsPerPage: 'Items per pagina',
    range: '{{start}} - {{end}} van {{total}}',
    noBranch: 'Selecteer een vestiging om landingspagina’s te bekijken',
    empty: 'Geen landingspagina’s gevonden',
    emptyDesc: 'Er zijn nog geen landingspagina’s voor deze vestiging.',
    addLandingPage: 'Landingspagina toevoegen',
    newLanding: 'Nieuwe landingspagina',
    newLandingDesc: 'Maak een nieuwe landingspagina voor uw vestiging.',
    createLanding: 'Landingspagina aanmaken',
    statsCount: '{{count}} landingspagina’s',
    present: 'aanwezig',
    url: 'URL',
    qrCode: 'QR-code',
    preview: 'Voorbeeld',
    showPreview: 'Voorbeeld tonen',
    copyUrl: 'URL kopiëren',
    editLanding: 'Landingspagina bewerken',
    deleteLanding: 'Landingspagina verwijderen',
    duplicate: 'Dupliceren',
    validation: {
      landingPageNameMin: 'De naam moet minimaal 2 tekens bevatten',
      landingPageNameMax: 'De naam mag niet langer zijn dan 50 tekens',
    },
    deleteModal: {
      title: 'Landingspagina verwijderen?',
      description:
        'Weet u zeker dat u deze landingspagina wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
    },
    toast: {
      createSuccess: 'Landingspagina succesvol aangemaakt.',
      createError: 'Kan landingspagina niet aanmaken. Probeer het opnieuw.',
      deleteSuccess: 'Landingspagina succesvol verwijderd.',
      deleteError: 'Kan landingspagina niet verwijderen. Probeer het opnieuw.',
      duplicateSuccess: 'Landingspagina succesvol gedupliceerd.',
      duplicateError:
        'Kan landingspagina niet dupliceren. Probeer het opnieuw.',
      updateSuccess: 'Landingspagina succesvol bijgewerkt.',
      updateError: 'Kan landingspagina niet bijwerken. Probeer het opnieuw.',
      logoUploadSuccess: 'Logo succesvol geüpload.',
      logoUploadError: 'Kan logo niet uploaden.',
      logoRemoveSuccess: 'Logo succesvol verwijderd.',
      logoRemoveError: 'Kan logo niet verwijderen.',
      backgroundUploadSuccess: 'Achtergrond succesvol geüpload.',
      backgroundUploadError: 'Kan achtergrond niet uploaden.',
      backgroundRemoveSuccess: 'Achtergrond succesvol verwijderd.',
      backgroundRemoveError: 'Kan achtergrond niet verwijderen.',
      buttonSaveSuccess: 'Knop succesvol opgeslagen.',
      buttonSaveError: 'Kan knop niet opslaan.',
      buttonDeleteSuccess: 'Knop succesvol verwijderd.',
      buttonDeleteError: 'Kan knop niet verwijderen.',
    },
    edit: {
      pageTitle: 'Landingspagina bewerken',
      subtitle: 'Configureer media en actieknoppen.',
      formConfiguration: 'Landingspagina-configuratie',
      formConfigurationDesc: 'Configureer media en actieknoppen.',
      linkForm: 'Landingspagina-link',
      close: 'Sluiten',
      mediaTitle: 'Media',
      mediaDescription:
        'Upload een logo en achtergrondafbeelding voor uw landingspagina.',
      detailsTitle: 'Details',
      logo: 'Logo',
      background: 'Achtergrondafbeelding',
      imageUpload: 'Klik of sleep om een afbeelding te uploaden',
      removeImage: 'Afbeelding verwijderen',
      uploading: 'Uploaden...',
      removing: 'Verwijderen...',
      buttons: {
        title: 'Landingsknoppen',
        description:
          'Voeg actieknoppen toe op uw landingspagina. Gebruik het vinkje om elke rij op te slaan.',
        text: 'Knoptekst',
        link: 'Link',
        color: 'Achtergrond',
        textColor: 'Tekstkleur',
        add: 'Knop toevoegen',
        save: 'Knop opslaan',
        delete: 'Knop verwijderen',
        empty: 'Nog geen knoppen. Klik op + om er een toe te voegen.',
        textPlaceholder: 'bijv. Nu boeken',
        linkPlaceholder: 'https://',
        validationRequired: 'Knoptekst en link zijn verplicht.',
      },
    },
    public: {
      documentTitle: 'Landingspagina',
      loading: 'Landingspagina laden...',
      unavailableTitle: 'Landingspagina niet beschikbaar',
      unavailableDesc:
        'Deze landingspagina is niet gevonden of is niet meer beschikbaar.',
      buttonFallback: 'Openen',
    },
  },
  tags: {
    title: 'Tags',
    titleWithBranch: 'Tags {{branch}}',
    pageDescription: 'Geen tags aanwezig',
    subtitle: 'Taglijst voor {{branch}}',
    subtitleDefault: 'Bekijk en beheer uw taglijst',
    searchPlaceholder: 'Zoek tags op naam',
    searchPlaceholder2: 'Voer tagnaam in',
    tagName: 'Tagnaam',
    itemsPerPage: 'Items per pagina',
    range: '{{start}} - {{end}} van {{total}}',
    noBranch: 'Selecteer een vestiging om tags te bekijken',
    empty: 'Geen tags aanwezig',
    emptyDesc:
      'Maak de eerste tag om klanten, boekingen en marketingsegmentaties beter te organiseren.',
    addTag: 'Tag toevoegen',
    createTitle: 'Tag toevoegen',
    createDesc: 'Maak een nieuwe tag voor uw vestiging.',
    editTitle: 'Tag bewerken',
    editDesc: 'Werk de tagnaam bij.',
    editTag: 'Tag bewerken',
    duplicate: 'Dupliceren',
    typeLabel: 'TAG',
    statsCount: '{{count}} tags aanwezig',
    updatedAgo: '2 minuten geleden bijgewerkt',
    creating: 'Aanmaken...',
    saving: 'Opslaan...',
    saveChanges: 'Wijzigingen opslaan',
    toast: {
      createSuccess: 'Tag succesvol aangemaakt.',
      createError: 'Tag aanmaken mislukt. Probeer het opnieuw.',
      updateSuccess: 'Tag succesvol bijgewerkt.',
      updateError: 'Tag bijwerken mislukt. Probeer het opnieuw.',
      deleteSuccess: 'Tag succesvol verwijderd.',
      deleteError: 'Tag verwijderen mislukt. Probeer het opnieuw.',
      duplicateSuccess: 'Tag succesvol gedupliceerd.',
      duplicateError: 'Tag dupliceren mislukt. Probeer het opnieuw.',
    },
    validation: {
      tagNameMin: 'Tagnaam moet minimaal 2 tekens bevatten',
      tagNameMax: 'Tagnaam mag niet langer zijn dan 50 tekens',
    },
    deleteTag: 'Tag verwijderen',
    deleteModel: {
      deleteTitle: 'Tag verwijderen?',
      deleteDescription:
        'Weet u zeker dat u deze tag wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
    },
  },
  publicRelations: {
    title: 'Public Relations',
    titleWithBranch: 'PR {{branch}}',
    pageDescription: 'Geen PR aanwezig',
    subtitle: 'PR-lijst voor {{branch}}',
    subtitleDefault: 'Bekijk en beheer uw PR-lijst',
    searchPlaceholder: 'Zoek PR op naam',
    searchPlaceholder2: 'Voer PR-naam in',
    prName: 'PR-naam',
    itemsPerPage: 'Items per pagina',
    range: '{{start}} - {{end}} van {{total}}',
    noBranch: 'Selecteer een vestiging om PR te bekijken',
    empty: 'Geen PR aanwezig',
    emptyDesc:
      'Maak de eerste PR om referrals, samenwerkingen en klantbeheer te organiseren.',
    addPr: 'PR toevoegen',
    createTitle: 'PR toevoegen',
    createDesc: 'Maak een nieuw public relations-contact voor uw vestiging.',
    editTitle: 'PR bewerken',
    editDesc: 'Werk de naam van het public relations-contact bij.',
    editPr: 'PR bewerken',
    duplicate: 'Dupliceren',
    typeLabel: 'PR MANAGER',
    statsCount: '{{count}} PR aanwezig',
    updatedAgo: '2 minuten geleden bijgewerkt',
    creating: 'Aanmaken...',
    saving: 'Opslaan...',
    saveChanges: 'Wijzigingen opslaan',
    toast: {
      createSuccess: 'PR succesvol aangemaakt.',
      createError: 'PR aanmaken mislukt. Probeer het opnieuw.',
      updateSuccess: 'PR succesvol bijgewerkt.',
      updateError: 'PR bijwerken mislukt. Probeer het opnieuw.',
      deleteSuccess: 'PR succesvol verwijderd.',
      deleteError: 'PR verwijderen mislukt. Probeer het opnieuw.',
      duplicateSuccess: 'PR succesvol gedupliceerd.',
      duplicateError: 'PR dupliceren mislukt. Probeer het opnieuw.',
    },
    validation: {
      prNameMin: 'PR-naam moet minimaal 2 tekens bevatten',
      prNameMax: 'PR-naam mag niet langer zijn dan 50 tekens',
    },
    deletePR: 'PR verwijderen',
    deleteModel: {
      deleteTitle: 'PR verwijderen?',
      deleteDescription:
        'Weet u zeker dat u deze PR wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
    },
  },
  staff: {
    collaborators: {
      title: 'Medewerkers',
      subtitle: 'Medewerkers voor {{branch}}',
      subtitleDefault: 'Bekijk en beheer de medewerkerslijst',
      searchPlaceholder:
        'Zoek medewerkers op voornaam, achternaam, e-mail, telefoonnummer of rol',
      firstName: 'Voornaam',
      lastName: 'Achternaam',
      firstNamePlaceholder: 'Voer voornaam in',
      lastNamePlaceholder: 'Voer achternaam in',
      email: 'E-mail',
      phone: 'Telefoonnummer',
      role: 'Rol',
      itemsPerPage: 'Items per pagina',
      viewDetails: 'Details Bekijken',
      sharedCredentials: 'Gedeelde Inloggegevens',
      addCollaborator: 'Medewerker Toevoegen',
      editCollaborator: 'Medewerker Bewerken',
      deleteCollaborator: 'Medewerker Verwijderen',
      changePassword: {
        action: 'Wachtwoord wijzigen',
        title: 'Wachtwoord wijzigen',
        subtitle: 'Stel een nieuw wachtwoord in voor {{name}}',
        newPassword: 'Nieuw wachtwoord',
        newPasswordPlaceholder: 'Voer 6-cijferig wachtwoord in',
        confirmPassword: 'Bevestig wachtwoord',
        confirmPasswordPlaceholder: 'Bevestig 6-cijferig wachtwoord',
        submit: 'Wachtwoord opslaan',
        validation: {
          passwordDigits: 'Wachtwoord moet precies 6 cijfers bevatten',
          passwordMismatch: 'Wachtwoorden komen niet overeen',
        },
      },
      range: '{{start}} - {{end}} van {{total}}',
      noBranch: 'Selecteer een vestiging om medewerkers te bekijken',
      empty: 'Geen medewerkers gevonden',
      emptyDesc: 'Er zijn nog geen medewerkers voor deze vestiging.',
      emailPlaceholder: 'Voer e-mail in',
      phonePlaceholder: 'Voer telefoonnummer in',
      username: 'Gebruikersnaam',
      usernamePlaceholder: 'Voer gebruikersnaam in',
      password: 'Wachtwoord',
      passwordPlaceholder: 'Voer wachtwoord in',
      gender: 'Geslacht',
      selectGender: 'Selecteer geslacht',
      male: 'Man',
      female: 'Vrouw',
      selectRole: 'Selecteer rol',
      remunerationType: 'Vergoedingstype',
      selectRemunerationType: 'Selecteer vergoedingstype',
      hourly: 'Uurloon',
      daily: 'Dagloon',
      monthly: 'Maandsalaris',
      sendCredentials: 'Stuur inloggegevens',
      roles: {
        receptionist: 'Receptionist',
        assistantCook: 'Kok assistent',
        administrator: 'Beheerder',
        bartender: 'Barman',
        chef: 'Chef-kok',
        dishwasher: 'Afwasser',
        pizzaChef: 'Pizzabakker',
        director: 'Directeur',
        waiter: 'Ober',
        publicRelations: 'Public relations',
        deputyDirector: 'Adjunct-directeur',
      },
      validation: {
        firstNameMin: 'Voornaam moet minimaal 2 tekens bevatten',
        lastNameMin: 'Achternaam moet minimaal 2 tekens bevatten',
        firstNameMax: 'Voornaam mag niet langer zijn dan 40 tekens',
        lastNameMax: 'Achternaam mag niet langer zijn dan 40 tekens',
        emailInvalid: 'Voer een geldig e-mailadres in',
        phoneInvalid: 'Telefoonnummer moet 6 tot 15 cijfers bevatten',
        countryCodeInvalid: 'Selecteer een geldige landcode',
        usernameMin: 'Gebruikersnaam moet minimaal 4 tekens bevatten',
        usernameMax: 'Gebruikersnaam mag niet langer zijn dan 30 tekens',
        passwordMin: 'Wachtwoord moet minimaal 8 tekens bevatten',
        passwordDigits: 'Wachtwoord moet precies 6 cijfers bevatten',
        genderRequired: 'Selecteer een geslacht',
        roleRequired: 'Selecteer een rol',
        remunerationRequired: 'Selecteer een vergoedingstype',
      },
      deleteModel: {
        deleteTitle: 'Medewerker verwijderen?',
        deleteDescription:
          'Weet u zeker dat u deze medewerker wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
      },
      collaboratorDetails: {
        title: 'Medewerkerdetails',
        subtitle: 'Bekijk medewerkerinformatie en arbeidsdetails.',
        personalDetails: 'Persoonlijke Gegevens',
        employmentDetails: 'Arbeidsgegevens',
        jobDescription: 'Functie',
        dateOfBirth: 'Geboortedatum',
        branchCode: 'Vestigingscode',
        contractType: 'Contracttype',
        startDateInduction: 'Startdatum Inductie',
        endDateInduction: 'Einddatum Inductie',
        mobileDeviceAccessDetail: 'Mobiel Apparaattoegang Detail',
        uniqueId: 'Unieke ID',
        platform: 'Platform',
        model: 'Model',
        machine: 'Machine',
        release: 'Release',
        fcmToken: 'FCM-token',
        registrationDate: 'Registratiedatum',
        noMobileDevices: 'Geen mobiele apparaten geregistreerd.',
        close: 'Sluiten',
        contractTypes: {
          determined: 'Bepaald',
          indeterminate: 'Onbepaald',
        },
        visible: 'Zichtbaar',
        fired: 'Ontslagen',
        retribution: 'Vergoeding',
        yes: 'Ja',
        no: 'Nee',
      },
      toast: {
        createSuccess: 'Medewerker succesvol aangemaakt.',
        createError: 'Kan de medewerker niet aanmaken. Probeer het opnieuw.',
        updateSuccess: 'Medewerker succesvol bijgewerkt.',
        updateError: 'Kan de medewerker niet bijwerken. Probeer het opnieuw.',
        deleteSuccess: 'Medewerker succesvol verwijderd.',
        deleteError: 'Kan de medewerker niet verwijderen. Probeer het opnieuw.',
        sendCredentialsSoon:
          'Het verzenden van inloggegevens is binnenkort beschikbaar.',
        sendCredentialsSuccess: 'Inloggegevens succesvol verzonden.',
        sendCredentialsError:
          'Verzenden van inloggegevens mislukt. Probeer het opnieuw.',
        sendCredentialsMissingPhone:
          'Deze medewerker heeft geen geldig telefoonnummer.',
        sendCredentialsMissingCredentials:
          'Gebruikersnaam of wachtwoord ontbreekt voor deze medewerker.',
        changePasswordSuccess: 'Wachtwoord succesvol bijgewerkt.',
        changePasswordError:
          'Kan het wachtwoord niet bijwerken. Probeer het opnieuw.',
      },
      credentialsMessage:
        'Uw Proventi-inloggegevens:\nUsername: {{username}}\nPassword: {{password}}',
    },
    collaboratorReports: {
      title: 'Medewerkersrapporten',
      titleWithMonth: 'Rapport {{branch}} van {{monthYear}}',
      subtitle: 'Medewerkersrapporten voor {{branch}}',
      subtitleDefault: 'Bekijk en beheer de medewerkersrapporten',
      noReportsPresent: 'Geen rapport aanwezig',
      searchPlaceholder: 'Zoeken op naam',
      name: 'Voornaam',
      lunch: 'Lunch',
      cena: 'Diner',
      illness: 'Ziekte',
      holiday: 'Vakantie',
      free: 'Vrij',
      hoursWorked: 'Gewerkte Uren',
      itemsPerPage: 'Items per pagina',
      range: '{{start}} - {{end}} van {{total}}',
      noBranch: 'Selecteer een vestiging om medewerkersrapporten te bekijken',
      empty: 'Geen medewerkersrapporten gevonden',
      emptyDesc: 'Er zijn nog geen medewerkersrapporten voor deze vestiging.',
      previousMonth: 'Vorige maand',
      nextMonth: 'Volgende maand',
      downloadPdf: 'Download rapport van {{monthYear}}',
      downloadPdfSuccess: 'PDF van medewerkersrapport gedownload.',
      downloadPdfError: 'Downloaden van PDF van medewerkersrapport mislukt.',
      pdfTitle: 'Medewerkersrapporten',
      pdfMeta:
        '{{branch}} · {{monthYear}} · {{count}} medewerkers · Gegenereerd op {{date}}',
    },
  },
  extraServices: {
    title: 'Extra Diensten en Producten',
    subtitle:
      'Beheer diensten, producten en voorzieningen beschikbaar voor {{branchName}}',
    subtitleDefault: 'Bekijk en beheer de lijst met extra diensten',
    statsCount: '{{count}} extra diensten',
    searchPlaceholder: 'Zoek een dienst of product...',
    noBranch: 'Selecteer een vestiging om extra diensten te bekijken',
    empty: 'Geen extra dienst',
    emptyDesc:
      'Je hebt nog geen extra diensten of producten geconfigureerd. Voeg voorzieningen toe om de ervaring van je klanten te verbeteren.',
    itemsPerPage: 'Items per pagina',
    range: '{{start}} - {{end}} van {{total}}',
    columns: {
      service: 'Dienst',
      price: 'Prijs',
      guests: 'Gasten',
      state: 'Status',
    },
    active: 'Actief',
    inactive: 'Inactief',
    createTitle: 'Nieuwe dienst aanmaken',
    createDesc: 'Voeg een nieuwe dienst of product toe voor uw vestiging.',
    editTitle: 'Dienst bewerken',
    editDesc: 'Dienstinformatie bijwerken',
    createService: 'Dienst aanmaken',
    addService: 'Dienst toevoegen',
    creating: 'Aanmaken...',
    saveChanges: 'Wijzigingen opslaan',
    close: 'Sluiten',
    name: 'Naam',
    namePlaceholder: 'Voer de dienstnaam in',
    description: 'Beschrijving',
    descriptionPlaceholder: 'Voer beschrijving in',
    price: 'Prijs (€)',
    pricePlaceholder: '0',
    guests: 'Aantal gasten',
    guestsPlaceholder: '1',
    activeService: 'Actieve dienst',
    serviceImage: 'Dienstafbeelding',
    uploadImage: 'Afbeelding uploaden',
    removeImage: 'Afbeelding verwijderen',
    uploading: 'Uploaden...',
    removing: 'Verwijderen...',
    editService: 'Dienst bewerken',
    duplicate: 'Dupliceren',
    deleteService: 'Dienst verwijderen',
    deleteModal: {
      title: 'Dienst verwijderen?',
      description:
        'Weet u zeker dat u deze dienst wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
    },
    validation: {
      nameRequired: 'Naam is verplicht',
      nameMax: 'Naam mag niet langer zijn dan 120 tekens',
      descriptionMax: 'Beschrijving mag niet langer zijn dan 300 tekens',
      numberInvalid: 'Voer alleen een geldig getal in',
      priceMin: 'Prijs kan niet negatief zijn',
      guestsMin: 'Aantal gasten kan niet negatief zijn',
      guestsInteger: 'Aantal gasten moet een geheel getal zijn',
    },
    toast: {
      createSuccess: 'Dienst succesvol aangemaakt.',
      createError: 'Kan dienst niet aanmaken. Probeer het opnieuw.',
      updateSuccess: 'Dienst succesvol bijgewerkt.',
      updateError: 'Kan dienst niet bijwerken. Probeer het opnieuw.',
      deleteSuccess: 'Dienst succesvol verwijderd.',
      deleteError: 'Kan dienst niet verwijderen. Probeer het opnieuw.',
      duplicateSuccess: 'Dienst succesvol gedupliceerd.',
      duplicateError: 'Kan dienst niet dupliceren. Probeer het opnieuw.',
      uploadSuccess: 'Afbeelding succesvol geüpload.',
      uploadError: 'Kan afbeelding niet uploaden.',
      removeImageSuccess: 'Afbeelding succesvol verwijderd.',
      removeImageError: 'Kan afbeelding niet verwijderen.',
    },
  },
  formMenu: {
    title: 'Form Menu',
    subtitleDefault: 'Bekijk en beheer Form Menus voor je filiaal',
    statsCount: '{{count}} Form Menus',
    searchPlaceholder: 'Zoek een Form Menu...',
    noBranch: 'Selecteer een filiaal om Form Menus te bekijken',
    empty: 'Geen Form Menu',
    emptyDesc:
      'Je hebt nog geen Form Menus geconfigureerd. Voeg er een toe om te beginnen.',
    columns: {
      item: 'Menunaam',
      status: 'Status',
      price: 'Prijs',
      totalBooking: 'Totaal boekingen',
      forms: 'Formulieren',
      state: 'Status',
    },
    formsCount: '{{count}} formulieren',
    active: 'Actief',
    inactive: 'Inactief',
    createTitle: 'Form Menu maken',
    createDesc: 'Voeg een nieuw Form Menu toe voor je filiaal.',
    editTitle: 'Form Menu bewerken',
    editDesc: 'Form Menu-informatie bijwerken',
    createItem: 'Maken',
    addItem: 'Form Menu toevoegen',
    saveChanges: 'Wijzigingen opslaan',
    close: 'Sluiten',
    name: 'Menunaam',
    namePlaceholder: 'Voer Form Menu-naam in',
    description: 'Beschrijving',
    descriptionPlaceholder: 'Voer beschrijving in',
    price: 'Prijs (€)',
    pricePlaceholder: '0',
    forms: 'Formulieren',
    formsPlaceholder: 'Selecteer formulieren',
    activeItem: 'Actief Form Menu',
    itemImage: 'Menuafbeelding',
    uploadImage: 'Afbeelding uploaden',
    removeImage: 'Afbeelding verwijderen',
    uploading: 'Uploaden...',
    weekdaysLabel: 'Weekdagen',
    selectAllWeekdays: 'Alles selecteren',
    clearWeekdays: 'Wissen',
    availabilityDates: {
      title: 'Beschikbaarheidsdatums menu',
      dateRange: 'Datumbereik',
      placeholder: 'Selecteer datumbereik',
      empty:
        'Nog geen beschikbaarheidsdatums. Voeg een bereik toe om te beginnen.',
      addRange: 'Bereik toevoegen',
      createTitle: 'Datumbereik toevoegen',
      editTitle: 'Datumbereik bewerken',
      create: 'Toevoegen',
      update: 'Bijwerken',
      editRange: 'Bereik bewerken',
      deleteRange: 'Bereik verwijderen',
      deleteTitle: 'Datumbereik verwijderen?',
      deleteDescription:
        'Weet je zeker dat je dit beschikbaarheidsbereik wilt verwijderen?',
      dateRequired: 'Selecteer een datumbereik.',
      dateOrder: 'Einddatum moet op of na de startdatum liggen.',
      dateOverlap:
        'Dit bereik overlapt bestaande beschikbaarheidsdatums. Kies vrije datums.',
    },
    weekdays: {
      monday: 'Maandag',
      tuesday: 'Dinsdag',
      wednesday: 'Woensdag',
      thursday: 'Donderdag',
      friday: 'Vrijdag',
      saturday: 'Zaterdag',
      sunday: 'Zondag',
    },
    editItem: 'Form Menu bewerken',
    duplicate: 'Dupliceren',
    editNotFound: 'Dit Form Menu is niet gevonden of niet meer beschikbaar.',
    deleteItem: 'Form Menu verwijderen',
    deleteModal: {
      title: 'Form Menu verwijderen?',
      description:
        'Weet je zeker dat je dit Form Menu wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
    },
    validation: {
      nameRequired: 'Naam is verplicht',
      nameMax: 'Naam mag maximaal 120 tekens bevatten',
      descriptionMax: 'Beschrijving mag maximaal 300 tekens bevatten',
      numberInvalid: 'Voer alleen een geldig getal in',
      priceMin: 'Prijs mag niet negatief zijn',
    },
    toast: {
      createSuccess: 'Form Menu succesvol aangemaakt.',
      createError: 'Form Menu aanmaken mislukt. Probeer het opnieuw.',
      updateSuccess: 'Form Menu succesvol bijgewerkt.',
      updateError: 'Form Menu bijwerken mislukt. Probeer het opnieuw.',
      deleteSuccess: 'Form Menu succesvol verwijderd.',
      deleteError: 'Form Menu verwijderen mislukt. Probeer het opnieuw.',
      duplicateSuccess: 'Form Menu succesvol gedupliceerd.',
      duplicateError: 'Form Menu dupliceren mislukt. Probeer het opnieuw.',
      showEnabled: 'Form Menu is nu zichtbaar.',
      showDisabled: 'Form Menu is nu verborgen.',
      statusError:
        'Status van Form Menu bijwerken mislukt. Probeer het opnieuw.',
      uploadSuccess: 'Afbeelding succesvol geüpload.',
      uploadError: 'Afbeelding uploaden mislukt.',
      removeImageSuccess: 'Afbeelding succesvol verwijderd.',
      removeImageError: 'Afbeelding verwijderen mislukt.',
      dateRangeCreateSuccess: 'Beschikbaarheidsbereik toegevoegd.',
      dateRangeCreateError: 'Beschikbaarheidsbereik toevoegen mislukt.',
      dateRangeUpdateSuccess: 'Beschikbaarheidsbereik bijgewerkt.',
      dateRangeUpdateError: 'Beschikbaarheidsbereik bijwerken mislukt.',
      dateRangeDeleteSuccess: 'Beschikbaarheidsbereik verwijderd.',
      dateRangeDeleteError: 'Beschikbaarheidsbereik verwijderen mislukt.',
      dateRangeLoadError: 'Beschikbaarheidsbereik laden mislukt.',
    },
  },
  events: {
    title: 'Evenementen',
    subtitleDefault: 'Bekijk en beheer evenementen van het filiaal',
    statsCount: '{{count}} evenementen',
    searchPlaceholder: 'Zoek evenementen...',
    filterDatePlaceholder: 'Filter op datum',
    reset: 'Resetten',
    noBranch: 'Selecteer een filiaal om evenementen te bekijken',
    empty: 'Geen evenementen',
    emptyDesc:
      'Je hebt nog geen evenementen aangemaakt. Voeg een evenement toe om te beginnen.',
    columns: {
      event: 'Evenement',
      start: 'Start',
      end: 'Einde',
      capacity: 'Capaciteit',
      totalBooking: 'Total Booking',
      forms: 'Formulieren',
      status: 'Status',
    },
    formsCount: '{{count}} formulieren',
    createTitle: 'Evenement maken',
    createDesc: 'Voeg een nieuw evenement toe voor je filiaal.',
    editTitle: 'Evenement bewerken',
    editDesc: 'Evenementinformatie bijwerken',
    createEvent: 'Evenement maken',
    createItem: 'Aanmaken',
    addEvent: 'Evenement toevoegen',
    saveChanges: 'Wijzigingen opslaan',
    close: 'Sluiten',
    name: 'Naam',
    namePlaceholder: 'Voer evenementnaam in',
    description: 'Beschrijving',
    descriptionPlaceholder: 'Voer beschrijving in',
    startDate: 'Startdatum',
    startTime: 'Starttijd',
    endDate: 'Einddatum',
    endTime: 'Eindtijd',
    capacity: 'Max. capaciteit',
    capacityPlaceholder: '100',
    visible: 'Max. capaciteit tonen',
    forms: 'Formulieren',
    formsPlaceholder: 'Selecteer formulieren',
    mandatory: 'Evenement verplicht',
    weekdaysLabel: 'Weekdagen',
    selectAllWeekdays: 'Alles selecteren',
    clearWeekdays: 'Wissen',
    availabilityDates: {
      title: 'Beschikbaarheidsdata evenement',
      dateRange: 'Datumbereik',
      placeholder: 'Selecteer datumbereik',
      empty:
        'Nog geen beschikbaarheidsdata. Voeg een bereik toe om te beginnen.',
      addRange: 'Bereik toevoegen',
      createTitle: 'Datumbereik toevoegen',
      editTitle: 'Datumbereik bewerken',
      create: 'Toevoegen',
      update: 'Bijwerken',
      editRange: 'Bereik bewerken',
      deleteRange: 'Bereik verwijderen',
      deleteTitle: 'Datumbereik verwijderen?',
      deleteDescription:
        'Weet je zeker dat je dit beschikbaarheidsbereik wilt verwijderen?',
      dateRequired: 'Selecteer een datumbereik.',
      dateOrder: 'Einddatum moet op of na de startdatum liggen.',
      dateOverlap: 'Dit bereik overlapt bestaande data. Kies vrije data.',
    },
    weekdays: {
      monday: 'Maandag',
      tuesday: 'Dinsdag',
      wednesday: 'Woensdag',
      thursday: 'Donderdag',
      friday: 'Vrijdag',
      saturday: 'Zaterdag',
      sunday: 'Zondag',
    },
    eventImage: 'Evenementafbeelding',
    uploadImage: 'Afbeelding uploaden',
    removeImage: 'Afbeelding verwijderen',
    uploading: 'Uploaden...',
    removing: 'Verwijderen...',
    editEvent: 'Evenement bewerken',
    duplicate: 'Dupliceren',
    deleteEvent: 'Evenement verwijderen',
    deleteModal: {
      title: 'Evenement verwijderen?',
      description:
        'Weet je zeker dat je dit evenement wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
    },
    validation: {
      nameRequired: 'Naam is verplicht',
      nameMax: 'Naam mag maximaal 120 tekens bevatten',
      descriptionMax: 'Beschrijving mag maximaal 500 tekens bevatten',
      capacityInvalid: 'Voer een geldige capaciteit in',
      capacityInteger: 'Capaciteit moet een heel getal zijn',
      capacityMin: 'Capaciteit moet minstens 1 zijn',
      capacityMax: 'Capaciteit mag niet hoger zijn dan 999',
      startDateRequired: 'Startdatum is verplicht',
      startTimeRequired: 'Starttijd is verplicht',
      endDateRequired: 'Einddatum is verplicht',
      endTimeRequired: 'Eindtijd is verplicht',
      endBeforeStart: 'Einde moet na start liggen',
    },
    toast: {
      createSuccess: 'Evenement succesvol aangemaakt.',
      createError: 'Evenement aanmaken mislukt. Probeer opnieuw.',
      updateSuccess: 'Evenement succesvol bijgewerkt.',
      updateError: 'Evenement bijwerken mislukt. Probeer opnieuw.',
      deleteSuccess: 'Evenement succesvol verwijderd.',
      deleteError: 'Evenement verwijderen mislukt. Probeer opnieuw.',
      duplicateSuccess: 'Evenement succesvol gedupliceerd.',
      duplicateError: 'Evenement dupliceren mislukt. Probeer opnieuw.',
      showEnabled: 'Evenement is nu zichtbaar.',
      showDisabled: 'Evenement is nu verborgen.',
      statusError: 'Status van evenement bijwerken mislukt. Probeer opnieuw.',
      uploadSuccess: 'Afbeelding succesvol geüpload.',
      uploadError: 'Afbeelding uploaden mislukt.',
      saveBeforeImage:
        'Vul de verplichte evenementvelden in voordat u een afbeelding uploadt.',
      removeImageSuccess: 'Afbeelding succesvol verwijderd.',
      removeImageError: 'Afbeelding verwijderen mislukt.',
      dateRangeCreateSuccess: 'Beschikbaarheidsbereik toegevoegd.',
      dateRangeCreateError: 'Beschikbaarheidsbereik toevoegen mislukt.',
      dateRangeUpdateSuccess: 'Beschikbaarheidsbereik bijgewerkt.',
      dateRangeUpdateError: 'Beschikbaarheidsbereik bijwerken mislukt.',
      dateRangeDeleteSuccess: 'Beschikbaarheidsbereik verwijderd.',
      dateRangeDeleteError: 'Beschikbaarheidsbereik verwijderen mislukt.',
      dateRangeLoadError: 'Beschikbaarheidsbereik laden mislukt.',
    },
  },
  reports: {
    title: 'Rapporten',
    subtitle: 'Analyse- en rapportagedashboard',
    comingSoon: 'Rapporten binnenkort beschikbaar',
    comingSoonDesc:
      'Geavanceerde analyses en aangepaste rapporten komen in een toekomstige release.',
  },
  errors: {
    somethingWrong: 'Er is iets misgegaan',
    unexpected: 'Er is een onverwachte fout opgetreden. Probeer opnieuw.',
    pageNotFound: 'Pagina niet gevonden',
    pageNotFoundDesc: 'De pagina die je zoekt bestaat niet of is verplaatst.',
    accessDenied: 'Toegang geweigerd',
    accessDeniedDesc:
      'Je hebt geen toestemming om deze bron te openen. Neem contact op met je beheerder.',
    backToDashboard: 'Terug naar dashboard',
    backToHome: 'Terug naar home',
    publicHint:
      'Controleer de link die je hebt ontvangen, of neem contact op met de locatie als je hulp nodig hebt.',
    applicationError: 'Applicatiefout',
    reload: 'Pagina herladen',
  },
  publicPromo: {
    documentTitle: 'Promotie',
    loading: 'Promotie laden',
    unavailableTitle: 'Promotie niet beschikbaar',
    unavailableDesc:
      'Deze promotielink ontbreekt, is ongeldig of is niet meer actief.',
    expiredDesc: 'Deze promotie is verlopen en niet meer beschikbaar.',
    comingSoonDesc:
      'Deze promotie is nog niet klaar om reacties te verzamelen.',
    formUnavailableTitle: 'Formulier niet beschikbaar',
    formUnavailableMessage:
      'Dit promotieformulier is momenteel opgeschort of niet beschikbaar.',
    badge: 'Promotie',
    requestTitle: 'Vraag je coupon aan',
    requestSubtitle: 'Vul je gegevens in om de promotie aan te vragen.',
    optional: 'Optioneel',
    birthDate: 'Geboortedatum',
    cap: 'Postcode',
    capPlaceholder: 'Bijv. 1012',
    address: 'Adres',
    addressPlaceholder: 'Straat en huisnummer',
    nationality: 'Nationaliteit',
    nationalityPlaceholder: 'Je nationaliteit',
    requiredNote: '* Verplichte velden',
    submitting: 'Opslaan…',
    personalInfo: 'Persoonlijke informatie',
    phoneLabel: 'Mobiel',
    phonePlaceholder: 'Mobiele telefoon',
    firstName: 'Voornaam',
    firstNamePlaceholder: 'Voornaam',
    lastName: 'Achternaam',
    lastNamePlaceholder: 'Achternaam',
    email: 'E-mail',
    emailPlaceholder: 'E-mail',
    consentsTitle: 'Ik stem in met de verwerking van persoonsgegevens',
    privacyConsent: 'Privacy en gegevensverwerking',
    marketingConsent: 'Marketing- en communicatietoestemming',
    profilingConsent: 'Toestemming voor profilering',
    detailsLink: 'Details',
    selectAll: 'Alles selecteren',
    submit: 'Vraag de coupon aan',
    toast: {
      success: 'Coupon succesvol gegenereerd.',
      error: 'Genereren van coupon mislukt. Probeer opnieuw.',
    },
    thankYou: {
      title: 'Bedankt!',
      message: 'Je verzoek is succesvol verzonden.',
      continue: 'Doorgaan',
    },
    lookupFound: 'We hebben je gegevens gevonden en ingevuld.',
    welcomeBack: 'Welkom terug, {{firstName}}. We hebben je gegevens al.',
    welcomeBackNoName: 'Welkom terug. We hebben je gegevens al.',
    alreadyHasCoupon: 'Je hebt al een coupon voor deze actie.',
    alreadyUsed: 'Deze coupon is al gebruikt.',
    maxCoupons:
      'Er kunnen geen coupons meer worden gemaakt voor deze actie. De limiet is bereikt.',
    lookupError:
      'We konden dit nummer niet controleren. Je kunt het formulier nog steeds invullen.',
    lookupNotFound: 'Deze actie is niet gevonden.',
    lookupInvalid: 'Controleer het mobiele nummer en probeer opnieuw.',
    resend: {
      title: 'Coupon via WhatsApp versturen?',
      message:
        'Je hebt al een coupon aangevraagd. We kunnen die opnieuw via WhatsApp sturen, met de QR-code.',
      confirm: 'Verstuur via WhatsApp',
      decline: 'Nu niet',
      close: 'Sluiten',
      success: 'We hebben de coupon naar je WhatsApp gestuurd.',
      error: 'De coupon kon niet worden verstuurd. Probeer opnieuw.',
    },
    validation: {
      prefixRequired: 'Selecteer een landcode.',
      phoneMin: 'Voer een geldig mobiel nummer in.',
      phoneRange: 'Voer 6 tot 15 cijfers in.',
      firstNameRequired: 'Voornaam is verplicht.',
      lastNameRequired: 'Achternaam is verplicht.',
      emailRequired: 'E-mail is verplicht.',
      emailInvalid: 'Voer een geldig e-mailadres in.',
      birthDateInvalid:
        'Voer een geldige datum in die niet in de toekomst ligt.',
      privacyRequired: 'Privacytoestemming is verplicht.',
    },
  },
  publicFidelity: {
    documentTitle: 'Fidelitykaart',
    loading: 'Fidelityprogramma laden',
    unavailableTitle: 'Programma niet beschikbaar',
    unavailableDesc:
      'Controleer de ontvangen link of neem contact op met het restaurant.',
    badge: 'Uw loyaliteitsprogramma',
    cardLabel: 'Fidelitykaart',
    previewPoints: 'Elk bezoek telt.',
    previewDiscount: 'Voordelen voor u.',
    requestTitle: 'Vraag uw kaart aan',
    requestSubtitle: 'Maak uw persoonlijke kaart en houd die bij u.',
    optional: 'Optioneel',
    requiredNote: '* Verplichte velden',
    submitting: 'Opslaan…',
    phoneLabel: 'Mobiel',
    phonePlaceholder: 'Uw nummer',
    firstName: 'Voornaam',
    firstNamePlaceholder: 'Uw voornaam',
    lastName: 'Achternaam',
    lastNamePlaceholder: 'Uw achternaam',
    email: 'E-mail',
    emailPlaceholder: 'naam@voorbeeld.nl',
    consentsTitle: 'Uw voorkeuren',
    privacyConsent: 'Privacy en gegevensverwerking',
    marketingConsent: 'Toestemming voor marketing en communicatie',
    profilingConsent: 'Toestemming voor profilering',
    detailsLink: 'Informatie',
    selectAll: 'Alles selecteren',
    submit: 'Maak mijn kaart',
    successTitle: 'Uw kaart is klaar.',
    successSubtitle: 'Sla de QR op en toon die in het restaurant.',
    qrAlt: 'QR-code van uw fidelitykaart',
    openQr: 'QR openen en opslaan',
    welcomeBack: 'Welkom terug, {{firstName}}. We hebben uw gegevens al.',
    welcomeBackNoName: 'Welkom terug. We hebben uw gegevens al.',
    lookupError:
      'We konden dit nummer niet controleren. U kunt het formulier nog steeds invullen.',
    lookupUnavailable: 'Dit programma is niet meer beschikbaar.',
    lookupInvalid: 'Controleer het mobiele nummer en probeer opnieuw.',
    requestLink: 'Link opnieuw aanvragen',
    requestLinkSending: 'Aanvraag verzenden…',
    requestLinkSent:
      'Als dit contact aan een actieve kaart is gekoppeld, wordt de link opnieuw verzonden.',
    requestLinkError:
      'De link kon niet worden aangevraagd. Probeer het opnieuw.',
    errors: {
      alreadyRegistered:
        'U hebt al een kaart voor dit programma. Gebruik de ontvangen QR of neem contact op met het restaurant.',
      unavailable: 'Dit programma is niet meer beschikbaar.',
      generic:
        'De kaart kon niet worden gemaakt. Probeer het zo opnieuw of neem contact op met het restaurant.',
    },
    validation: {
      prefixRequired: 'Selecteer een landcode.',
      phoneRange: 'Voer 6 tot 15 cijfers in.',
      firstNameRequired: 'Voer de voornaam in.',
      lastNameRequired: 'Voer de achternaam in.',
      emailInvalid: 'Controleer het e-mailadres.',
      privacyRequired: 'Privacytoestemming is verplicht.',
    },
  },
  publicBookingForm: {
    imagePreview: {
      open: 'Afbeelding vergroten: {{name}}',
      close: 'Afbeeldingsvoorbeeld sluiten',
      error: 'De afbeelding kan niet worden geladen.',
    },
    title: 'Reservering',
    welcome: 'Welkom bij',
    loading: 'Reserveringsformulier laden',
    missingFormCode: 'Formuliercode ontbreekt in de URL.',
    loadError: 'Dit reserveringsformulier kan niet worden geladen.',
    inactiveTitle: 'Formulier niet beschikbaar',
    inactiveMessage: 'Dit reserveringsformulier is momenteel niet actief.',
    invalidFormType: 'Deze link is geen reserveringsformulier.',
    steps: {
      date: 'Datum',
      hours: 'Uren',
      menu: 'Menu',
      extra: 'Extra',
      details: 'Details',
      calendar: 'Kalender',
    },
    eventBanner: {
      countOne: '{{count}} evenement deze maand',
      count: '{{count}} evenementen deze maand',
      tapHint: 'Tik om te zien op welke dagen',
    },
    menuBanner: {
      countOne: '{{count}} menu deze maand',
      count: "{{count}} menu's deze maand",
      tapHint: 'Tik om te zien op welke dagen',
    },
    calendar: {
      legendEvent: 'Speciaal evenement',
      legendMenu: "Menu's beschikbaar",
    },
    restaurantNoMenu: {
      title: 'No menu',
      description: 'Aantal gasten zonder menu',
    },
    eventSheet: {
      fromTime: 'Vanaf {{time}}',
      maxCovers: 'Max. {{count}} couverts',
      gotIt: 'Doorgaan',
      continueWithEvent: 'Doorgaan voor evenement',
      showMore: 'Meer tonen',
      showLess: 'Minder tonen',
      close: 'Sluiten',
    },
    hours: {
      selectedDate: 'Geselecteerde datum',
      guestsLabel: 'Gasten (kinderen uitgesloten)',
      childrenLabel: 'Kinderen',
      timeSlotLabel: 'Beschikbaar tijdslot',
      guestOption: '{{count}} Gasten',
      childrenOption: '{{count}} Kinderen',
      overlimit: 'Limiet overschreden met {{count}}',
      overbooking: 'Overbooking met {{count}}',
      availabilityFinished: 'Beschikbaarheid opgebruikt voor dit tijdslot',
      attentionTitle: 'Let op',
      attentionMessage:
        'Het geselecteerde aantal gasten overschrijdt de resterende beschikbaarheid voor dit tijdslot.',
      attentionOk: 'OK',
      callOverMaxOption: 'Bel voor tafels met meer dan {{count}} gasten',
      callOverMaxTitle: 'Info',
      callOverMaxMessageBefore: 'Bel',
      callOverMaxMessageAfter: 'om een tafel te reserveren?',
      callOverMaxHint:
        'We openen de telefoonkiezer zodat je het gesprek kunt afronden.',
      callOverMaxCall: 'Bellen',
      callOverMaxCancel: 'Annuleren',
    },
    extra: {
      badge: 'Extra',
      catalogTitle: 'Dienstencatalogus',
      mandatoryNotice: 'De keuze van minstens één product/dienst is verplicht.',
      serviceForGuests: 'Dienst/tafel voor {{count}} gasten',
      decrease: 'Hoeveelheid verlagen',
      increase: 'Hoeveelheid verhogen',
    },
    menu: {
      badge: 'Menu',
      catalogTitle: 'Menucatalogus',
      mandatoryQuantityHint:
        'Kies per persoon een menu (of “Geen menu”). Het totaal van de selecties moet gelijk zijn aan {{count}} (gasten + kinderen).',
      decrease: 'Hoeveelheid verlagen',
      increase: 'Hoeveelheid verhogen',
    },
    recap: {
      title: 'Reserveringssamenvatting',
      time: 'Uren',
      guests: 'Gasten',
      children: 'Kinderen',
      selectedServices: 'Geselecteerde diensten',
      selectedMenus: "Geselecteerde menu's",
      total: 'Totaal',
      paymentOnSite: 'Betaling ter plaatse',
    },
    details: {
      phoneTitle: 'Voer uw telefoonnummer in om verder te gaan',
      welcomeBackTitle: 'Welkom terug, {{name}}!',
      welcomeBackTitleGeneric: 'Welkom terug!',
      welcomeBackMessage:
        'We hebben uw gegevens al. U kunt doorgaan of ze indien nodig bijwerken.',
      personalInfo: 'Persoonlijke gegevens',
      consentsTitle:
        'Ik geef toestemming voor de verwerking van persoonsgegevens',
      privacyConsent: 'Verwerking van privacy en gegevens',
      marketingConsent: 'Toestemming marketing en communicatie',
      commercialConsent:
        'Toestemming tot doorgifte van gegevens aan derden voor commerciële doeleinden',
      profilingConsent: 'Toestemming tot profilering',
      detailsLink: 'Details',
      selectAll: 'Alles selecteren',
      consentModal: {
        title: 'INFORMATIE OVER DE VERWERKING VAN PERSOONSGEGEVENS',
        subtitle:
          '(overeenkomstig artikel 13 van Verord. EU nr. 679/2016 - AVG)',
        addressLabel: 'Adres',
        emailLabel: 'E-mail',
        lastUpdated: 'Laatst bijgewerkt',
        section1Title: '1. Premisse',
        section1P1:
          'Deze informatie wordt verstrekt door {{branchName}} (fiscale code en btw-nummer), met statutaire zetel in {{branchAddress}} (hierna de "Verwerkingsverantwoordelijke"). (hierna de "Verwerkingsverantwoordelijke"), in overeenstemming met EU-verordening 2016/679 (AVG) en D.lgs. 196/2003 (Privacycode), zoals gewijzigd door <b>D.lgs. 101/2018.</b>',
        section1P2:
          'Deze informatie geldt voor de verwerking van persoonsgegevens door de Verwerkingsverantwoordelijke via het <b>"Pro20"</b>-platform voor het beheer van de activiteit, reserveringen, aangeboden diensten en communicatie met klanten.',
        section1P3: 'De informatie wordt verstrekt aan gebruikers die:',
        section1Li1:
          '• De website van de eigenaar gebruiken om informatie aan te vragen of een tafel te reserveren;',
        section1Li2:
          '• Vrijwillig hun gegevens verstrekken via persoonlijke of lokale mobiele apparaten (bijv. tablets) om toegang te krijgen tot diensten zoals het loyaliteitsprogramma of gratis Wi-Fi;',
        section1Li3:
          '• Zich fysiek in de vestiging presenteren en hun gegevens verstrekken om nieuwsbrieven, commerciële aanbiedingen en promotiemateriaal over producten en diensten van de eigenaar te ontvangen.',
        section2Title: '2. Verwerkingsverantwoordelijke',
        section2Sensitive:
          'Gevoelige gegevens worden alleen verwerkt indien strikt noodzakelijk en met toestemming van de gebruiker, geuit door expliciete handelingen zoals het selecteren van een online-optie (bijv. "Ik accepteer" of "Ik stem in") of de ondertekening van een papieren formulier.',
        section3Title: '3. Verwerkte gegevens en methoden van verkrijging',
        section3Intro: 'De gegevens worden als volgt verkregen:',
        section3Li1:
          '• Via het PRO20-platform voor het beheer van reserveringen en aangeboden diensten.',
        section3Li2:
          '• Via de website van de eigenaar, wanneer de gebruiker informatie aanvraagt of een tafel reserveert',
        section3Li3:
          '• Direct in de zaak, wanneer de gebruiker gegevens verstrekt om toegang te krijgen tot diensten (Wi-Fi, loyaliteit, enz.).',
        section4Title: '4. Navigatiegegevens',
        section4Body:
          "Als de gebruiker interactie heeft met de website of andere digitale platforms, worden navigatiegegevens automatisch verzameld (bijv. IP-adres, apparaattype, besturingssysteem, bezochte pagina's). Het gebruik van cookies en vergelijkbare tools wordt beschreven in de Cookie Policy, raadpleegbaar op de website van de eigenaar.",
        section5Title: '5. Doeleinden van de verwerking',
        section51Title: '5.1 Primaire doeleinden',
        section51Intro: 'De gegevens worden verwerkt om:',
        section51Li1:
          '✔ Reserveringen en gevraagde diensten te beheren (snel-rij, Wi-Fi, loyaliteit, tevredenheidsenquêtes, enz.)',
        section51Li2:
          '✔ Te voldoen aan wettelijke, fiscale en administratieve verplichtingen',
        section51Li3:
          '✔ Personeel en dienst te beschermen tegen onwettig of frauduleus gedrag',
        section51Li4:
          '✔ Anonieme statistische analyses om de kwaliteit van diensten te verbeteren',
        section51Li5:
          '✔ Te communiceren met de gebruiker over de reservering of andere gevraagde diensten',
        section51Note:
          '📌 Het verstrekken van gegevens is noodzakelijk om toegang te krijgen tot de diensten: het niet verstrekken kan de reservering of dienstverlening verhinderen.',
        section52Title:
          '5.2 Secundaire doeleinden (Marketing en promoties): alleen met expliciete toestemming.',
        section52Intro:
          'Onder voorbehoud van expliciete toestemming kunnen gegevens worden gebruikt voor:',
        section52Li1:
          '📢 Verzending van promotionele communicatie en gepersonaliseerde aanbiedingen, via e-mail, sms of andere digitale kanalen',
        section52Li2: '📢 Marktonderzoek en enquêtes over klantervaring',
        section52Li3:
          '📢 Analyse van consumptiegewoonten om gepersonaliseerde ervaringen aan te bieden.',
        section52Note:
          '⚠ Toestemming voor marketing is optioneel en kan te allen tijde worden ingetrokken door te schrijven naar {{email}}.',
        section6Title: '6. Ontvangers van gegevens',
        section6Intro: 'De verzamelde gegevens kunnen worden meegedeeld aan:',
        section6Li1:
          '• Geautoriseerd intern personeel van de eigenaar, voor het beheer van de dienst.',
        section6Li2:
          '• IT-dienstverleners en beheerplatforms (bijv. online reserveringen, e-mailmarketing, webhosting), met inachtneming van vertrouwelijkheidsovereenkomsten.',
        section6Li3:
          '• Overheidsinstanties, bij wettelijke verzoeken of regelgevingsverplichtingen.',
        section6Note:
          '📌 De gegevens worden NIET openbaar gemaakt of verkocht aan derden.',
        section7Title: '7. Bewaring van gegevens',
        section7Intro: 'De gegevens worden bewaard voor:',
        section7Li1:
          '📌 Administratieve en boekhoudkundige doeleinden: gedurende de tijd die nodig is om aan wettelijke verplichtingen te voldoen (tot 10 jaar).',
        section7Li2:
          '📌 Marketingdoeleinden: tot intrekking van de toestemming of, bij afwezigheid van interacties, maximaal 24 maanden.',
        section7Li3:
          '📌 Navigatiegegevens: volgens hetgeen is vastgelegd in de Cookie Policy.',
        section8Title: '8. Rechten van de gebruiker',
        section8Intro: 'Gebruikers hebben het recht om:',
        section8Li1:
          '✅ Toegang tot hun persoonsgegevens te krijgen en informatie over de verwerking te verkrijgen.',
        section8Li2:
          '✅ Rectificatie of verwijdering van persoonsgegevens te verzoeken.',
        section8Li3:
          '✅ Verwerking te beperken of ertegen te protesteren om legitieme redenen.',
        section8Li4:
          '✅ Toestemming te allen tijde in te trekken zonder afbreuk aan de rechtmatigheid van eerdere verwerking.',
        section8Li5:
          '✅ Een klacht in te dienen bij de Autoriteit persoonsgegevens (www.garanteprivacy.it).',
        section9Title:
          '9. Toestemming tot doorgifte van gegevens aan derden voor commerciële doeleinden',
        section9Li1:
          '✅ Ik stem in met de verwerking van mijn persoonsgegevens voor directe marketing door derden, ook via doorgifte aan commerciële partners in sectoren zoals uitgeverij, technologie, reizen, financiën, enz. Deze partijen kunnen mij per e-mail, telefoon of andere kanalen contacteren voor hun eigen promo-initiatieven. Toestemming is optioneel en kan te allen tijde worden ingetrokken door te schrijven naar {{email}}.',
        section9Contact:
          '📧 Om uw rechten uit te oefenen, neem contact op: {{email}}',
        section10Title: '10. Wijzigingen van de informatie',
        section10Body:
          'De Verwerkingsverantwoordelijke behoudt zich het recht voor deze informatie bij te werken. Wijzigingen worden via de website of per e-mail meegedeeld indien nodig.',
      },
    },
    actions: {
      continue: 'Doorgaan',
      back: 'Terug',
      proceedToDetails: 'Ga naar persoonlijke gegevens',
    },
    validation: {
      date: 'Selecteer een datum.',
      hours: 'Vul gasten, kinderen en tijdslot in.',
      menu: 'Controleer je menuselectie.',
      menuMandatory:
        'Kies menu’s zodat het totaal gelijk is aan gasten + kinderen.',
      extra: 'Selecteer minstens één dienst.',
    },
    fieldErrors: {
      firstNameMin: 'Voornaam is verplicht.',
      firstNameInvalid: 'Voornaam mag alleen letters bevatten.',
      lastNameMin: 'Achternaam moet minstens 2 tekens bevatten.',
      lastNameInvalid: 'Achternaam mag alleen letters bevatten.',
      emailRequired: 'E-mail is verplicht.',
      emailInvalid: 'Voer een geldig e-mailadres in.',
      postalCodeRequired: 'Postcode is verplicht.',
      dateOfBirthRequired: 'Geboortedatum is verplicht.',
      specialRequestsRequired: 'Speciale verzoeken zijn verplicht.',
      singleChoiceRequired: 'Selecteer een optie.',
      multiChoiceRequired: 'Selecteer minimaal één optie.',
      dateOfBirthInvalid: 'Voer een geldige geboortedatum in.',
      dateOfBirthFuture: 'Geboortedatum mag niet in de toekomst liggen.',
      countryCodeInvalid: 'Selecteer een geldige landcode.',
      phoneRequired: 'Telefoonnummer is verplicht.',
      phoneInvalid: 'Telefoonnummer mag alleen cijfers bevatten.',
      phoneMin: 'Telefoonnummer moet minstens 6 cijfers bevatten.',
      phoneMax: 'Telefoonnummer mag maximaal 15 cijfers bevatten.',
      privacyRequired: 'Privacytoestemming is verplicht.',
      cardRequestConsentRequired:
        'Accepteer de voorwaarden voor kaartcontrole / borg.',
      bookingTimeInvalid: 'Selecteer een geldige tijd.',
      coverOverlimit:
        'Gasten en kinderen overschrijden het maximum aantal covers van dit formulier.',
    },
    cardRequest: {
      title: 'Card required for this booking',
      notice:
        'Om deze reservering te bevestigen is een betaalkaart vereist voor het geselecteerde tijdvak.',
      alreadyOnFile:
        'Er is al een kaart voor dit nummer. Bevestig de reservering om die te gebruiken.',
      acceptValidation:
        'Ik accepteer de kaartverificatie. Er wordt tijdens deze handeling geen bedrag afgeschreven of geblokkeerd.',
      acceptDeposit:
        'Ik accepteer de pre-autorisatie van {{amount}} op de kaart en de voorwaarden voor incasso, annulering en no-show.',
      acceptPayNow:
        'Ik bevestig de betaling van {{amount}} en accepteer de voorwaarden voor annulering, terugbetaling en no-show.',
      policyToggle: 'Lees het volledige betalingsbeleid',
      policyTitle: 'Beleid voor betaling, kaart en annulering',
      policy: {
        s1Title: '1. Identiteit van de locatie',
        s1Body:
          'Reservering en betaling worden beheerd door {{branchName}}, {{branchAddress}}. Stripe is uitsluitend de betalingsdienstverlener. Contact: {{contact}}.',
        s2Title: '2. Kaartverificatie',
        s2Body:
          'Bij alleen verificatie wordt geen bedrag afgeschreven of geblokkeerd. De kaart wordt via Stripe gecontroleerd.',
        s3Title: '3. Pre-autorisatie / borg',
        s3Body:
          'Er kan een pre-autorisatie van {{amount}} ({{perGuest}} per gast) tijdelijk worden geblokkeerd. Vrijgave hangt af van de bank.',
        s4Title: '4. Vooruitbetaling',
        s4Body:
          'Indien geconfigureerd kan een aanbetaling worden geïnd volgens de regels van de locatie.',
        s5Title: '5. Annulering en wijziging',
        s5Body:
          'Gratis annulering kan gelden tot de deadline van de locatie. Daarna kunnen bedragen worden ingehouden of geïnd.',
        s6Title: '6. No-show en vertraging',
        s6Body:
          'Bij no-show of aanzienlijke vertraging kan de locatie bedragen inhouden of innen volgens haar regels.',
        s7Title: '7. Terugbetalingen',
        s7Body:
          'In aanmerking komende terugbetalingen gaan naar dezelfde betaalmethode, binnen de termijnen van locatie en provider.',
        s8Title: '8. Bewaring van de kaart',
        s8Body:
          'Kaartgegevens kunnen getokeniseerd door Stripe worden bewaard en alleen met specifieke toestemming opnieuw gebruikt. PRO20 en de locatie bewaren het volledige kaartnummer niet.',
        s9Title: '9. Geschillen en ondersteuning',
        s9Body:
          'Voor annuleringen, geschillen of hulp contacteer de locatie ({{contact}}) met uw boekingscode.',
        version: 'Versie {{version}}',
      },
    },
    legacyCard: {
      eyebrow: 'Directe verificatie',
      title: 'Creditcardgegevens',
      subtitle: 'Deze locatie gebruikt Stripe niet voor deze reservering.',
      holder: 'Naam op de kaart',
      number: 'Kaartnummer',
      month: 'Maand',
      year: 'Jaar',
      cvc: 'CVV',
      circuit: '{{circuit}}',
      verified: 'Kaart geverifieerd · eindigt op {{last4}}',
      cancel: 'Annuleren',
      verify: 'Kaart verifiëren',
      verifying: 'Verifiëren…',
      confirm: 'Bevestigen',
      verifySuccess: 'Kaart succesvol geverifieerd',
      verifyError: 'De kaartgegevens zijn niet geldig',
      requiredTitle: 'Kaart vereist',
      required:
        'De kaart moet worden geverifieerd om de reservering te voltooien.',
      storeWarning:
        'De reservering is aangemaakt, maar de kaart kon niet worden opgeslagen. Probeer het opnieuw via het kaartverzoek.',
      saved: 'Kaart opgeslagen · eindigt op {{last4}}',
      alreadyOnFile:
        'Er staat al een kaart voor deze gast. Verdere gegevens zijn niet nodig.',
      pageLoadError: 'Dit kaartverzoek kan niet worden geopend.',
      saveError: 'De kaart kon niet worden opgeslagen. Probeer het opnieuw.',
      fieldRequired: 'Verplicht veld',
      fieldInvalid: 'Ongeldige waarde',
      circuits: {
        VISA: 'Visa',
        MASTERCARD: 'Mastercard',
        AMERICAN_EXPRESS: 'American Express',
        DISCOVER: 'Discover',
        JCB: 'JCB',
        MAESTRO: 'Maestro',
      },
    },
    stripe: {
      verifyTitle: 'Verifieer je kaart',
      verifyDescription:
        'Voer je kaartgegevens in om deze reservering te bevestigen. Er wordt nu geen bedrag afgeschreven.',
      verifyConfirm: 'Kaart verifiëren',
      payTitle: 'Betaling voltooien',
      payDescription:
        'Autoriseer {{amount}} om deze reservering te bevestigen.',
      payConfirm: 'Betaal {{amount}}',
      initializing: 'Veilige betaling voorbereiden…',
      processing: 'Verwerken…',
      initError:
        'Stripe-betaling kon niet worden gestart. Probeer het opnieuw.',
      confirmError: 'Betaling kon niet worden voltooid. Probeer het opnieuw.',
      verifyPending:
        'Stripe heeft de kaart nog niet bevestigd. Probeer het opnieuw.',
      cardRequired:
        'De kaart moet worden geverifieerd om de reservering te voltooien.',
      resumePayment: 'Betaling voltooien',
      resumeNotice:
        'Je reservering staat in de wacht en wacht op betaling. Voltooi de betaling om deze te bevestigen — er wordt geen nieuwe reservering aangemaakt.',
    },
    phonePlaceholder: 'Voer telefoonnummer in',
    unknownCustomer: 'Onbekende klant',
    blacklist: 'Zwarte lijst',
    customerDetails: 'Jouw gegevens',
    bookingDetails: 'Reserveringsdetails',
    firstName: 'Voornaam',
    lastName: 'Achternaam',
    email: 'E-mail',
    phone: 'Telefoon',
    postalCode: 'Postcode',
    dateOfBirth: 'Geboortedatum',
    guests: 'Gasten',
    children: 'Kinderen',
    time: 'Tijd',
    selectTime: 'Selecteer tijd',
    slotNotAvailable: 'Tijdslot niet beschikbaar',
    noAvailableTimes: 'Geen beschikbare tijden voor de geselecteerde datum.',
    specialRequests: 'Notities en aanvragen',
    comingWithDogs: 'Ik kom met honden',
    extraServices: 'Extra diensten',
    amenityGuests: '{{count}} gasten',
    submit: 'Reservering verzenden',
    submitError: 'Kan je reservering niet verzenden. Probeer het opnieuw.',
    existingBooking: {
      title: 'Let op, er bestaat al een reservering',
      date: 'Datum',
      time: 'Tijd',
      guests: 'Gasten',
      children: 'Kinderen',
      event: 'Evenement',
      menus: 'Menu’s',
      noMenu: 'Geen menu',
      services: 'Diensten',
      continueQuestion: 'Wil je doorgaan en deze reservering toch maken?',
      continue: 'Doorgaan',
    },
    thankYou: {
      title: 'Bedankt!',
      message:
        'Je reserveringsaanvraag is ontvangen. We nemen binnenkort contact met je op.',
      summaryTitle: 'Overzicht van de reservering',
      date: 'Datum',
      time: 'Tijd',
      guests: 'Gasten',
      guestsValue: '{{guests}} Gast ({{children}} Kinderen)',
      location: 'Locatie',
      name: 'Naam',
      phone: 'Telefoon',
      eventCode: 'Evenementcode',
      eventName: 'Evenement',
      menus: "Menu's",
      makeAnother: 'Nog een reservering maken',
      backHome: 'Terug naar home',
      continue: 'Doorgaan',
    },
  },
  publicBookingUpdate: {
    title: 'Reservering bijwerken',
    loading: 'Reservering laden',
    missingBookingCode: 'De boekingscode ontbreekt in de URL.',
    loadError: 'Kan deze reservering niet laden.',
    formLoadError: 'Kan de beschikbaarheid voor deze reservering niet laden.',
    noFormTitle: 'Online wijzigen niet beschikbaar',
    noFormMessage:
      'Deze reservering kan niet online worden gewijzigd. Neem rechtstreeks contact op met het restaurant.',
    notEditable: 'De reservering kan niet meer worden gewijzigd',
    hello: 'Hallo {{name}}',
    subtitle: 'Je wijzigt je reservering bij {{branch}}',
    currentBadge: 'Huidig: {{time}} · {{date}}',
    currentlyBooked: 'Huidige reservering',
    current: {
      title: 'Jouw reservering',
      client: 'Klant',
      date: 'Huidige datum',
      time: 'Huidige tijd',
      guests: 'Gasten',
      children: 'Kinderen',
      event: 'Evenement',
      menu: 'Menu',
      menuPeople: 'Personen',
      showDetails: 'Details',
      hideDetails: 'Verbergen',
    },
    pendingModification: {
      title: 'Er is al een wijzigingsverzoek in afwachting van bevestiging',
      summary: '{{date}} · {{time}} · {{guests}} gasten, {{children}} kinderen',
      date: 'Datum',
      time: 'Tijd',
      guests: 'Gasten',
      children: 'Kinderen',
    },
    form: {
      title: 'Reservering wijzigen',
      subtitle: 'Kies een nieuwe datum, groepsgrootte en tijdslot.',
      selectedDate: 'Geselecteerde datum',
      guestsLabel: 'Gasten (kinderen niet inbegrepen)',
      childrenLabel: 'Kinderen',
      timeSlotLabel: 'Beschikbaar tijdslot',
      guestOption: '{{count}} Gasten',
      childrenOption: '{{count}} Kinderen',
      selectTime: 'Selecteer tijd',
      noAvailableTimes: 'Geen beschikbare tijden voor de geselecteerde datum.',
      notesLabel: 'Persoonlijke notities en verzoeken',
      notesPlaceholder: 'Voeg een notitie toe voor het restaurant…',
      readyToUpdate:
        'Controleer je wijzigingen en tik op Bijwerken om het wijzigingsverzoek te versturen.',
      calendarUnavailable:
        'Kalender is niet beschikbaar voor deze boeking (formulier of vestiging ontbreekt).',
    },
    actions: {
      update: 'Reservering bijwerken',
      updating: 'Bezig met bijwerken…',
      delete: 'Verwijderen',
      cancelReservation: 'Reservering annuleren',
      cancelling: 'Annuleren…',
    },
    deleteConfirm:
      'Weet je zeker dat je deze reservering wilt annuleren? Dit kan niet ongedaan worden gemaakt.',
    deleteModal: {
      title: 'Reservering annuleren?',
      description:
        'Weet je zeker dat je deze reservering wilt annuleren? Dit kan niet ongedaan worden gemaakt.',
    },
    toast: {
      updateSuccess: 'Wijzigingsverzoek verzonden.',
      updateError: 'Kan de reservering niet bijwerken. Probeer het opnieuw.',
      validationError: 'Corrigeer de gemarkeerde velden voordat je bijwerkt.',
      deleteSuccess: 'Reservering succesvol verwijderd.',
      deleteError: 'Kan de reservering niet verwijderen. Probeer het opnieuw.',
    },
    thankYou: {
      badge: 'Verzoek verzonden',
      title: 'Bedankt',
      headerMessage:
        'We hebben je wijzigingsverzoek ontvangen en reageren zo snel mogelijk.',
      statusLabel: 'Verzoek verzonden',
      awaitingTitle: 'Je wijziging wacht op bevestiging',
      awaitingMessage:
        'We hebben je wijzigingsverzoek ontvangen en reageren zo snel mogelijk.',
      step1Title: 'Controle door het restaurant',
      step1Message:
        'Het personeel controleert beschikbaarheid en de details van het verzoek.',
      step2Title: 'Automatisch antwoord',
      step2Message:
        'Je ontvangt een update zodra de wijziging is geaccepteerd of geweigerd.',
      closeHint:
        'Je kunt deze pagina sluiten: je verzoek is correct geregistreerd.',
    },
    result: {
      updatedTitle: 'Reservering bijgewerkt',
      updatedMessage:
        'Je wijzigingen zijn opgeslagen. Het restaurant is op de hoogte gebracht.',
      deletedTitle: 'Reservering verwijderd',
      deletedMessage:
        'Je reservering is geannuleerd. We hopen je een andere keer te zien.',
    },
  },
  backoffice2020: {
    title: 'Backoffice PRO20',
    logout: 'Uitloggen',
    login: {
      documentTitle: 'Backoffice inloggen',
      title: 'Inloggen op Backoffice',
      subtitle: 'Gebruik je admin-e-mail en wachtwoord om door te gaan.',
      emailPlaceholder: 'E-mail',
      passwordPlaceholder: 'Wachtwoord',
      rememberCredentials: 'Inloggegevens onthouden',
      signIn: 'Inloggen',
      invalidCredentials: 'Ongeldig e-mailadres of wachtwoord.',
      adminBadge: 'Admin-toegang',
      authorizedOnly: 'Alleen geautoriseerd personeel',
      heroTitle: 'Beheer het Pro20-platform met vertrouwen',
      heroSubtitle:
        'Veilige backoffice-toegang voor restaurants, messaging en platformbrede bediening.',
      heroCardTitle: 'Veilig controlecentrum',
      heroCardSubtitle: 'Platformoperaties',
      heroPointOne: 'Beheer restaurants, medewerkers en onboarding',
      heroPointTwo: 'Monitor messagingstatus en operationele alerts',
      heroPointThree:
        'Pas platforminstellingen toe met volledige verantwoording',
      trustedBy: 'Vertrouwd voor veilige operaties',
      trustAccessTitle: 'Gecontroleerde toegang',
      trustAccessDesc: 'Alleen-admin credentials met beschermde sessies',
      trustAuditTitle: 'Audit-klaar',
      trustAuditDesc: 'Kritieke acties blijven traceerbaar',
      trustPlatformTitle: 'Platformschaal',
      trustPlatformDesc: 'Beheer locaties vanuit één console',
      validation: {
        emailRequired: 'E-mail is verplicht',
        emailInvalid: 'Voer een geldig e-mailadres in',
        emailMax: 'E-mail mag maximaal 255 tekens bevatten',
        passwordMin: 'Wachtwoord moet minstens 8 tekens bevatten',
        passwordMax: 'Wachtwoord mag maximaal 128 tekens bevatten',
      },
    },
    whatsapp: 'WhatsApp',
    footer: 'BackOffice Pro20 © {{year}}',
    headingTitle: 'Welkom bij het Pro20 bedieningspaneel',
    headingWelcomeBack: 'Welkom terug, {{name}}',
    headingWelcomeBackGeneric: 'Welkom terug',
    actions: {
      announcements: 'Aankondigingen',
      notifications: 'Meldingen',
      globalAlertTooltip: 'Globale melding in de app',
      expiredActivities: 'Verlopen activiteiten',
      expiredActivitiesTooltip: 'Deadline-waarschuwingen in-/uitschakelen',
      removeBanner: 'Banner verwijderen',
      removeBannerTooltip: 'Globale banner uit de app verwijderen',
    },
    releaseMobile: {
      title: 'Release Mobile',
      currentVersion: 'Huidige versie {{version}}',
      manageVersion: 'Versie beheren',
      toast: {
        versionUpdated: 'App-versie bijgewerkt',
        versionUpdateFailed: 'App-versie kon niet worden bijgewerkt',
        bannerRemoved: 'Globale banner verwijderd',
        bannerRemoveFailed: 'Globale banner kon niet worden verwijderd',
      },
    },
    manageVersionModal: {
      title: 'App-versie bijwerken',
      loading: 'Huidige versie laden…',
      loadError: 'Huidige versie kon niet worden geladen.',
      major: 'Major',
      majorHint: 'Grote wijzigingen',
      minor: 'Minor',
      minorHint: 'Nieuwe functies',
      bug: 'Bug',
      bugHint: 'Applicatiefixes',
      patch: 'Patch',
      patchHint: 'Release Review',
      formatHint: 'Het gepubliceerde formaat is Major.Minor.Bug.Patch.',
      update: 'Versie bijwerken',
    },
    removeBannerModal: {
      title: 'Globale banner verwijderen?',
      description: 'Het bericht wordt niet meer in de app weergegeven.',
      confirm: 'Banner verwijderen',
      cancel: 'Annuleren',
    },
    globalAlertModal: {
      title: 'Voer het nieuwe globale waarschuwingsbericht in',
      confirmation: 'Bevestigen',
      cancel: 'Annuleren',
      toast: {
        success: 'Globale waarschuwing bijgewerkt',
        failed: 'Kon globale waarschuwing niet bijwerken',
      },
    },
    createActivityModal: {
      title: 'Vestiging aanmaken',
      cancel: 'Annuleren',
      create: 'Aanmaken',
      selectUser: 'Gebruiker selecteren',
      selectUserPlaceholder: 'Selecteer een gebruiker',
      selectUserSearchPlaceholder: 'Zoek gebruiker op naam of e-mail',
      restaurantName: 'Restaurantnaam',
      email: 'E-mail',
      phone: 'Telefoon',
      vatNumber: 'BTW-nummer',
      companyName: 'Bedrijfsnaam',
      sections: {
        marketing: 'Marketing',
        address: 'Adres',
        geolocation: 'Geolocatie',
        integration: 'Integratie',
      },
      gtmCode: 'GTM-code',
      gtmCodeHint: 'Google Tag Manager-code',
      facebookPixel: 'Facebook Pixel',
      facebookPixelHint: 'De Pixel-code bestaat alleen uit cijfers',
      address: 'Adres',
      postalCode: 'Postcode',
      city: 'Stad',
      country: 'Land',
      latitude: 'Breedtegraad',
      latitudeHint: 'Bijv. 45.464211',
      longitude: 'Lengtegraad',
      longitudeHint: 'Bijv. 9.191383',
      ipraticoApiKey: 'iPratico API-sleutel',
      ipraticoApiKeyHint: 'API-sleutel voor integratie met iPracto',
      noUsers: 'Geen gebruikers beschikbaar',
      toast: {
        created: 'Vestiging aangemaakt',
        createFailed: 'Vestiging kon niet worden aangemaakt',
      },
      validation: {
        userRequired: 'Gebruikersselectie is verplicht',
        restaurantRequired: 'Restaurantnaam is verplicht',
        emailInvalid: 'Voer een geldig e-mailadres in',
        facebookPixelNumeric: 'Facebook Pixel mag alleen cijfers bevatten',
      },
    },
    editActivity: {
      pageTitle: 'Activiteit bewerken',
      breadcrumbRoot: 'backoffice',
      close: 'Sluiten',
      back: 'Terug naar activiteiten',
      notFound: 'Activiteit niet gevonden.',
      update: 'Bijwerken',
      enable: 'Inschakelen',
      disabled: 'Uitgeschakeld',
      smsRemaining: 'Resterende SMS',
      addSms: 'SMS toevoegen',
      editSms: 'Resterende SMS bewerken',
      configureSmsModal: {
        title: 'Resterend aantal SMS bijwerken',
        branchCodeLabel: 'BranchCode {{branchCode}}',
        confirm: 'SMS configureren',
      },
      aiCreditsRemaining: 'AI-tegoeden',
      editAiCredits: 'AI-tegoeden bewerken',
      configureAiCreditsModal: {
        title: 'AI-tegoedsaldo bijwerken',
        branchCodeLabel: 'BranchCode {{branchCode}}',
        confirm: 'AI-tegoeden configureren',
      },
      emailSetup: 'E-mailconfiguratie',
      editEmailConfig: 'E-mailconfiguratie bewerken',
      restoreDefaultEmailConfig: 'Standaardconfiguratie herstellen',
      emailFields: {
        mailResidue: 'E-mailsaldo',
        customerEmail: 'Klant-e-mail',
        fromAddress: 'Afzenderadres',
        password: 'Wachtwoord',
        smtpServer: 'SMTP-server',
        smtpPort: 'SMTP-poort',
      },
      configureEmailModal: {
        title: 'Resterend aantal e-mails bijwerken',
        mailResidue: 'E-mailsaldo',
        customerEmail: 'Klant-e-mail voor gepersonaliseerde berichten',
        fromAddress: 'Afzenderadres',
        password: 'Gegenereerd wachtwoord',
        smtpServer: 'SMTP-server',
        smtpPort: 'SMTP-poort',
        required: 'Dit veld is verplicht',
        save: 'Configuratie opslaan',
      },
      sections: {
        general: 'Algemene gegevens',
        subscription: 'Abonnement',
        address: 'Adres',
        coordinates: 'Coördinaten',
        marketing: 'Marketing',
        integrations: 'Integraties',
        communications: 'Communicatie',
      },
      restaurantName: 'Vestigingsnaam',
      branchNameOnGoogle: 'Merknaam (Google)',
      email: 'E-mail',
      capacity: 'Capaciteit locatie',
      vatNumber: 'BTW-nummer',
      companyName: 'Bedrijfsnaam',
      phone: 'Mobiel',
      url: 'URL',
      deadline: 'Vervaldatum',
      address: 'Adres',
      postalCode: 'Postcode',
      city: 'Stad',
      province: 'Provincie / Plaats',
      region: 'Regio',
      country: 'Land',
      longitude: 'Lengtegraad',
      latitude: 'Breedtegraad',
      gtmCode: 'GTM-code',
      facebookPixel: 'FB Pixel-code',
      ipraticoApiKey: 'iPratico XApiKey',
      ipraticoApiKeyPlaceholder: 'Voer de sleutel in',
      menuRestaurant: 'Restaurantmenu',
      menuRestaurantDesc:
        'Schakel het digitale restaurantmenu in voor deze vestiging.',
      officialApi: 'Officiële API',
      officialApiDesc:
        'Schakel de officiële WhatsApp API in voor deze vestiging.',
      googleReservation: 'Google Reservation',
      googleReservationDesc:
        'Schakel Google Reservation in voor deze vestiging.',
      supplierOrders: 'Leveranciersbestellingen',
      supplierOrdersDesc:
        'Schakel leveranciersbestellingen in voor deze vestiging.',
      deliveryTakeaway: 'Bezorgen / Afhalen',
      deliveryTakeawayDesc:
        'Schakel bezorgen en afhalen in voor deze vestiging. Vereist het Restaurantmenu.',
      automation: 'Automatisering',
      automationDesc: 'Schakel automatisering in voor deze vestiging.',
      statusEnabled: 'Actief',
      statusDisabled: 'Uit',
      sectionHints: {
        general:
          'Naam, contact en hoeveel gasten deze vestiging kan ontvangen.',
        subscription: 'Wanneer de toegang tot deze vestiging verloopt.',
        address: 'Waar gasten en partners deze vestiging vinden.',
        coordinates:
          'Kaartpositie die in openbare vermeldingen wordt gebruikt.',
        marketing: 'Trackingcodes voor de website van deze vestiging.',
        integrations:
          'Producten en partnerdiensten die aan deze vestiging zijn gekoppeld.',
        communications:
          'SMS-saldo, AI-tegoeden en het postvak voor berichten aan gasten.',
      },
      publicApi: {
        title: 'Openbare REST API',
        description:
          'Stel de reserveringsgeschiedenis van deze vestiging open. Aanroepers authenticeren met de X-API-KEY-header.',
        configure: 'Configureren',
        statusConfigured: 'Geconfigureerd',
        statusMissing: 'Niet geconfigureerd',
        notConfigured:
          'Nog geen X-API-KEY. Configureer deze vestiging om er een op de server aan te maken.',
        loadFailed: 'De X-API-KEY kon niet worden geladen.',
        retry: 'Opnieuw',
        configureFailed: 'De X-API-KEY kon niet worden aangemaakt',
        configured: 'X-API-KEY aangemaakt',
        deleteConfiguration: 'Configuratie verwijderen',
        deleteConfirmTitle: 'REST API-configuratie verwijderen?',
        deleteConfirmDescription:
          'Dit verwijdert de X-API-KEY van deze vestiging. Bestaande aanroepen kunnen die sleutel niet meer gebruiken.',
        deleted: 'REST API-configuratie verwijderd',
        deleteFailed: 'De REST API-configuratie kon niet worden verwijderd',
        keyLabel: 'X-API-KEY',
        keyHint:
          'Stuur deze waarde in de X-API-KEY-header bij elke aanvraag van de reserveringsgeschiedenis.',
        showKey: 'Sleutel tonen',
        hideKey: 'Sleutel verbergen',
        copyKey: 'Sleutel kopiëren',
        copiedKey: 'X-API-KEY gekopieerd',
        copyFailed: 'Kopiëren naar het klembord is mislukt',
        bookingHistory: 'Reserveringsgeschiedenis',
        sampleRequest: 'Voorbeeldverzoek',
        sampleResponse: 'Voorbeeldantwoord',
        copySample: 'Voorbeeld kopiëren',
        copiedSample: 'Voorbeeld gekopieerd',
        filtersHint:
          'Elke parameter staat apart. Het endpoint wijzigt met de filters die u opneemt.',
        copyFilters: 'Alle filters kopiëren',
        showFilters: 'Filters tonen',
        hideFilters: 'Filters verbergen',
        copiedFilters: 'Filters gekopieerd',
        endpointLabel: 'Endpoint',
        params: {
          page: 'page',
          size: 'size',
          status: 'status',
          bookingDate: 'bookingDate',
          bookingDateFrom: 'bookingDateFrom',
          bookingDateTo: 'bookingDateTo',
          search: 'search',
          timeRangeFilter: 'timeRangeFilter',
          assigned: 'assigned',
          walkInOnly: 'walkInOnly',
          sortBy: 'sortBy',
          sortDirection: 'sortDirection',
        },
        languages: {
          curl: 'cURL',
          javascript: 'JavaScript',
          python: 'Python',
          php: 'PHP',
        },
      },
      toast: {
        updated: 'Vestiging bijgewerkt',
        updateFailed: 'Vestiging kon niet worden bijgewerkt',
        featureUpdated: 'Functie bijgewerkt',
        featureUpdateFailed: 'Functie kon niet worden bijgewerkt',
        deliveryRequiresMenu:
          'Schakel Restaurantmenu in voordat je Delivery / Takeaway activeert.',
        smsUpdated: 'SMS-aantal bijgewerkt',
        smsUpdateFailed: 'SMS-aantal bijwerken mislukt',
        aiCreditsUpdated: 'AI-tegoeden bijgewerkt',
        aiCreditsUpdateFailed: 'AI-tegoeden bijwerken mislukt',
        emailUpdated: 'E-mailconfiguratie opgeslagen',
        emailRestored: 'E-mailconfiguratie hersteld',
      },
      validation: {
        restaurantRequired: 'Vestigingsnaam is verplicht',
        emailInvalid: 'Voer een geldig e-mailadres in',
        urlInvalid: 'Voer een geldige link in',
        facebookPixelNumeric: 'Facebook Pixel mag alleen cijfers bevatten',
        capacityInvalid: 'Voer een geldige capaciteit in',
        capacityInteger: 'Capaciteit moet een geheel getal zijn',
        capacityMin: 'Capaciteit moet minstens 0 zijn',
      },
    },
    activities: {
      title: 'Vestigingen',
      count: '{{count}} vestigingen',
      create: 'Vestiging aanmaken',
      loadError: 'Vestigingen laden mislukt. Probeer het opnieuw.',
      expiringCaption: 'Verloopt',
      expiringAria: '{{count}} vestigingen verlopen in {{month}}',
    },
    tabs: {
      branch: 'Branch Pro20',
      users: 'Pro20 Users',
      reports: 'iPratico - Report',
      security: 'Beveiliging',
    },
    security: {
      title: 'Wachtwoord wijzigen',
      subtitle: 'Werk het wachtwoord van je backoffice-account bij.',
      password: 'Wachtwoord',
      confirmPassword: 'Bevestig wachtwoord',
      submit: 'Wachtwoord bijwerken',
      validation: {
        required: 'Wachtwoord is verplicht',
        minLength: 'Wachtwoord moet minstens 6 tekens bevatten',
        confirmRequired: 'Bevestig je wachtwoord',
        mismatch: 'Wachtwoorden komen niet overeen',
      },
      toast: {
        success: 'Wachtwoord succesvol bijgewerkt',
        failed: 'Wachtwoord kon niet worden bijgewerkt. Probeer opnieuw.',
      },
    },
    users: {
      title: 'Gebruikers',
      count: '{{count}} gebruikers',
      create: 'Gebruiker aanmaken',
      searchPlaceholder: 'Zoek gebruiker..',
      loadError: 'Gebruikers konden niet worden geladen. Probeer opnieuw.',
      table: {
        userCode: 'UserCode',
        name: 'Naam',
        email: 'E-mail',
        phone: 'Telefoon',
        activity: 'Activiteit',
        visibleOnApp: 'Zichtbaar in de app',
      },
      tooltips: {
        linkActivity: 'Activiteit koppelen aan gebruiker',
        editUser: 'Gebruiker bewerken',
        goToBackoffice: 'Ga naar de backoffice van de gebruiker',
        deleteUser: 'Gebruiker verwijderen',
      },
      fields: {
        name: 'Naam',
        email: 'E-mail',
        phone: 'Telefoon',
        password: 'Wachtwoord',
        confirmPassword: 'Bevestig wachtwoord',
      },
      createModal: {
        title: 'Gebruiker aanmaken',
        cancel: 'Annuleren',
        create: 'Aanmaken',
      },
      editModal: {
        title: 'Gebruiker bewerken',
        cancel: 'Annuleren',
        update: 'Bijwerken',
      },
      linkActivityModal: {
        title: 'Activiteit selecteren',
        searchLabel: 'Activiteit zoeken',
        searchPlaceholder: 'Zoeken op naam of code',
        availableTitle: 'Beschikbare activiteiten',
        cancel: 'Annuleren',
        confirm: 'Bevestigen',
      },
      relatedActivitiesModal: {
        title: 'Gekoppelde activiteiten',
        name: 'Naam',
        code: 'Code',
        expiration: 'Vervaldatum',
        unlink: 'Activiteit ontkoppelen',
        close: 'Sluiten',
      },
      unlinkModal: {
        title: 'Activiteit ontkoppelen?',
        description:
          'Weet je zeker dat je "{{name}}" wilt ontkoppelen van deze gebruiker? Deze actie kan niet ongedaan worden gemaakt.',
      },
      deleteModal: {
        title: 'Gebruiker verwijderen?',
        description:
          'Weet je zeker dat je "{{name}}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
      },
      validation: {
        nameRequired: 'Naam is verplicht',
        emailRequired: 'E-mail is verplicht',
        emailInvalid: 'Voer een geldig e-mailadres in',
        passwordMin: 'Wachtwoord moet minstens 6 tekens bevatten',
        passwordMismatch: 'Wachtwoorden komen niet overeen',
      },
      toast: {
        created: 'Gebruiker aangemaakt',
        createFailed: 'Gebruiker kon niet worden aangemaakt',
        updated: 'Gebruiker bijgewerkt',
        updateFailed: 'Gebruiker kon niet worden bijgewerkt',
        deleted: 'Gebruiker verwijderd',
        deleteFailed: 'Gebruiker kon niet worden verwijderd',
        loadFailed: 'Gebruikersdetails konden niet worden geladen',
        activitiesLinked: 'Activiteiten gekoppeld aan gebruiker',
        activitiesLinkFailed: 'Activiteiten konden niet worden gekoppeld',
        activityUnlinked: 'Activiteit ontkoppeld van gebruiker',
        activityUnlinkFailed: 'Activiteit kon niet worden ontkoppeld',
        noAssociatedActivity: 'Geen gekoppelde activiteit',
        visibilityUpdated: 'Gebruikerszichtbaarheid bijgewerkt',
        visibilityUpdateFailed:
          'Gebruikerszichtbaarheid kon niet worden bijgewerkt',
      },
    },
    reports: {
      badge: 'Integratiemonitor',
      title: 'iPratico-fouten',
      subtitle:
        'Controleer aanvragen, responses en anomalieën die tijdens bewerkingen richting iPratico zijn geregistreerd.',
      errorsFound: 'fouten gevonden',
      actions: {
        update: 'Bijwerken',
        reset: 'Reset',
        filter: 'Filteren',
      },
      filters: {
        branchCode: 'Vestigingscode',
        operation: 'Bewerking',
        httpStatus: 'HTTP-status',
        fromDay: 'Vanaf dag',
        toDay: 'Tot dag',
        allStatuses: 'Alle statussen',
        status400: '400 — Ongeldige aanvraag',
        status401: '401 — Niet geautoriseerd',
        status403: '403 — Toegang geweigerd',
        status404: '404 — Bron niet gevonden',
        status409: '409 — Conflict',
        status429: '429 — Te veel verzoeken',
        status500: '500 — iPratico-fout',
        status502: '502 — Gateway',
        status503: '503 — Service niet beschikbaar',
      },
      table: {
        date: 'Datum',
        branch: 'Vestiging',
        operation: 'Bewerking',
        status: 'Status',
        endpoint: 'Endpoint',
        mistake: 'Fout',
      },
      detail: {
        title: 'Foutdetail',
        branch: 'Vestiging',
        branchName: 'Vestigingsnaam',
        branchCode: 'Vestigingscode',
        date: 'Datum',
        method: 'Methode',
        status: 'Status',
        apiKey: 'Gebruikte X-API-KEY',
        endpoint: 'Endpoint',
        message: 'Bericht',
        requestPayload: 'Request-payload',
        responsePayload: 'iPratico-antwoord',
      },
      tooltips: {
        openDetail: 'Detail openen',
        closeDetail: 'Detail sluiten',
        copyPayload: 'Payload kopieren',
      },
      toast: {
        updated: 'Rapporten bijgewerkt',
        copied: 'Gekopieerd naar klembord',
        loadFailed: 'iPratico-fouten konden niet worden geladen',
      },
      loadError:
        'iPratico-fouten konden niet worden geladen. Probeer het opnieuw.',
    },
    searchPlaceholder: 'Zoeken op naam, code, bedrijfsnaam of BTW',
    filters: {
      features: {
        label: 'Functies',
        google: 'Google-reservering',
        menu: 'Restaurantmenu',
        ipraticoKey: 'iPratico-sleutel',
        whatsappBusiness: 'WhatsApp Business',
        stripe: 'Stripe',
      },
    },
    table: {
      nameActivities: 'Vestigingsnaam',
      branchCode: 'BranchCode',
      extra: 'Extra',
      deadline: 'Expiration',
      expiredOn: 'Verlopen op {{date}}',
      alertOnApp: 'Melding in app',
      companyName: 'Bedrijfsnaam',
      noCompanyName: 'Geen bedrijfsnaam',
      delete: 'Vestiging verwijderen',
      settings: 'Vestiging bijwerken',
      chat: 'Berichten monitoren',
      send: 'Naar de backoffice',
      tooltips: {
        whatsappBusiness: 'WhatsApp Business ingeschakeld',
        menu: 'Menu ingeschakeld',
        google: 'Google-reservering ingeschakeld',
        ipraticoKey: 'iPratico-sleutel',
        sms: 'Resterende SMS: {{count}}',
        aiCredits: 'AI-tegoeden: {{count}}',
        loadError: 'Kon gebruikers voor deze vestiging niet laden',
      },
    },
    stripeConnect: {
      tooltip: {
        ready: 'Stripe Connect actief — configuratie bekijken of verwijderen',
        pending: 'Stripe-onboarding afronden, hervatten of verwijderen',
        unlinked: 'Vestiging aan Stripe Connect koppelen',
      },
      link: {
        title: 'Stripe onboardinglink',
        body: 'De link voor {{branch}} is klaar.',
        warning:
          'Dit is een tijdelijke Stripe-link voor eenmalig gebruik: deel of gebruik hem zo snel mogelijk.',
        openLink: 'Link openen',
        copyLink: 'Link kopiëren',
        linkCopied: 'Link gekopieerd',
        copyFailed:
          'Kopiëren mislukt — selecteer en kopieer de link handmatig.',
      },
      connected: {
        title: 'Stripe Connect actief',
        body: '{{branch}} is al gekoppeld en kan betalingen en uitbetalingen ontvangen.',
      },
      pending: {
        title: 'Stripe-controles bezig',
        body: 'Stripe vraagt nog om enkele controles voor {{branch}}.',
        resume: 'Onboarding hervatten',
      },
      error: {
        title: 'Stripe-configuratie mislukt',
        fallback: 'Controleer de Stripe-configuratie en probeer het opnieuw.',
      },
      remove: {
        action: 'Stripe-configuratie verwijderen',
        confirmTitle: 'Stripe-configuratie verwijderen?',
        confirmBody:
          'Stripe ontkoppelen voor {{branch}}? De vestiging kan geen Stripe-betalingen meer ontvangen totdat deze opnieuw is gekoppeld.',
        success: 'Stripe-configuratie verwijderd',
        failed: 'Stripe-configuratie kon niet worden verwijderd',
      },
      close: 'Sluiten',
    },
    accessUserModal: {
      title: 'Selecteer een gebruiker om toegang te krijgen tot de backoffice',
      name: 'Naam',
      email: 'E-mail',
      close: 'Sluiten',
      loadError: 'Kon gebruikers voor deze vestiging niet laden',
    },
    signInAsUser: {
      success: 'Ingelogd als geselecteerde gebruiker',
      failed: 'Kon niet inloggen als deze gebruiker',
      missingIdentity: 'Gebruikerscode en e-mail zijn verplicht',
    },
    expiringBranchesModal: {
      title: 'Vestigingen die verlopen in {{month}}',
      name: 'Naam',
      userCode: 'Usercode',
      email: 'Mail',
      expires: 'Verloopt',
      close: 'Sluiten',
      empty: 'Geen vestigingen verlopen deze maand.',
      openDetails: 'Vestigingsdetails openen',
    },
    deleteActivityModal: {
      title: 'Bevestigt u de verwijdering van de vestiging?',
      description:
        'Schrijf hier de vestigingscode ({{branchCode}}) om door te gaan met de verwijdering',
      branchCodePlaceholder: 'VESTIGINGSCODE',
      eliminate: 'Verwijderen',
      cancel: 'Annuleren',
      deleted: 'Vestiging verwijderd',
      deleteFailed: 'Vestiging kon niet worden verwijderd',
    },
    access: {
      title: 'Backoffice-toegang',
      description:
        'Voer de numerieke code in om toegang te krijgen tot het Pro20 bedieningspaneel.',
      codePlaceholder: '',
      enter: 'Invoeren',
    },
    validation: {
      codeRequired: 'Toegangscode is verplicht',
      codeNumeric: 'Toegangscode mag alleen cijfers bevatten',
      invalidCode: 'Ongeldige toegangscode',
    },
    messageMonitor: {
      title: 'Message Monitor',
      subtitle: 'Controleer normale en geplande verzendingen voor API-tests.',
      close: 'Sluiten',
      reset: 'Resetten',
      update: 'Bijwerken',
      stats: {
        posted: 'Geplaatst op deze pagina',
        errors: 'Fouten op deze pagina',
        inProgress: 'Bezig op deze pagina',
      },
      filters: {
        origin: 'Herkomst',
        state: 'Status',
        channel: 'Kanaal',
        reference: 'Referentie',
        referencePlaceholder: 'Referentie',
        originEveryone: 'Iedereen',
        originNormal: 'Normaal',
        originScheduled: 'Gepland',
        originBulk: 'Bulk',
        stateEveryone: 'Iedereen',
        stateSent: 'Verzonden',
        stateErrors: 'Fouten',
        statePending: 'In afwachting',
        stateInQueue: 'In coda',
        stateInProgress: 'Bezig',
        channelEveryone: 'Iedereen',
        channelWhatsapp: 'WhatsApp',
        channelThem: 'THEM',
        channelEmail: 'E-mail',
        channelSms: 'SMS',
      },
      eventsFound: '{{count}} gebeurtenissen gevonden',
      empty: 'Geen berichten gevonden met de geselecteerde filters.',
      loadError:
        'Berichtgebeurtenissen konden niet worden geladen. Probeer opnieuw.',
      actions: {
        resubmit: 'Bericht opnieuw verzenden',
      },
      toast: {
        resubmitted: 'Bericht opnieuw verzonden',
        resubmitFailed: 'Bericht kon niet opnieuw worden verzonden',
      },
      table: {
        date: 'Data',
        type: 'Type',
        recipient: 'Ontvanger',
        reference: 'Referentie',
        message: 'Bericht',
        channel: 'Kanaal',
        state: 'Status',
        attempts: 'Pogingen',
        endpoint: 'Endpoint',
        payload: 'Payload',
        details: 'Details',
      },
    },
  },
  homeLanding: {
    documentTitle:
      'PRO20 — Software voor restaurantreserveringen en tafelboekingen',
    login: 'Inloggen',
    bookDemo: 'Demo boeken',
    startFreeTrial: 'Gratis proefperiode aanvragen',
    learnMore: 'Meer informatie',
    nav: {
      home: 'Home',
      product: 'Product',
      solutions: 'Oplossingen',
      industries: 'Sectoren',
      integrations: 'Integraties',
      pricing: 'Prijzen',
      resources: 'Resources',
      why: 'Waarom PRO20',
      apps: 'Apps',
      howItWorks: 'Hoe het werkt',
      reviews: 'Klantreviews',
      faq: 'FAQ',
      about: 'Over ons',
    },
    hero: {
      partnerBadge: 'Google Reservation C2C Partner',
      metaPartnerBadge: 'Officiële Meta Tech Provider',
      titleBefore: 'All-in-one platform voor',
      titleHighlight: 'restaurants',
      titleAfter: 'en tafelboekingen.',
      subtitle:
        'Beheer reserveringen, plattegronden, personeel, marketing, betalingen en verras je gasten — alles in één krachtig platform.',
      checkGoogle: 'Google Reservations',
      checkWhatsapp: 'WhatsApp Official API',
      checkFloor: 'Plattegrond & tafels',
      checkMarketing: 'Marketing & loyalty',
      checkPayments: 'Betalingen & deposito’s',
      checkApps: 'iOS- & Android-apps',
      trustedBy: 'Vertrouwd door 100+ restaurants in 10+ steden in Italië.',
      previewAlt: 'Voorbeeld van het PRO20-reserveringsdashboard',
      chatAria: 'Chat openen',
    },
    trust: {
      heading: 'Vertrouwd door 100+ restaurants & venue-merken',
      subtitle:
        'Zelfstandige restaurants en multi-site groepen gebruiken PRO20 om elke dag tafels te vullen.',
    },
    stats: {
      title: 'Vertrouwd door restaurantteams wereldwijd',
      subtitle: 'Echte schaal bij restaurants, cafés, beach clubs en bars.',
      restaurantsValue: '100+',
      restaurantsLabel: 'Restaurants',
      countriesValue: '10+',
      countriesLabel: 'Steden in Italië',
      bookingsValue: '2M+',
      bookingsLabel: 'Beheerde boekingen',
      uptimeValue: '99.9%',
      uptimeLabel: 'Uptime',
    },
    features: {
      title: 'Alles wat je nodig hebt om reserveringen te runnen',
      subtitle:
        'Van boekingen en plattegronden tot marketing, loyalty en integraties — één platform.',
      reservationsTitle: 'Slimme reserveringen',
      reservationsDesc:
        'Accepteer, beheer en optimaliseer boekingen via elk kanaal in realtime.',
      floorTitle: 'Plattegrond & tafels',
      floorDesc:
        'Visuele plattegrond met live tafelstatus voor soepelere service.',
      bookingLinkTitle: 'Openbare boekingslink',
      bookingLinkDesc:
        'Maak een boekingsformulier en deel de openbare URL op je website, QR, WhatsApp of socials zodat gasten online tafels reserveren.',
      marketingTitle: 'Marketing & campagnes',
      marketingDesc:
        'Bereik gasten met gerichte aanbiedingen, herinneringen en campagnes.',
      loyaltyTitle: 'Loyalty & promo',
      loyaltyDesc:
        'Beloon vaste gasten en stimuleer herhaalbezoek met flexibele promoties.',
      reportsTitle: 'Rapporten & inzichten',
      reportsDesc:
        'Volg bezetting, omzet en gasttrends met duidelijke dashboards.',
    },
    why: {
      title: 'Waarom PRO20?',
      subtitle:
        'Speciaal gebouwd voor restaurants en bars — geen generieke boekingswidget.',
      allInOneTitle: 'All-in-one platform',
      allInOneDesc:
        'Reserveringen, plattegrond, marketing, loyalty en betalingen op één plek.',
      channelsTitle: 'Native Google & WhatsApp',
      channelsDesc: 'Ontmoet gasten waar ze al boeken en berichten sturen.',
      hospitalityTitle: 'Gemaakt voor floor operations',
      hospitalityDesc:
        'Plattegrond, staff-workflows en live servicetools die bij de vloer passen.',
      appsTitle: 'Apps voor elk apparaat',
      appsDesc:
        'Native telefoon- en iPad-apps op Android en iOS, gesynchroniseerd met de web-backoffice.',
      fidelityTitle: 'Fidelity & loyalty',
      fidelityDesc:
        'Beloon vaste gasten met punten, kaarten en promo’s — stimuleer herhaalbezoek vanuit één programma.',
      supportTitle: '24×7 Support',
      supportDesc:
        'Hulp rond de klok wanneer je team die nodig heeft — dag en nacht, elke dag van de week.',
    },
    industries: {
      title: 'Gebouwd voor elk type venue',
      subtitle:
        'Van fine dining tot beach clubs — PRO20 past zich aan hoe jouw venue gasten bedient.',
      restaurants: 'Restaurants',
      cafes: 'Cafés',
      beachClubs: 'Beach Clubs',
      bars: 'Bars & pubs',
      rooftops: 'Rooftops',
      resorts: 'Resorts',
    },
    integrations: {
      title: 'Krachtige integraties',
      subtitle:
        'Koppel de tools die je al gebruikt — boekingen, messaging, POS en betalingen.',
      google: 'Google Reservations',
      whatsapp: 'WhatsApp Official API',
      ipratico: 'iPratico POS',
      centralino: 'Centralino VOIP',
      stripe: 'Stripe-betalingen',
      meta: 'Meta Tech Partner',
    },
    engage: {
      title: 'Communiceer. Engageer. Verrass.',
      subtitle:
        'Bereik je gasten direct via WhatsApp Official API en geautomatiseerde berichten.',
      confirmations: 'Boekingsbevestigingen',
      reminders: 'Herinneringen & alerts',
      offers: 'Aanbiedingen & promoties',
      feedback: 'Feedback & reviews',
      chatGuest: 'Hoi! Je tafel voor 2 is bevestigd om 20:00.',
      chatModify: 'Boeking wijzigen',
      chatCancel: 'Boeking annuleren',
      chatDirections: 'Routebeschrijving',
    },
    apps: {
      title: 'Run je venue vanaf telefoon of iPad',
      subtitle:
        'Native PRO20-apps voor telefoons en iPads — beschikbaar op Android en iOS, zodat je team synchroon blijft op de vloer en onderweg.',
      phoneTablet: 'Telefoons & iPads',
      androidIos: 'Android & iOS',
      floorPlan: 'Volledige plattegrond en tafelcontrole op iPad',
      onTheGo: 'Beheer boekingen overal vanaf je telefoon',
      sync: 'Dezelfde live data als de web-backoffice',
      appStore: 'Download in de App Store',
      googlePlay: 'Beschikbaar op Google Play',
    },
    about: {
      title: 'Over ons',
      subtitle:
        'We helpen restaurantmerken elke tafel om te zetten in een betere gastervaring.',
      body: 'PRO20 is een all-in-one platform voor tafelboekingen, vertrouwd door venues wereldwijd. Van zelfstandige restaurants tot multi-site groepen combineren we reserveringen, plattegronden, gastmessaging en marketing zodat operators zich kunnen focussen op hospitality — niet op jongleren met tools.',
      missionTitle: 'Onze missie',
      missionBody:
        'Wereldklasse reserveringsoperaties eenvoudig, verbonden en prettig maken voor elke venue.',
      pillarGuests: 'Gasten eerst',
      pillarOperators: 'Operators versterkt',
      pillarConnected: 'Altijd verbonden',
      imageAlt: 'Restaurantteam aan het werk',
    },
    howItWorks: {
      title: 'Aan de slag in 6 eenvoudige stappen',
      subtitle:
        'Alles wat je nodig hebt om je restaurantoperaties te beheren, van setup tot tevreden gasten.',
      steps: {
        s1: {
          title: 'Maak je restaurant aan',
          desc: 'Stel je restaurantprofiel in met keuken, locatie en branding in enkele minuten.',
        },
        s2: {
          title: 'Ontwerp zalen & tafels',
          desc: 'Bouw je plattegrond met de drag-and-drop planner en organiseer tafels zoals jij wilt.',
        },
        s3: {
          title: 'Configureer boekingsregels',
          desc: 'Definieer tijdslots, groepsgrootte, deposito’s en policies die bij je service passen.',
        },
        s4: {
          title: 'Deel & ontvang boekingen',
          desc: 'Publiceer je boekingslink op je website, WhatsApp, socials of QR-codes.',
        },
        s5: {
          title: 'Beheer & groei',
          desc: 'Beheer zaal, personeel en marketing vanuit één plek terwijl reserveringen binnenkomen.',
        },
        s6: {
          title: 'Analyseer & optimaliseer',
          desc: 'Gebruik duidelijke rapporten over bezetting, omzet en gasttrends om elke service te verbeteren.',
        },
      },
      mocks: {
        createTitle: 'Maak je restaurant aan',
        name: 'Restaurantnaam',
        cuisine: 'Keukentype',
        location: 'Locatie',
        continue: 'Doorgaan',
        floorTitle: 'Zaalplanner',
        rulesTitle: 'Boekingsinstellingen',
        timeSlots: 'Tijdslots',
        bookingRules: 'Boekingsregels',
        advancePayment: 'Vooruitbetaling',
        shareTitle: 'Deel boekingslink',
        manageTitle: 'Overzicht vandaag',
        reservations: 'Reserveringen',
        occupied: 'Bezette tafels',
        walkIns: 'Walk-ins',
        revenue: 'Omzet',
        reportsTitle: 'Rapportoverzicht',
        totalReservations: 'Totaal reserveringen',
        occupancy: 'Bezettingsgraad',
      },
      ctaText:
        'Klaar om te starten? Sluit je aan bij duizenden restaurants die al groeien met PRO20.',
      bookDemo: 'Boek een demo',
      contactSales: 'Contact sales',
    },
    faq: {
      title: 'Veelgestelde vragen',
      subtitle:
        'Snelle antwoorden over PRO20 restaurantreserveringssoftware voor multi-site venues.',
      viewAll: 'Bekijk alle FAQ',
      q1: 'Kan ik een openbare boekingslink delen?',
      a1: 'Ja. Maak een boekingsformulier en deel de openbare URL op je website, QR, WhatsApp of socials zodat gasten online tafels reserveren.',
      q2: 'Ondersteunt het meerdere restaurants?',
      a2: 'Ja. Multi-locatiegroepen kunnen venues centraal beheren via PRO20 Backoffice.',
      q3: 'Kan ik WhatsApp verbinden?',
      a3: 'Ja. PRO20 ondersteunt WhatsApp Business en de officiële Meta API voor bevestigingen, herinneringen en campagnes.',
      q4: 'Welke integraties zijn beschikbaar?',
      a4: 'Google Reservations, WhatsApp Official API, POS-systemen, Stripe, Razorpay en nog veel meer.',
      q5: 'Is er een mobiele app?',
      a5: 'Ja. Native apps voor telefoons en iPads op Android en iOS blijven synchroon met het webplatform.',
      q6: 'Hoe begin ik?',
      a6: 'Boek een demo of start een gratis proefperiode — we helpen je zalen, tafels en je openbare boekingslink in te stellen.',
    },
    reviews: {
      title: 'Neem het niet alleen van ons aan',
      subtitle:
        'Restaurants in 10+ steden in Italië gebruiken PRO20 al om reserveringen te beheren, tafels te vullen en gasten te verrassen. Klaar om te zien waar het over gaat?',
      reassurance:
        '(Echt waar. Geen kleine lettertjes. Geen verkoopdruk. Gewoon solide software en een team dat restaurants begrijpt.)',
      trustLabel: 'Uit honderden reviews',
      r1: {
        quote:
          'PRO20 verving drie tools die we eerder gebruikten. Reserveringen en WhatsApp eindelijk op één plek.',
        author: 'Marco Rossi • Owner, Osteria Bella',
      },
      r2: {
        quote:
          'De setup was snel en de tafelplanner is precies wat ons floor team nodig had op drukke avonden.',
        author: 'Sophie Laurent • Manager, Café Lumière',
      },
      r3: {
        quote:
          'Seizoenscapaciteit en gastmessaging waren chaos. Nu is het voorspelbaar en professioneel.',
        author: 'Elena Bianchi • Director, Azure Beach Club',
      },
      r4: {
        quote:
          'Onze openbare boekingslink vulde avondslots die we leeg lieten. Gasten boeken zelf — wij bevestigen alleen.',
        author: 'Alex Kim • Owner, Harbor Bistro',
      },
    },
    nextLevel: {
      title: 'Klaar om je restaurant naar het volgende niveau te tillen?',
      body: 'PRO20 is ontworpen om dagelijkse operaties te vereenvoudigen, boekingen te verhogen en de stress van een drukke venue te verminderen.',
      closing: 'Start vandaag met PRO20 en ervaar het verschil.',
    },
    cta: {
      heading: 'Klaar om je restaurant te transformeren?',
      subtitle: 'Sluit je aan bij duizenden restaurants die groeien met PRO20.',
    },
    footer: {
      blurb:
        'De all-in-one restaurantreserveringssoftware voor tafelboekingen, gastengagement en venue-operaties.',
      product: 'Product',
      productReservations: 'Reserveringen',
      productFloor: 'Plattegrond',
      productMarketing: 'Marketing',
      productLoyalty: 'Loyalty',
      productReports: 'Rapporten',
      productIntegrations: 'Integraties',
      solutions: 'Oplossingen',
      solutionsRestaurants: 'Voor restaurants',
      solutionsCafes: 'Voor cafés',
      solutionsBeach: 'Voor beach clubs',
      solutionsBars: 'Voor bars',
      solutionsChains: 'Voor ketens',
      resources: 'Resources',
      resourcesBlog: 'Blog',
      resourcesGuides: 'Guides',
      resourcesHelp: 'Helpcentrum',
      resourcesCases: 'Case studies',
      resourcesApi: 'API-documentatie',
      resourcesApps: 'Mobiele apps',
      company: 'Bedrijf',
      companyAbout: 'Over ons',
      companyCareers: 'Carrières',
      companyContact: 'Contact',
      companyPartners: 'Partners',
      legal: 'Juridisch',
      legalPrivacy: 'Privacybeleid',
      legalTerms: 'Servicevoorwaarden',
      legalCookies: 'Cookiebeleid',
      copyright: '© {{year}} PRO20. Alle rechten voorbehouden.',
      socialFacebook: 'Facebook',
      socialInstagram: 'Instagram',
      socialLinkedin: 'LinkedIn',
      socialYoutube: 'YouTube',
    },
  },
  faqPage: {
    documentTitle: 'FAQ — PRO20 restaurantreserveringssoftware',
    title: 'Veelgestelde vragen',
    subtitle:
      'Snelle antwoorden op de meest gestelde vragen over PRO20 restaurantreserveringssoftware.',
    searchPlaceholder: 'Zoek je vraag...',
    emptyResults:
      'Geen vragen komen overeen met je zoekopdracht. Probeer een ander trefwoord of categorie.',
    categories: {
      all: 'Alle vragen',
      reservations: 'Reserveringen',
      whatsapp: 'WhatsApp',
      payments: 'Betalingen',
      reports: 'Rapporten',
      staff: 'Personeel & gebruikers',
      integrations: 'Integraties',
      general: 'Algemeen',
    },
    promo: {
      badge: 'All-in-one platform',
      titleBefore: 'Alles wat je nodig hebt om',
      titleAccent: 'te beheren & te groeien',
      titleAfter: 'met je restaurant',
      body: 'Van reserveringen tot marketing, van WhatsApp-automatisering tot rapporten — PRO20 helpt je je bedrijf slimmer te runnen.',
      restaurantsValue: '10.000+',
      restaurantsLabel: 'Restaurants',
      bookingsValue: '2M+',
      bookingsLabel: 'Reserveringen',
      countriesValue: '10+',
      countriesLabel: 'Steden in Italië',
      uptimeValue: '99,9%',
      uptimeLabel: 'Uptime',
    },
    items: {
      q1: 'Kan ik boekingslinks delen?',
      a1: 'Ja. Maak een boekingsformulier en deel de openbare URL op je website, QR, WhatsApp of socials zodat gasten online tafels reserveren.',
      q1Tag1: 'Openbare URL',
      q1Tag2: 'QR-code',
      q1Tag3: 'WhatsApp',
      q1Tag4: 'Social media',
      q1Tag5: 'Website',
      q2: 'Ondersteunt PRO20 meerdere restaurants of locaties?',
      a2: 'Ja. Multi-site groepen beheren venues centraal via PRO20 Backoffice met gedeelde controles en rapportages.',
      q3: 'Kan ik WhatsApp Official koppelen en berichten sturen?',
      a3: 'Ja. PRO20 ondersteunt WhatsApp Business en de officiële Meta API voor bevestigingen, herinneringen, campagnes en berichten.',
      q4: 'Welke betaalgateways worden ondersteund?',
      a4: 'PRO20 integreert met Stripe en andere providers zodat je deposito’s kunt innen en no-shows kunt verminderen.',
      q5: 'Is er een mobiele app beschikbaar?',
      a5: 'Ja. Native apps voor telefoons en iPads op Android en iOS blijven gesynchroniseerd met het webplatform.',
      q6: 'Welke talen worden ondersteund?',
      a6: 'PRO20 ondersteunt meerdere talen voor operators en gasten, waaronder Engels, Italiaans, Spaans, Frans, Nederlands en Chinees.',
      q7: 'Kan ik mijn personeel en hun rollen beheren?',
      a7: 'Ja. Wijs rollen en rechten toe zodat je team alleen ziet wat nodig is — van zaalpersoneel tot multi-site managers.',
      q8: 'Kan ik marketingcampagnes en promotiecodes beheren?',
      a8: 'Ja. Draai campagnes, aanbiedingen en promo’s vanuit PRO20 om rustigere momenten te vullen en vaste gasten te belonen.',
      q9: 'Welke rapporten kan ik krijgen?',
      a9: 'Volg bezetting, boekingen, omzettrends en gastactiviteit met dashboards voor restaurantoperaties.',
      q10: 'Hoe veilig zijn mijn gegevens?',
      a10: 'We gebruiken industriestandaard beveiliging om restaurant- en gastdata te beschermen, met betrouwbare uptime.',
    },
    cta: {
      eyebrow: 'Nog vragen?',
      title: 'We staan voor je klaar!',
      body: 'Praat met onze restaurantexperts en ontdek hoe PRO20 je bedrijf kan transformeren.',
      bookDemo: 'Boek een demo',
      contactSales: 'Contact sales',
      startTrial: 'Of vraag een gratis proefperiode aan',
    },
  },
  legalPages: {
    privacy: {
      documentTitle: 'Privacybeleid — PRO20',
      loading: 'Privacybeleid laden…',
      loadError:
        'Gegevens van de verwerkingsverantwoordelijke konden niet worden geladen. Het beleid wordt met beperkte informatie getoond.',
    },
    privacyPolicy: {
      documentTitle: 'Privacyverklaring | Proventi',
      title: 'Informatie over de verwerking van persoonsgegevens',
      subtitle:
        'Overeenkomstig de artikelen 13 en 14 van Verordening (EU) 2016/679 – AVG',
      contents: 'Op deze pagina',
      body: privacyPolicyBodyNl,
    },
    terms: {
      documentTitle: 'Servicevoorwaarden — PRO20',
      title: 'Servicevoorwaarden',
      subtitle: 'Deze voorwaarden worden binnenkort bijgewerkt.',
      body: 'De PRO20-servicevoorwaarden worden voorbereid. Kom later terug of neem contact met ons op als u tussentijds een kopie nodig heeft.',
    },
  },
};
